import React, { useRef, useState, useEffect, useCallback } from 'react';
import { MindMapNode, Notebook, Language, NodeAttachment, YouTubeVideoEntry } from '../types';
import { connectYouTube, isYouTubeConnected, uploadToYouTube, getYouTubeChannel, subscribeYouTube, buildVideoAppendPatch, buildVideoUpdatePatch, updateYouTubeVideo, fetchYouTubePlaylists, createYouTubePlaylist, addVideoToPlaylist, YouTubePlaylist, getVideosForNode } from '../services/youtubeService';
import { generateYouTubeDescription, generateYouTubeMetadataFromTranscript, generateVideoNote, postCheckVideo } from '../services/geminiService';
import { resolveNoteLink } from '../services/socialService';
import { getFirestore, doc, setDoc, getDoc, arrayUnion, onSnapshot, collection, query, where } from 'firebase/firestore';
// Note documents are written through safeUpdateDoc, which re-applies video patches onto the
// note as it is on the server (a stale copy must not drop videos linked in the meantime).
import { safeUpdateDoc as updateDoc } from '../services/videoPatchWrite';
import { ref as storageRef, uploadBytesResumable, uploadString, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { X, Upload, CheckCircle2, Youtube, AlertCircle, Film, Circle, Square, Pause, Play, Plus, Trash2, Sparkles, Loader2, Camera, CameraOff, Pen, RotateCcw, ShieldCheck, ShieldAlert, ShieldX, Maximize2, ChevronDown, ChevronUp, Edit3, Keyboard, ListVideo, Search, GripVertical, Scissors, History } from 'lucide-react';
import { ScreenDrawingOverlay, type DrawingStash } from './ScreenDrawingOverlay';
import { RecordingClassmate, type CompanionRole, type CompanionCorrection } from './RecordingClassmate';
import { httpsCallable } from 'firebase/functions';
import { functions, storage } from '../services/firebase';
import { mergeClipLinksIntoDescription } from '../utils/youtubeChapters';
import RecordingRewatchPanel from './RecordingRewatchPanel';
import { chunksAfterRevert } from '../utils/recordingRevert';
import { cutChunksAt, resumeExportChunks, type ResumePart, type ExportChunk } from '../utils/recordingResume';
import { createScreenActivityProbe } from '../utils/screenActivity';
import { steadyInterval, type SteadyInterval } from '../utils/steadyInterval';
import TrimVideoPanel from './TrimVideoPanel';
import { ThumbnailEditorModal, generateThumbnail, THUMB_PRESETS } from './ThumbnailEditorModal';
export type { ThumbPresetId } from './ThumbnailEditorModal';
export { THUMB_PRESETS } from './ThumbnailEditorModal';

// ── Drawing hotkey config (shared with ScreenDrawingOverlay via localStorage key 'draw_hotkeys') ──
type DrawHkAction = 'pen'|'eraser'|'line'|'arrow'|'circle'|'select'|'colorNext'|'colorPrev'|'sizeUp'|'sizeDown'|'toggleOverlay'|'savePage';
type DrawHkMap = Record<DrawHkAction, string>;
const DRAW_HK_DEFAULTS: DrawHkMap = { pen:'p', eraser:'e', line:'l', arrow:'a', circle:'c', select:'v', colorNext:']', colorPrev:'[', sizeUp:'=', sizeDown:'-', toggleOverlay:'d', savePage:'n' };
const DRAW_HK_LABELS: Record<DrawHkAction, string> = { pen:'Pen', eraser:'Eraser', line:'Line', arrow:'Arrow', circle:'Circle', select:'Select', colorNext:'Next color', colorPrev:'Prev color', sizeUp:'Size +', sizeDown:'Size −', toggleOverlay:'Toggle drawing', savePage:'Save & new page' };
function loadDrawHk(): DrawHkMap {
    try { const s = localStorage.getItem('draw_hotkeys'); if (s) return { ...DRAW_HK_DEFAULTS, ...JSON.parse(s) }; } catch {}
    return { ...DRAW_HK_DEFAULTS };
}
function saveDrawHk(hk: DrawHkMap) { try { localStorage.setItem('draw_hotkeys', JSON.stringify(hk)); } catch {} }

interface Props {
    node: MindMapNode;
    notebook: Notebook;
    language: Language;
    uid?: string;
    onClose: () => void;
    onDone: (videoUrl: string, videoTitle: string, videoId?: string, videoDescription?: string, segments?: SegmentStatus[], links?: { videoStoragePath?: string; exportJobId?: string; clipOfJobId?: string }) => void;
    /** A drawing made while recording was saved onto the note: the note's attachments as they now
     *  are on the server, so the open app can show it (and never save an older list over it). */
    onAttachmentsSaved?: (attachments: NodeAttachment[], notebookId: string, nodeId: string) => void;
    /** The note open in the expanded view, if any: drawings saved while recording go there rather
     *  than to the note the recording was started from. */
    drawingTarget?: { notebookId: string; nodeId: string; title: string } | null;
    /** The note open in the expanded view: the virtual classmate follows it while you navigate. */
    liveNote?: { nodeId: string; title: string; content: string; attachments?: NodeAttachment[] } | null;
    initialFile?: File;
    // Set when this modal was opened with an already-exported file handed off from elsewhere
    // (Combine, or Manager's "resume a stuck job" recovery) — the id of the videoExportJobs doc
    // that produced initialFile, so a real YouTube upload success can mark it consumed. Never set
    // just because this modal was opened; only a genuine upload (or the user dismissing it back in
    // Manager) resolves a job — see the "Resumable video export/upload jobs" plan.
    sourceJobId?: string;
    // Multi-flow support: several instances of this modal can exist at once (one per record/export/
    // upload flow). Only the foreground one renders its full UI; the others collapse to a corner pill.
    sessionId?: string;
    slot?: number; // keeps two flows on the same note from sharing recovery storage
    isForeground?: boolean;
    pillIndex?: number; // stacking order of the corner pill
    onRequestForeground?: () => void;
    onRecordingChange?: (recording: boolean) => void;
    // Spin-off clips: parts marked while recording are rendered as separate videos. The app owns
    // them once requested (they outlive this dialog) and opens each as its own upload flow.
    onClipsStarted?: (clips: { jobId: string; title: string; mainJobId: string }[]) => void;
    /** This flow uploads a spin-off clip of the recording rendered by that job. */
    clipOfJobId?: string;
    initialTitle?: string;
}

type Stage = 'pick' | 'clip-review' | 'chunk-review' | 'timeline-review' | 'review' | 'uploading' | 'done' | 'error';
type WebcamShape = 'rect' | 'circle';
type RecordState = 'idle' | 'recording' | 'paused';
type CheckpointMode = 'off' | 'auto' | 'manual' | 'hybrid';

interface Clip {
    id: number;
    blob: Blob;
    durationSecs: number;
    name: string;
}

interface SegmentStatus {
    index: number;
    startSecs: number;
    endSecs?: number;
    status: 'recording' | 'uploading' | 'checking' | 'good' | 'warning' | 'needs_redo' | 'skipped';
    score?: number;
    clarityScore?: number;
    confidence?: number;
    riskLevel?: 'low' | 'medium' | 'high';
    transcript?: string;
    summary?: string;
    whatWasRight?: string; // specific points explained correctly — shown for good results too, not only problems
    mainIssue?: string;
    suggestedCorrection?: string;
    overridden?: boolean; // user chose to keep despite warning/needs_redo
}

// Auto-Pace: one labeled stretch of the recorded (media-time) timeline. Separate concept from
// SegmentStatus above — that one scores speech quality for AI checkpoints; this one just labels
// playback speed for the post-recording chunk-review step. TRUE_IDLE (paused) periods never
// produce a chunk since paused time doesn't exist in the recorded media.
// Auto-Pace content check: independent of AI Checkpoint's SegmentStatus. A check is a window of
// speaking audio sent to analyzeVideoSegment — not the same unit as a SpeedChunk (one SpeedChunk
// can have zero checks, one, or several if the speaking stretch was long enough to be split).
interface AutoPaceCheckResult {
    status: 'good' | 'warning' | 'needs_redo';
    score?: number;
    clarityScore?: number;
    summary?: string;
    whatWasRight?: string;
    mainIssue?: string;
    suggestion?: string;
    transcript?: string; // kept so postCheckVideo can synthesize a real summary, not just scores
    startMs: number;
    endMs: number;
}

// One row in the large "AI Kiểm Tra" reading modal — AI Checkpoint segments and Auto-Pace check
// windows normalised into the same shape.
interface CheckItem {
    key: string;
    label: string;
    range: string;
    status: 'recording' | 'checking' | 'good' | 'warning' | 'needs_redo' | 'skipped';
    score?: number;
    clarityScore?: number;
    summary?: string;
    whatWasRight?: string;
    mainIssue?: string;
    suggestion?: string;
    transcript?: string;
    segIndex?: number; // AI Checkpoint segments only — enables Redo / Keep
    overridden?: boolean;
    chunkIndex?: number; // Auto-Pace checks only — enables per-sentence "revert to here"
    revertMs?: number; // Auto-Pace checks only — exact moment to revert to (this check's startMs)
}

interface SpeedChunk {
    index: number;
    startMs: number;
    endMs: number;
    speed: number; // configurable — no longer just 1 or 4; `kind` (not this number) says what it means
    kind: 'speaking' | 'silent';
    thumbnail?: string;
    checking?: boolean;
    checks?: AutoPaceCheckResult[];
}

// "Insert clip" (mutually exclusive with Auto-Pace, same discipline as AI Checkpoint vs Auto-Pace —
// two independent state machines are simpler than one merged one, see plan doc). `sourceStartMs`/
// `sourceEndMs` — never generic `startMs`/`endMs` — measure position in *this recording's own raw
// footage*, which is NOT the same as position in the final composed output once anything gets
// inserted (the final output's timeline is a derived value, computed when the export chunk list is
// built, never tracked directly during recording).
type TimelineSegment =
    | { type: 'live'; sourceStartMs: number; sourceEndMs: number }
    | { type: 'inserted'; sourceClip: { notebookId: string; nodeId: string; videoId: string }; title: string; thumbnail?: string };
type AutoPaceMode = 'speaking' | 'active-silent' | 'idle';
// An earlier video placed in front of the new recording ("continue a video"); endMs null = to its end.
type SeriesPart = { key: string; nodeId: string; videoId: string; title: string; startMs: number; endMs: number | null };

function formatTime(secs: number) {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

// Auto-Pace content-check status dot for the chunk-review strip. Color = worst status across all
// check windows that landed on this chunk (a long speaking chunk can have several); tooltip lists
// the actual issues via the native `title` attribute — no separate popover needed.
function autoPaceCheckDot(chunk: SpeedChunk): { className: string; title: string } | null {
    if (chunk.kind !== 'speaking') return null;
    const checks = chunk.checks || [];
    if (checks.length === 0 && !chunk.checking) return null;
    const worst: 'needs_redo' | 'warning' | 'good' | null = checks.some(c => c.status === 'needs_redo') ? 'needs_redo'
        : checks.some(c => c.status === 'warning') ? 'warning'
        : checks.length > 0 ? 'good' : null;
    const className = worst === 'needs_redo' ? 'bg-red-500'
        : worst === 'warning' ? 'bg-amber-500'
        : worst === 'good' ? 'bg-emerald-500'
        : 'bg-indigo-400 animate-pulse';
    const issues = checks.filter(c => c.mainIssue).map(c => c.suggestion ? `${c.mainIssue} → ${c.suggestion}` : c.mainIssue).join('\n');
    const title = issues || (chunk.checking ? 'Checking…' : 'Looks good');
    return { className, title };
}

// Picks the single most-severe check result to headline for a chunk in the live floating panel —
// same severity ranking as autoPaceCheckDot, just returning the actual result (score/issue/
// suggestion) instead of only a color.
function worstAutoPaceCheck(checks?: AutoPaceCheckResult[]): AutoPaceCheckResult | null {
    if (!checks || checks.length === 0) return null;
    return checks.find(c => c.status === 'needs_redo')
        || checks.find(c => c.status === 'warning')
        || checks[checks.length - 1];
}


function findL1Ancestor(node: MindMapNode, nodes: MindMapNode[]): MindMapNode {
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const rootId = nodes.find(n => !n.parentId)?.id;
    let cur = nodeMap.get(node.id);
    while (cur && cur.parentId && cur.parentId !== rootId) {
        cur = nodeMap.get(cur.parentId);
    }
    return cur ?? node;
}

function buildFooter(node: MindMapNode, notebook: Notebook, uid?: string): string {
    const lines: string[] = [];
    // Try to resolve the most specific GitHub link: blob URL to the exact note + anchor
    const l1 = findL1Ancestor(node, notebook.nodes);
    const specificLink = resolveNoteLink(notebook.lastPublishUrl, l1.title, node.id);
    if (specificLink) {
        lines.push(`📖 Full notes: ${specificLink}`);
    } else if (uid && notebook.isPublic) {
        lines.push(`📖 View notes: https://studyboard.app?mode=view&uid=${uid}&id=${notebook.id}`);
    }
    lines.push('🎓 Recorded with StudyBoard — studyboard.app');
    return lines.join('\n');
}

function buildRawContext(node: MindMapNode): string {
    const parts: string[] = [];
    if (node.title) parts.push(`Topic: ${node.title}`);
    if (node.content?.trim()) parts.push(node.content.trim());
    if (node.analysis) {
        const a = node.analysis;
        if (a.keyInsights?.length) parts.push('Key insights:\n' + a.keyInsights.join('\n'));
        if (a.intuition) parts.push('Intuition: ' + a.intuition);
    }
    return parts.join('\n\n');
}

function mergeClips(clips: Clip[]): File {
    const type = clips[0]?.blob.type || 'video/webm';
    const merged = new Blob(clips.map(c => c.blob), { type });
    const ext = type.includes('mp4') ? 'mp4' : 'webm';
    return new File([merged], `studyboard-${Date.now()}.${ext}`, { type });
}

// The collapsed AI-check chip row grows by one chip per check — left unbounded it stretches across
// the whole screen. Fixed width (about 7 chips), horizontally scrollable, and it keeps the newest
// chip in view; the mouse wheel scrolls it sideways.
const ChipStrip: React.FC<{ count: number; children: React.ReactNode }> = ({ count, children }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => { const el = ref.current; if (el) el.scrollLeft = el.scrollWidth; }, [count]);
    return (
        <div
            ref={ref}
            onWheel={e => { const el = ref.current; if (el && Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft += e.deltaY; }}
            className="flex flex-nowrap items-center gap-1 max-w-[250px] overflow-x-auto hide-scrollbar [&>*]:flex-shrink-0"
        >
            {children}
        </div>
    );
};

// Export jobs that belong to an upload session currently open in this tab. A second session on the
// same note must not offer (or upload) another session's processed video as if it were its own —
// that session is still alive and will upload it itself.
const liveExportJobIds = new Set<string>();

export function YouTubeUploadModal({ node, notebook, language, uid, onClose, onDone, onAttachmentsSaved, drawingTarget, liveNote, initialFile, sourceJobId, slot = 0, isForeground = true, pillIndex = 0, onRequestForeground, onRecordingChange, onClipsStarted, clipOfJobId, initialTitle }: Props) {
    const vn = language === 'vietnamese';
    const vnRef = useRef(vn);
    vnRef.current = vn;
    const db = getFirestore();
    const auth = getAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [stage, setStage] = useState<Stage>(initialFile ? 'review' : 'pick');
    const [file, setFile] = useState<File | null>(initialFile ?? null);
    const [clips, setClips] = useState<Clip[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [ytConnected, setYtConnected] = useState(isYouTubeConnected());
    const [ytChannel, setYtChannel] = useState<{ name: string; thumbnail: string } | null>(null);
    const [progress, setProgress] = useState(0);
    // Lets the user shrink the modal to a corner pill during a long export/upload wait and keep
    // using StudyBoard elsewhere — purely a render branch, the component itself stays mounted the
    // whole time so export/upload listeners and in-flight fetches are never disturbed.
    const [modalMinimized, setModalMinimized] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [resultUrl, setResultUrl] = useState('');
    const [visibility, setVisibility] = useState<'unlisted' | 'public' | 'private'>('unlisted');

    // Playlist — pick an existing real YouTube playlist (or create one) right here at upload time,
    // instead of needing a separate trip to YouTube Manager afterward.
    const [playlists, setPlaylists] = useState<YouTubePlaylist[]>([]);
    const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
    const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
    // "Is this video made for kids?" — YouTube requires an explicit answer; never defaulted for the
    // user. Their last answer is remembered as a prefill since it rarely changes for a channel.
    const [madeForKids, setMadeForKids] = useState<boolean | null>(() => {
        try { const v = localStorage.getItem('sb_yt_made_for_kids'); return v === null ? null : v === '1'; } catch { return null; }
    });
    const chooseMadeForKids = (v: boolean) => {
        setMadeForKids(v);
        try { localStorage.setItem('sb_yt_made_for_kids', v ? '1' : '0'); } catch { /* ignore */ }
    };
    useEffect(() => {
        if (stage !== 'review' || !isYouTubeConnected()) return;
        fetchYouTubePlaylists().then(setPlaylists).catch(() => { /* the picker just stays empty */ });
    }, [stage]);
    const previewUrlRef = useRef<string | null>(null);

    // Recording state
    const [recordState, setRecordState] = useState<RecordState>('idle');
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    useEffect(() => {
        onRecordingChange?.(recordState !== 'idle');
        return () => onRecordingChange?.(false);
    }, [recordState]);
    // Guard against an accidental hard refresh (Cmd/Ctrl+Shift+R), tab close, or navigation while
    // recording or paused mid-recording — a page reload during active MediaRecorder capture never
    // gets to run the stop handler that persists the recording to IndexedDB (see idbSaveRecording),
    // so the in-progress video is lost outright, not just left unsaved. Browsers don't allow a page
    // to block the reload/close shortcut itself (Cmd+Shift+R is a reserved browser-level shortcut,
    // not interceptable via keydown/preventDefault), but beforeunload's native confirmation prompt
    // is the standard way to give the user a chance to cancel before it actually happens.
    useEffect(() => {
        if (recordState === 'idle') return;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [recordState]);
    // Same risk as the beforeunload guard above, but for closing the modal itself (backdrop click or
    // the X button) instead of leaving the page — a real incident: recording stopped normally
    // (finish() already wrote the raw file to IndexedDB), but before the user advanced to
    // chunk-review/export, an accidental backdrop click dismissed the whole modal via onClose. The
    // raw recording was still recoverable afterwards, but only as the untouched original — the
    // chunk-review/export step (which applies Auto-Pace's speed-up-silence timeline) never ran, so
    // what got uploaded was the raw, un-paced recording instead of the processed one. Neither close
    // path had ever checked for this — only the 'uploading' stage was protected.
    const requestClose = useCallback(() => {
        if (stage === 'uploading') return; // never allow closing mid-upload — no confirm needed, just blocked
        const hasUnprocessedRecording = recordState !== 'idle' || (clips.length > 0 && stage !== 'done');
        if (hasUnprocessedRecording) { setShowCloseConfirm(true); return; }
        onClose();
    }, [stage, recordState, clips.length, onClose]);
    const [showDrawing, setShowDrawing] = useState(false);
    // Keep the drawing overlay mounted (hidden via CSS, not unmounted) once first opened, so
    // toggling it off/on with the hotkey never loses the in-progress drawing.
    const [drawingMounted, setDrawingMounted] = useState(false);
    useEffect(() => { if (showDrawing) setDrawingMounted(true); }, [showDrawing]);
    const [drawingSaveMsg, setDrawingSaveMsg] = useState('');
    // The drawing board unmounts when a recording stops; what was on it is kept here, comes back on
    // the next recording, and — if it was never saved to the note — the post-recording screen offers to save it.
    const drawingStashRef = useRef<DrawingStash | null>(null);
    const [unsavedDrawing, setUnsavedDrawing] = useState(false);
    const [savingStashedDrawing, setSavingStashedDrawing] = useState(false);
    const [drawingSaveFailed, setDrawingSaveFailed] = useState(false);
    const handleSaveDrawingToNote = useCallback(async (dataUrl: string, fabricJson?: string): Promise<boolean> => {
        const fail = (why: string) => {
            setDrawingSaveFailed(true);
            setDrawingSaveMsg(`✗ Chưa lưu được hình vẽ: ${why}`);
            setTimeout(() => setDrawingSaveMsg(''), 6000);
            return false;
        };
        try {
            const userId = auth.currentUser?.uid || uid;
            // The note open in the expanded view wins; otherwise the note this recording belongs to.
            const target = drawingTarget ?? (notebook?.id && node?.id ? { notebookId: notebook.id, nodeId: node.id, title: node.title || '' } : null);
            if (!userId || !target) return fail('không xác định được note');
            const nodeRef = doc(db, `users/${userId}/notebooks/${target.notebookId}/nodes/${target.nodeId}`);
            const attachment: NodeAttachment = {
                id: Math.random().toString(36).substring(7),
                type: 'drawing',
                content: dataUrl,
                mimeType: 'image/png',
                createdAt: Date.now(),
            };
            // The editable strokes go to Storage (a note document holds at most 1 MB); the note keeps
            // only their path. Without them the drawing still saves — it just reopens as a picture.
            if (fabricJson) {
                try {
                    const vectorPath = `users/${userId}/drawings/${attachment.id}.json`;
                    await uploadString(storageRef(storage, vectorPath), fabricJson, 'raw', { contentType: 'application/json' });
                    attachment.vectorPath = vectorPath;
                } catch (e) { console.warn('[drawing] strokes not saved; the picture still is', e); }
            }
            await updateDoc(nodeRef, { attachments: arrayUnion(attachment) });
            // Hand the server's list back to the app: its copy of the note predates this write.
            try {
                const fresh = await getDoc(nodeRef);
                const list = fresh.exists() ? (fresh.data().attachments as NodeAttachment[] | undefined) : undefined;
                if (list?.some(a => a.id === attachment.id)) onAttachmentsSaved?.(list, target.notebookId, target.nodeId);
            } catch { /* saved on the server; the note shows it after a reload */ }
            setDrawingSaveFailed(false);
            setDrawingSaveMsg(target.title ? `✓ Đã lưu vào note "${target.title.length > 40 ? target.title.slice(0, 40) + '…' : target.title}"` : '✓ Đã lưu vào note');
            setTimeout(() => setDrawingSaveMsg(''), 2000);
            return true;
        } catch (e: any) {
            console.error('[drawing] save to note failed', e);
            // A note document holds at most 1 MB; drawings are stored inside it.
            const tooBig = /exceeds the maximum|maximum allowed size|too large/i.test(String(e?.message || ''));
            return fail(tooBig ? 'note đã quá đầy (giới hạn 1 MB), hãy xóa bớt hình vẽ cũ' : (e?.code || e?.message || 'lỗi không rõ'));
        }
    }, [auth, db, uid, notebook?.id, node?.id, node?.title, drawingTarget, onAttachmentsSaved]);
    const loadDrawingVector = useCallback(async (path: string): Promise<string | null> => {
        try {
            const url = await getDownloadURL(storageRef(storage, path));
            const res = await fetch(url);
            return res.ok ? await res.text() : null;
        } catch { return null; }
    }, []);
    const [camPreviewStream, setCamPreviewStream] = useState<MediaStream | null>(null);
    const camPreviewRef = useRef<HTMLVideoElement | null>(null);
    const recordingSelfViewRef = useRef<HTMLVideoElement | null>(null);
    const [recordSecs, setRecordSecs] = useState(0);
    const [useWebcam, setUseWebcam] = useState(false);
    const [webcamShape, setWebcamShape] = useState<WebcamShape>(() => {
        try { return (localStorage.getItem('yt_webcam_shape') || 'rect') as WebcamShape; } catch { return 'rect'; }
    });
    const webcamShapeRef = useRef<WebcamShape>('rect');
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
    const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string>('');
    const [selectedMicId, setSelectedMicId] = useState<string>('');
    const [showDevicePanel, setShowDevicePanel] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const webcamStreamRef = useRef<MediaStream | null>(null);
    const micStreamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    // The recording's audio mix (screen + mic); the virtual classmate's voice is added to it.
    const mixDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    // "Record with a virtual classmate" (Tèo asks questions while you explain).
    const [withClassmate, setWithClassmate] = useState<boolean>(() => { try { return localStorage.getItem('yt_with_classmate') === '1'; } catch { return false; } });
    useEffect(() => { try { localStorage.setItem('yt_with_classmate', withClassmate ? '1' : '0'); } catch {} }, [withClassmate]);
    // Tèo can be called in and sent away at any time during a recording (button on the recording
    // bar, or the ` key while he is away). Each call is a fresh session (classmateSession is its key).
    const [classmateOn, setClassmateOn] = useState(false);
    // Who sits in: Tèo (asks questions) or the professor (answers, and corrects serious mistakes).
    const [companionRole, setCompanionRole] = useState<CompanionRole>(() => { try { return localStorage.getItem('yt_companion_role') === 'professor' ? 'professor' : 'classmate'; } catch { return 'classmate'; } });
    useEffect(() => { try { localStorage.setItem('yt_companion_role', companionRole); } catch {} }, [companionRole]);
    const [professorCue, setProfessorCue] = useState<CompanionCorrection | null>(null);
    // A real-time check result serious enough for the professor to step in on camera.
    const cueProfessor = useCallback((d: any) => {
        const score = Number(d?.correctnessScore);
        const serious = d?.status === 'needs_redo' || (d?.status === 'warning' && score < 70);
        const sure = d?.confidence === undefined || Number(d.confidence) >= 0.7;
        if (serious && sure && d?.mainIssue) setProfessorCue({ id: `${Date.now()}`, issue: String(d.mainIssue), suggestion: d.suggestedCorrection ? String(d.suggestedCorrection) : undefined });
    }, []);
    const [classmateJoinMidway, setClassmateJoinMidway] = useState(false);
    const [classmateSession, setClassmateSession] = useState(0);
    const toggleClassmate = useCallback(() => {
        setClassmateOn(on => {
            if (!on) { setClassmateJoinMidway(true); setClassmateSession(n => n + 1); }
            return !on;
        });
    }, []);
    const connectClassmateAudio = useCallback((el: HTMLAudioElement): boolean => {
        const ctx = audioContextRef.current, dest = mixDestRef.current;
        if (!ctx || !dest || ctx.state === 'closed') return false;
        try {
            const src = ctx.createMediaElementSource(el);
            src.connect(dest);                 // into the video
            src.connect(ctx.destination);      // and out of the speakers
            // Auto-Pace must hear him too, or his questions would count as silence and be sped up.
            if (autoPaceAnalyserRef.current) src.connect(autoPaceAnalyserRef.current);
            ctx.resume().catch(() => {});
            return true;
        } catch { return false; }
    }, []);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const screenVideoRef = useRef<HTMLVideoElement | null>(null);
    const webcamVideoRef = useRef<HTMLVideoElement | null>(null);
    const rafRef = useRef<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const clipStartSecsRef = useRef(0);
    const recordSecsRef = useRef(0); // always-current recordSecs for use in callbacks

    // Precise active-recording clock (ms), independent of the 1s-granularity recordSecs UI counter above.
    // activeMsRef accumulates confirmed elapsed active time; lastResumeAtRef marks the performance.now()
    // instant the recorder most recently started/resumed. Frozen (not advanced) while paused — this is the
    // canonical timeline for auto-pace SpeedChunk boundaries (see plan finding 1).
    const activeMsRef = useRef(0);
    const lastResumeAtRef = useRef(0);
    // pauseOwner distinguishes a human-initiated pause from an Auto-Pace idle-pause (a manual pause must
    // never be auto-resumed by the state machine). Declared here (not just near pauseRecording below)
    // because getPreciseActiveMs relies on it: checking pauseOwnerRef instead of the MediaRecorder's own
    // state is deliberate — by the time `doFullStop` runs as `mr.onstop`, the recorder is already
    // 'inactive', not 'recording', so state-based detection would under-count the final open chunk.
    const pauseOwnerRef = useRef<'manual' | 'auto-idle' | null>(null);
    const getPreciseActiveMs = useCallback(() => {
        if (pauseOwnerRef.current == null) {
            return activeMsRef.current + (performance.now() - lastResumeAtRef.current);
        }
        return activeMsRef.current;
    }, []);

    // ── Spin-off clips ──
    // While recording (Auto-Pace), a part can be marked — tap once where a self-contained
    // explanation begins, once where it ends. Each marked part is also rendered as its own video
    // from the same raw recording. Times are on the recording's active clock, the same clock the
    // speed chunks use, so a clip is simply the chunks that fall inside its mark.
    interface ClipMark { startMs: number; endMs: number | null; title: string }
    const clipMarksRef = useRef<ClipMark[]>([]);
    const [clipMarks, setClipMarksState] = useState<ClipMark[]>([]);
    const setClipMarks = (next: ClipMark[]) => { clipMarksRef.current = next; setClipMarksState(next); };
    const openClipMark = clipMarks.find(m => m.endMs === null);
    const MIN_CLIP_MARK_MS = 5000; // an accidental double tap is not a clip
    const toggleClipMark = () => {
        const nowMs = Math.round(getPreciseActiveMs());
        const marks = clipMarksRef.current;
        const open = marks.find(m => m.endMs === null);
        if (open) {
            setClipMarks(nowMs - open.startMs < MIN_CLIP_MARK_MS
                ? marks.filter(m => m !== open)
                : marks.map(m => (m === open ? { ...m, endMs: nowMs } : m)));
        } else {
            setClipMarks([...marks, { startMs: nowMs, endMs: null, title: '' }]);
        }
    };

    // "Insert clip" — pause recording, splice in an existing video from the library, resume. Fully
    // independent of Auto-Pace/AI Checkpoint (mutually exclusive with Auto-Pace only, enforced by
    // hiding the button — see the floating toolbar). timelineRef stays empty unless this is used at
    // least once, so a normal recording session has zero extra behavior.
    const timelineRef = useRef<TimelineSegment[]>([]);
    const currentLiveStartRef = useRef(0);
    const [timelineSegments, setTimelineSegments] = useState<TimelineSegment[]>([]);
    const [showInsertPicker, setShowInsertPicker] = useState(false);
    // "Continue a video": earlier videos (with a backup copy) that go in front of this recording.
    // The export joins them on the server — the originals are not changed. Remembered per note, so
    // closing the window or a crash does not lose the choice.
    const seriesKey = `yt_series_${notebook.id}_${node.id}`;
    const [seriesParts, setSeriesParts] = useState<SeriesPart[]>(() => {
        try { const v = JSON.parse(localStorage.getItem(seriesKey) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
    });
    useEffect(() => {
        try { seriesParts.length ? localStorage.setItem(seriesKey, JSON.stringify(seriesParts)) : localStorage.removeItem(seriesKey); } catch { /* best effort */ }
    }, [seriesParts, seriesKey]);
    // "Continue recording" after Stop (pressed by accident, or the browser's "Stop sharing"): the
    // recording so far, cut at a chosen moment, is uploaded as its own raw file and the export puts
    // it in front of the new recording. Remembered per note, like the series parts above.
    const resumeKey = `yt_resume_${notebook.id}_${node.id}`;
    const [resumeParts, setResumeParts] = useState<ResumePart[]>(() => {
        try { const v = JSON.parse(localStorage.getItem(resumeKey) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
    });
    useEffect(() => {
        try { resumeParts.length ? localStorage.setItem(resumeKey, JSON.stringify(resumeParts)) : localStorage.removeItem(resumeKey); } catch { /* best effort */ }
    }, [resumeParts, resumeKey]);
    // The earlier recordings' bytes and their uploads, kept until the export has them.
    const resumeBlobsRef = useRef(new Map<string, Blob>());
    const resumeUploadsRef = useRef(new Map<string, Promise<void>>());
    const [resumePickMs, setResumePickMs] = useState<number | null>(null); // null = panel closed
    const resumePreviewRef = useRef<HTMLVideoElement | null>(null);
    const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
    const [showSeriesPicker, setShowSeriesPicker] = useState(false);
    const resumePanelOpen = resumePickMs !== null;
    useEffect(() => {
        if (!resumePanelOpen || !clips[0]) { setResumePreviewUrl(null); return; }
        const url = URL.createObjectURL(clips[0].blob);
        setResumePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [resumePanelOpen, clips]);
    const [seriesSearch, setSeriesSearch] = useState('');
    const [seriesTrimKey, setSeriesTrimKey] = useState<string | null>(null);
    // What was said in the earlier parts, so the live AI check knows it was already explained.
    const seriesPriorRef = useRef('');
    useEffect(() => {
        seriesPriorRef.current = seriesParts.map(p => {
            const n = notebook.nodes.find(x => x.id === p.nodeId);
            const v = n ? getVideosForNode(n).find(x => x.youtubeVideoId === p.videoId) : undefined;
            return v?.youtubeTranscript || '';
        }).filter(Boolean).join('\n');
    }, [seriesParts, notebook.nodes]);
    // Joined and uploaded: the next recording on this note starts a fresh video.
    useEffect(() => { if (stage === 'done') { setSeriesParts([]); setResumeParts([]); resumeBlobsRef.current.clear(); } }, [stage]);
    const seriesChunks = () => seriesParts.map(p => ({
        startMs: p.startMs, endMs: p.endMs ?? 4 * 60 * 60 * 1000, speed: 1, kind: 'speaking' as const,
        sourceClip: { notebookId: notebook.id, nodeId: p.nodeId, videoId: p.videoId },
    }));
    const [insertPickerSearch, setInsertPickerSearch] = useState('');

    // Checkpoint mode
    const [checkpointMode, setCheckpointMode] = useState<CheckpointMode>(() => {
        try {
            const v = localStorage.getItem('yt_checkpoint_mode_v2');
            return (['off', 'auto', 'manual', 'hybrid'] as CheckpointMode[]).includes(v as CheckpointMode) ? v as CheckpointMode : 'off';
        } catch { return 'off'; }
    });
    const [checkpointMins, setCheckpointMins] = useState<number>(() => {
        try { return parseFloat(localStorage.getItem('yt_checkpoint_mins') || '3') || 3; } catch { return 3; }
    });

    // Auto-Pace (Beta): opt-in, default off, mutually exclusive with AI checkpoint mode for v1 —
    // both mutate the recording lifecycle (pause/resume, chunk bookkeeping) and entangling them is
    // unnecessary risk. See plan: /Users/anhtran/.claude/plans/ticklish-percolating-torvalds.md
    const [autoPaceEnabled, setAutoPaceEnabledState] = useState<boolean>(() => {
        try { return localStorage.getItem('yt_autopace_v1') === '1'; } catch { return false; }
    });
    const setAutoPaceEnabled = useCallback((next: boolean) => {
        setAutoPaceEnabledState(next);
        if (next) setCheckpointMode('off');
    }, []);
    useEffect(() => {
        try { localStorage.setItem('yt_autopace_v1', autoPaceEnabled ? '1' : '0'); } catch {}
    }, [autoPaceEnabled]);

    // User-configurable playback speeds — kept as refs (not just state) so the recording-time state
    // machine (tick(), captured by closure when startRecording runs) always reads the latest value
    // without needing to be re-created mid-recording.
    const [speakingSpeed, setSpeakingSpeedState] = useState<number>(() => {
        try { return Number(localStorage.getItem('yt_autopace_speaking_speed')) || 1; } catch { return 1; }
    });
    const [silentSpeed, setSilentSpeedState] = useState<number>(() => {
        try { return Number(localStorage.getItem('yt_autopace_silent_speed')) || 4; } catch { return 4; }
    });
    const speakingSpeedRef = useRef(speakingSpeed);
    const silentSpeedRef = useRef(silentSpeed);
    const setSpeakingSpeed = useCallback((v: number) => {
        setSpeakingSpeedState(v); speakingSpeedRef.current = v;
        try { localStorage.setItem('yt_autopace_speaking_speed', String(v)); } catch {}
    }, []);
    const setSilentSpeed = useCallback((v: number) => {
        setSilentSpeedState(v); silentSpeedRef.current = v;
        try { localStorage.setItem('yt_autopace_silent_speed', String(v)); } catch {}
    }, []);
    // Seconds of total silence + inactivity before Auto-Pace auto-pauses (TRUE_IDLE) — user-tunable
    // so it can be dialed down while testing scenarios with real waiting gaps (e.g. Voice Tutor
    // "thinking" time) without redeploying. Same ref-backed pattern as speakingSpeed/silentSpeed.
    const [idleThresholdSecs, setIdleThresholdSecsState] = useState<number>(() => {
        try { return Number(localStorage.getItem('yt_autopace_idle_threshold_secs')) || 5; } catch { return 5; }
    });
    const idleThresholdSecsRef = useRef(idleThresholdSecs);
    const setIdleThresholdSecs = useCallback((v: number) => {
        setIdleThresholdSecsState(v); idleThresholdSecsRef.current = v;
        try { localStorage.setItem('yt_autopace_idle_threshold_secs', String(v)); } catch {}
    }, []);

    // Auto-Pace state machine internals (plan step 6). Tunable constants first —
    // these are the values most likely to need real-world adjustment after testing.
    const AUTOPACE_POLL_MS = 50;               // was 200 — VAD detection latency (see backdate below) includes up to one full poll interval
    const AUTOPACE_SPEECH_START_RMS = 0.02;   // hysteresis: enter "speaking" above this
    const AUTOPACE_SPEECH_END_RMS = 0.012;    // hysteresis: only leave "speaking" below this
    const AUTOPACE_SPEECH_HANGOVER_MS = 700;  // bridges brief gaps between spoken words
    // A word's own volume ramps up over ~50-150ms before crossing AUTOPACE_SPEECH_START_RMS, plus
    // up to one poll interval to actually sample it — that stretch of real speech was still
    // playing during the "silent" (fast) chunk right before detection fired, so it gets eaten
    // when that chunk is sped up (reported as "the first word gets cut off"). Compensate by
    // backdating the speaking-chunk boundary into the tail of the preceding silent chunk instead
    // of opening it exactly at the detection instant.
    const AUTOPACE_SPEECH_BACKDATE_MS = 250;
    const AUTOPACE_ACTIVE_WINDOW_MS = 4000;   // "recently interacting" window
    // Idle threshold is user-tunable — see idleThresholdSecs/idleThresholdSecsRef above.
    const AUTOPACE_AUTO_PAUSE_COOLDOWN_MS = 3000; // min dwell after an auto-resume before re-pausing
    const AUTOPACE_CHECK_MAX_WINDOW_MS = 75000;   // cap one content-check audio window; longer speaking stretches rotate into a fresh window
    const AUTOPACE_CHECK_MIN_DURATION_SECS = 8;   // skip analyzing windows shorter than this (jitter blips, not real explanation)
    // A check window no longer ends at every pause between speaking stretches: real explanations
    // come in bursts of a few seconds (talk, type, think), and closing at each 0.7 s gap left almost
    // every window under the 8 s minimum — so only the first stretch of a recording was ever checked.
    // A window now spans several stretches and is sent at the first natural pause after this much.
    const AUTOPACE_CHECK_TARGET_WINDOW_MS = 20000;

    const autoPaceModeRef = useRef<AutoPaceMode>('speaking');
    const autoPaceTickRef = useRef<SteadyInterval | null>(null);
    const autoPaceActivityCleanupRef = useRef<(() => void) | null>(null);
    const autoPaceAnalyserRef = useRef<AnalyserNode | null>(null);
    const autoPaceAnalyserBufRef = useRef<Uint8Array | null>(null);
    const isSpeakingRawRef = useRef(false);   // raw hysteresis state, pre-hangover
    const lastSpeechAtRef = useRef(0);        // performance.now() of most recent "speaking" sample
    const lastInteractionAtRef = useRef(0);   // performance.now() of most recent keydown/pointerdown/drag
    const lastAutoResumeAtRef = useRef(0);
    const speedChunksRef = useRef<SpeedChunk[]>([]);
    const currentChunkStartMsRef = useRef(0);
    const currentChunkSpeedRef = useRef(1);
    const currentChunkKindRef = useRef<'speaking' | 'silent'>('speaking');
    const [speedChunks, setSpeedChunks] = useState<SpeedChunk[]>([]); // live during recording (drives the floating check panel below) AND the post-stop review UI (step 7)

    // Auto-Pace content check: a fully independent audio-capture + analyze pipeline, separate from
    // AI Checkpoint's audioMrRef/segments. Never shares state with Checkpoint — see plan doc.
    const autoPaceCheckMrRef = useRef<MediaRecorder | null>(null);
    const autoPaceCheckWindowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoPaceCheckWindowCtlRef = useRef<{ rotate: boolean } | null>(null);
    const autoPaceCheckWindowStartRef = useRef(0); // active-time ms at which the open check window began
    // Updates for the SpeedChunk currently being spoken, queued here until closeAutoPaceChunk
    // actually pushes that chunk into speedChunksRef (a check window can complete before the
    // speaking stretch itself ends, especially after a max-window rotation).
    const pendingAutoPaceUpdatesRef = useRef<((c: SpeedChunk) => SpeedChunk)[]>([]);
    // Live floating check panel during recording — independent of Checkpoint's showSegmentPanel/
    // panelMinimized, same collapsed/expanded/minimized shape for visual consistency only.
    const [showAutoPaceCheckPanel, setShowAutoPaceCheckPanel] = useState(false);
    const [autoPaceCheckPanelMinimized, setAutoPaceCheckPanelMinimized] = useState(false);
    // Draggable via its grip so it can be nudged off whatever it covers (it sits on top of the
    // note's own top-right toolbar by default). null = default top-right corner. Remembered across
    // recordings; double-click the grip to snap back to the corner.
    const [autoPacePanelPos, setAutoPacePanelPos] = useState<{ x: number; y: number } | null>(() => {
        try { const s = localStorage.getItem('sb_autopace_panel_pos'); return s ? JSON.parse(s) : null; } catch { return null; }
    });
    const autoPacePanelRef = useRef<HTMLDivElement>(null);
    const startAutoPacePanelDrag = (e: React.PointerEvent) => {
        if (e.button !== 0 || !autoPacePanelRef.current) return;
        e.preventDefault();
        const rect = autoPacePanelRef.current.getBoundingClientRect();
        const offX = e.clientX - rect.left, offY = e.clientY - rect.top;
        let last = { x: rect.left, y: rect.top };
        const onMove = (ev: PointerEvent) => {
            const w = autoPacePanelRef.current?.offsetWidth || 0, h = autoPacePanelRef.current?.offsetHeight || 0;
            last = {
                x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - offX)),
                y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - offY)),
            };
            setAutoPacePanelPos(last);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            try { localStorage.setItem('sb_autopace_panel_pos', JSON.stringify(last)); } catch { /* ignore */ }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };
    const resetAutoPacePanelPos = () => {
        setAutoPacePanelPos(null);
        try { localStorage.removeItem('sb_autopace_panel_pos'); } catch { /* ignore */ }
    };
    const [showChecksModal, setShowChecksModal] = useState(false);
    // Per-row expand — the list stays compact (truncated) by default so it doesn't eat screen space
    // while recording; clicking a row reveals the full quoted issue/correction text.
    const [expandedAutoPaceCheckIdx, setExpandedAutoPaceCheckIdx] = useState<number | null>(null);
    // Live meter for the recording toolbar — lets the user SEE why a mode switch happened (their
    // voice crossing the threshold, or an interaction), rather than it feeling like a black box.
    const [autoPaceLiveRms, setAutoPaceLiveRms] = useState(0);
    const [autoPaceLiveMode, setAutoPaceLiveMode] = useState<AutoPaceMode>('speaking');

    const captureAutoPaceThumbnail = useCallback((): string | undefined => {
        try {
            const srcEl = (canvasRef.current && useWebcam ? canvasRef.current : screenVideoRef.current) as (HTMLCanvasElement | HTMLVideoElement | null);
            if (!srcEl) return undefined;
            const w = 160;
            const srcW = (srcEl as HTMLVideoElement).videoWidth || (srcEl as HTMLCanvasElement).width || 1;
            const srcH = (srcEl as HTMLVideoElement).videoHeight || (srcEl as HTMLCanvasElement).height || 1;
            const h = Math.round(w * (srcH / srcW));
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            const ctx = c.getContext('2d');
            if (!ctx) return undefined;
            ctx.drawImage(srcEl as CanvasImageSource, 0, 0, w, h);
            return c.toDataURL('image/jpeg', 0.5);
        } catch { return undefined; } // best-effort — a chunk missing a thumbnail is still fully usable
    }, [useWebcam]);

    const currentChunkThumbnailRef = useRef<string | undefined>(undefined);

    // Applies an update to the SpeedChunk at chunkIndex. If that chunk hasn't been pushed into
    // speedChunksRef yet (still the open/speaking chunk), the update is queued and replayed by
    // closeAutoPaceChunk instead — see pendingAutoPaceUpdatesRef above.
    const applyAutoPaceCheckUpdate = useCallback((chunkIndex: number, updater: (c: SpeedChunk) => SpeedChunk) => {
        if (chunkIndex < speedChunksRef.current.length) {
            speedChunksRef.current = speedChunksRef.current.map((c, i) => i === chunkIndex ? updater(c) : c);
            setSpeedChunks(speedChunksRef.current);
        } else {
            pendingAutoPaceUpdatesRef.current = [...pendingAutoPaceUpdatesRef.current, updater];
        }
    }, []);

    // Independent of AI Checkpoint's analyzeSegmentAudio — same Cloud Function, separate call site
    // and separate result storage (SpeedChunk.checks, not SegmentStatus/segments).
    const analyzeAutoPaceChunk = useCallback(async (chunkIndex: number, audioBlob: Blob, windowStartMs: number, windowEndMs: number) => {
        const MAX_AUDIO_BYTES = 7 * 1024 * 1024;
        if (audioBlob.size > MAX_AUDIO_BYTES) {
            applyAutoPaceCheckUpdate(chunkIndex, c => ({ ...c, checking: false }));
            return;
        }
        applyAutoPaceCheckUpdate(chunkIndex, c => ({ ...c, checking: true }));
        try {
            const reader = new FileReader();
            const base64 = await new Promise<string>((resolve, reject) => {
                reader.onload = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(audioBlob);
            });
            const fn = httpsCallable(functions, 'analyzeVideoSegment');
            const res = await fn({
                audioBase64: base64,
                audioMimeType: audioBlob.type || 'audio/webm',
                noteTitle: node.title || '',
                noteContent: node.content || '',
                noteAiFeedback: node.correctnessFeedback || '',
                segmentIndex: chunkIndex,
                segmentDurationSecs: (windowEndMs - windowStartMs) / 1000,
                // Everything said earlier in this recording, so the check judges this part in context.
                priorTranscript: [seriesPriorRef.current, ...speedChunksRef.current
                    .flatMap(c => c.checks || [])
                    .filter(k => k.endMs <= windowStartMs && k.transcript)
                    .sort((a, b) => a.startMs - b.startMs)
                    .map(k => k.transcript)].filter(Boolean).join('\n'),
                language: language === 'vietnamese' ? 'vietnamese' : 'english',
            });
            const d = res.data as any;
            const result: AutoPaceCheckResult = {
                status: d.status === 'needs_redo' || d.status === 'warning' ? d.status : 'good',
                score: d.correctnessScore,
                clarityScore: d.clarityScore,
                summary: d.summary || '',
                whatWasRight: d.whatWasRight || '',
                mainIssue: d.mainIssue,
                suggestion: d.suggestedCorrection,
                transcript: d.transcript || d.summary || '',
                startMs: windowStartMs,
                endMs: windowEndMs,
            };
            applyAutoPaceCheckUpdate(chunkIndex, c => ({ ...c, checking: false, checks: [...(c.checks || []), result] }));
            cueProfessor(d);
        } catch {
            applyAutoPaceCheckUpdate(chunkIndex, c => ({ ...c, checking: false }));
        }
    }, [node.title, node.content, node.correctnessFeedback, language, applyAutoPaceCheckUpdate, cueProfessor]);

    // Arms/re-arms the max-window rotation timeout for one check recorder — used both when a window
    // starts and when resumeRecording re-arms a fresh timeout after a manual pause.
    const armAutoPaceCheckWindowTimer = useCallback((mr: MediaRecorder, ctl: { rotate: boolean }) => {
        autoPaceCheckWindowTimerRef.current = setTimeout(() => {
            ctl.rotate = true;
            if (mr.state !== 'inactive') mr.stop();
        }, AUTOPACE_CHECK_MAX_WINDOW_MS);
    }, []);

    // Starts one content-check recording window for the speaking chunk at chunkIndex (its future
    // index in speedChunksRef — predictable since chunks only ever get pushed in order). Rotates
    // into a fresh window on its own if AUTOPACE_CHECK_MAX_WINDOW_MS elapses while still speaking;
    // closeAutoPaceChunk stops it without setting ctl.rotate, which ends the chain for good.
    const startAutoPaceCheckWindow = useCallback((chunkIndex: number, windowStartMs: number) => {
        if (!micStreamRef.current || micStreamRef.current.getAudioTracks().length === 0) return;
        const audioMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
        const mr = new MediaRecorder(new MediaStream(micStreamRef.current.getAudioTracks()), { mimeType: audioMime });
        const chunks: Blob[] = []; // owned by this recorder instance only — never a shared/reset ref
        const ctl = { rotate: false };
        mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
        mr.onstop = () => {
            if (autoPaceCheckWindowTimerRef.current) { clearTimeout(autoPaceCheckWindowTimerRef.current); autoPaceCheckWindowTimerRef.current = null; }
            const windowEndMs = getPreciseActiveMs();
            const durSecs = (windowEndMs - windowStartMs) / 1000;
            if (durSecs >= AUTOPACE_CHECK_MIN_DURATION_SECS) {
                const blob = new Blob(chunks, { type: audioMime });
                analyzeAutoPaceChunk(chunkIndex, blob, windowStartMs, windowEndMs);
            }
            if (ctl.rotate) startAutoPaceCheckWindow(chunkIndex, windowEndMs);
        };
        autoPaceCheckMrRef.current = mr;
        autoPaceCheckWindowCtlRef.current = ctl;
        autoPaceCheckWindowStartRef.current = windowStartMs;
        mr.start(500);
        armAutoPaceCheckWindowTimer(mr, ctl);
    }, [getPreciseActiveMs, analyzeAutoPaceChunk, armAutoPaceCheckWindowTimer]);

    const closeAutoPaceChunk = useCallback((endMs: number, final = false) => {
        const startMs = currentChunkStartMsRef.current;
        const mr = autoPaceCheckMrRef.current;
        if (mr && mr.state !== 'inactive') {
            // Send the window at the end of the recording, or at a natural pause (a speaking stretch
            // ending) once it holds enough explanation; otherwise it keeps listening across the gap.
            const longEnough = endMs - autoPaceCheckWindowStartRef.current >= AUTOPACE_CHECK_TARGET_WINDOW_MS;
            if (final || (currentChunkKindRef.current === 'speaking' && longEnough)) {
                mr.stop(); // without rotate: onstop sends this window and the chain ends
                autoPaceCheckMrRef.current = null;
                autoPaceCheckWindowCtlRef.current = null;
            }
        } else {
            autoPaceCheckMrRef.current = null;
            autoPaceCheckWindowCtlRef.current = null;
        }
        if (endMs <= startMs) return; // no zero/negative-length chunks
        let chunk: SpeedChunk = {
            index: speedChunksRef.current.length,
            startMs, endMs,
            speed: currentChunkSpeedRef.current,
            kind: currentChunkKindRef.current,
            thumbnail: currentChunkThumbnailRef.current,
        };
        for (const updater of pendingAutoPaceUpdatesRef.current) chunk = updater(chunk);
        pendingAutoPaceUpdatesRef.current = [];
        speedChunksRef.current = [...speedChunksRef.current, chunk];
        setSpeedChunks(speedChunksRef.current);
    }, []);

    const openAutoPaceChunk = useCallback((startMs: number, kind: 'speaking' | 'silent') => {
        currentChunkStartMsRef.current = startMs;
        currentChunkKindRef.current = kind;
        currentChunkSpeedRef.current = kind === 'speaking' ? speakingSpeedRef.current : silentSpeedRef.current;
        currentChunkThumbnailRef.current = captureAutoPaceThumbnail();
        if (kind === 'speaking') {
            pendingAutoPaceUpdatesRef.current = [];
            // A window still open from an earlier stretch keeps going (it spans the pause).
            if (!autoPaceCheckMrRef.current || autoPaceCheckMrRef.current.state === 'inactive') {
                startAutoPaceCheckWindow(speedChunksRef.current.length, startMs);
            }
        }
    }, [captureAutoPaceThumbnail, startAutoPaceCheckWindow]);
    const [segments, setSegments] = useState<SegmentStatus[]>([]);
    const segmentsRef = useRef<SegmentStatus[]>([]);
    const [recoveredFile, setRecoveredFile] = useState<File | null>(null);
    const [showSegmentPanel, setShowSegmentPanel] = useState(false);
    const [panelMinimized, setPanelMinimized] = useState(false);
    const [expandedSegIdx, setExpandedSegIdx] = useState<number | null>(null);
    const audioMrRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const chunkBytesRef = useRef(0); // running total of video chunk bytes in RAM
    const [recordingMB, setRecordingMB] = useState(0);
    const [sizeBanner, setSizeBanner] = useState<string | null>(null);
    const micLostWarnedRef = useRef(false);
    const sizeWarn800Fired = useRef(false);
    const sizeStop2GB = 2 * 1024 * 1024 * 1024;
    const isCheckpointCycleRef = useRef(false);
    const checkpointTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const currentSegmentIndexRef = useRef(0);
    const segmentStartSecsRef = useRef(0);
    const mimeTypeRef = useRef('');
    const screenStreamRef = useRef<MediaStream | null>(null);
    // Screen sharing can end on its own mid-recording — e.g. macOS reconfigures displays when a
    // Wacom tablet reconnects, or "Stop sharing" in Chrome's bar. The recording then pauses and
    // the user shares the screen again; the new screen feeds the SAME recording. Without webcam
    // the screen goes through a frame pipe (screenPipeRef) whose source can be swapped; with
    // webcam the compositing canvas just draws from the new stream.
    const screenPipeRef = useRef<{ writer: WritableStreamDefaultWriter<any>; reader: ReadableStreamDefaultReader<any> | null } | null>(null);
    const onScreenLostRef = useRef<() => void>(() => {});
    const screenProbeRef = useRef<ReturnType<typeof createScreenActivityProbe>>(null);
    const [screenLost, setScreenLost] = useState(false);
    const [resharing, setResharing] = useState(false);
    const [micUnavailable, setMicUnavailable] = useState(false);
    const [redoConfirm, setRedoConfirm] = useState<number | null>(null);
    // Auto-Pace's per-sentence equivalent of redoConfirm above — "go back to the exact moment
    // just before this flagged check" instead of Checkpoint's whole-segment granularity. Recording
    // is paused the instant the icon is clicked (see requestAutoPaceRevert) so nothing more gets
    // said while the user decides; confirming or cancelling always resumes it.
    const [pendingAutoPaceRevert, setPendingAutoPaceRevert] = useState<{ chunkIndex: number; targetMs: number } | null>(null);
    // Re-watch while paused and re-record from a chosen moment (Auto-Pace recordings). `rewatch` holds
    // the playable copy of what was recorded so far; `rewatchCuts` the stretches discarded this way.
    const [rewatch, setRewatch] = useState<{ url: string; endMs: number } | null>(null);
    const [rewatchLoading, setRewatchLoading] = useState(false);
    const [rewatchCuts, setRewatchCuts] = useState<{ fromMs: number; toMs: number }[]>([]);
    // What the chunk list looked like before the last discard, so it can be taken back until the
    // recording resumes (after that the old open chunk no longer describes anything).
    const rewatchUndoRef = useRef<{ chunks: SpeedChunk[]; openStartMs: number; openKind: 'speaking' | 'silent'; openSpeed: number; openThumb?: string } | null>(null);
    // Writes of raw-video parts to IndexedDB that have not finished yet; a re-watch must wait for
    // them or the copy it assembles would be missing the newest part.
    const pendingPartSavesRef = useRef(new Set<Promise<void>>());
    const [barPos, setBarPos] = useState<{x:number,y:number}>(() => {
        try { return {x: 16, y: Math.max(0, (window.innerHeight || 800) - 80)}; } catch { return {x: 16, y: 720}; }
    });
    const dragRef = useRef<{active:boolean,ox:number,oy:number,bx:number,by:number}>({active:false,ox:0,oy:0,bx:0,by:0});
    const segChunkCountsRef = useRef<number[]>([]); // chunk count at end of each segment (for redo splicing)
    const redoSplicesRef = useRef<{keepEnd: number, redoStart: number}[]>([]); // video splices for redo
    const handleCheckpointRef = useRef<(() => void) | null>(null); // set inside startRecording, called by timer/manual/redo

    // Drawing hotkey config (accessible pre-recording)
    const [drawHotkeys, setDrawHotkeys] = useState<DrawHkMap>(loadDrawHk);
    const [showDrawHkPanel, setShowDrawHkPanel] = useState(false);
    const [remapDrawAction, setRemapDrawAction] = useState<DrawHkAction | null>(null);

    // Keep ref in sync so setTimeout closures always see the latest segments
    useEffect(() => { segmentsRef.current = segments; }, [segments]);

    useEffect(() => {
        try { localStorage.setItem('yt_checkpoint_mode_v2', checkpointMode); } catch {}
    }, [checkpointMode]);

    useEffect(() => {
        try { localStorage.setItem('yt_checkpoint_mins', String(checkpointMins)); } catch {}
    }, [checkpointMins]);

    useEffect(() => {
        try { localStorage.setItem('yt_webcam_shape', webcamShape); } catch {}
        webcamShapeRef.current = webcamShape;
    }, [webcamShape]);

    // Sync drawHotkeys → localStorage + propagate toggleOverlay key to ScreenDrawingOverlay via same key
    useEffect(() => { saveDrawHk(drawHotkeys); }, [drawHotkeys]);

    // Wire self-view video during recording
    useEffect(() => {
        const el = recordingSelfViewRef.current;
        if (!el) return;
        if (recordState !== 'idle' && useWebcam && webcamStreamRef.current) {
            el.srcObject = webcamStreamRef.current;
        } else {
            el.srcObject = null;
        }
    }, [recordState, useWebcam]);

    // Live webcam preview in pick stage so user can adjust camera angle before recording
    useEffect(() => {
        if (!useWebcam || stage !== 'pick') {
            camPreviewStream?.getTracks().forEach(t => t.stop());
            setCamPreviewStream(null);
            return;
        }
        let active = true;
        (async () => {
            try {
                const constraints = selectedCameraId
                    ? { video: { deviceId: { exact: selectedCameraId } } }
                    : { video: true };
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
                setCamPreviewStream(stream);
            } catch {}
        })();
        return () => {
            active = false;
            setCamPreviewStream(prev => { prev?.getTracks().forEach(t => t.stop()); return null; });
        };
    }, [useWebcam, stage, selectedCameraId]);

    // Remap listener for pre-recording hotkey panel
    useEffect(() => {
        if (!remapDrawAction) return;
        const onKey = (e: KeyboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Escape') { setRemapDrawAction(null); return; }
            if (['Control', 'Meta', 'Alt', 'Shift'].includes(e.key)) return;
            const newKey = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            setDrawHotkeys(prev => ({ ...prev, [remapDrawAction]: newKey }));
            setRemapDrawAction(null);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [remapDrawAction]);

    const enumerateDevices = useCallback(async () => {
        // Need at least one getUserMedia call first to get labels
        try { await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { /* ignore */ }
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter(d => d.kind === 'videoinput');
        const micsFound = devices.filter(d => d.kind === 'audioinput');
        setCameras(cams);
        setMics(micsFound);
        if (cams.length && !selectedCameraId) setSelectedCameraId(cams[0].deviceId);
        if (micsFound.length && !selectedMicId) setSelectedMicId(micsFound[0].deviceId);
    }, [selectedCameraId, selectedMicId]);

    // Metadata
    const [title, setTitle] = useState(initialTitle || `${node.title || 'Untitled'} — ${notebook.title || 'StudyBoard'}`);
    const [description, setDescription] = useState(buildFooter(node, notebook, uid));
    const [isGenDesc, setIsGenDesc] = useState(false);

    const generateDescription = useCallback(async () => {
        const raw = buildRawContext(node);
        if (!raw.trim()) return;
        setIsGenDesc(true);
        try {
            const aiSummary = await generateYouTubeDescription(node.title || '', node.content || '', language);
            setDescription(aiSummary + '\n\n' + buildFooter(node, notebook, uid));
        } catch {
            // keep existing on error
        } finally {
            setIsGenDesc(false);
        }
    }, [node, notebook, language]);

    // Assemble the full spoken transcript from recording segments (in order), if AI Checkpoints
    // was enabled during recording. This reflects what was actually SAID, unlike node.content.
    const recordingTranscript = [...segments]
        .filter(s => s.status !== 'recording' && s.transcript?.trim())
        .sort((a, b) => a.index - b.index)
        .map(s => s.transcript!.trim())
        .join(' ');

    const [isGenFromTranscript, setIsGenFromTranscript] = useState(false);
    const generateFromTranscript = useCallback(async () => {
        if (!recordingTranscript.trim()) return;
        setIsGenFromTranscript(true);
        try {
            const result = await generateYouTubeMetadataFromTranscript(recordingTranscript, node.title || '', language);
            if (result.title) setTitle(result.title);
            if (result.description) setDescription(result.description + '\n\n' + buildFooter(node, notebook, uid));
        } catch {
            // keep existing on error
        } finally {
            setIsGenFromTranscript(false);
        }
    }, [recordingTranscript, node, notebook, language]);

    // "Note + AI feedback" (Learning-in-Public description overhaul): the description becomes a
    // reusable study note (node.content is canonical, transcript fills in anything spoken-but-not-
    // written) plus a visible AI quality-check section, styled like the GitHub AI-check callout.
    // Manual-trigger only, per product decision — same pattern as the other two generators.
    const [isGenNoteAndFeedback, setIsGenNoteAndFeedback] = useState(false);
    const generateNoteAndFeedback = useCallback(async () => {
        setIsGenNoteAndFeedback(true);
        try {
            const noteResult = await generateVideoNote(node.title || '', node.content || '', node.analysis?.keyInsights, recordingTranscript, language);

            // AI feedback needs real quality-check data — either AI Checkpoint segments, or (for
            // Auto-Pace recordings, which never produce segments) that mode's own independent
            // content-check windows. Skip entirely rather than fabricate a score when neither exists.
            const autoPaceChecksFlat = segments.length === 0
                ? speedChunks.flatMap(c => (c.checks || []).map(chk => ({
                    startMs: chk.startMs, endMs: chk.endMs, status: chk.status, score: chk.score,
                    mainIssue: chk.mainIssue, suggestion: chk.suggestion, transcript: chk.transcript,
                })))
                : [];
            let feedbackBlock = '';
            if (segments.length > 0 || autoPaceChecksFlat.length > 0) {
                try {
                    // A video is a short spoken companion to an already-checked note, not required to
                    // cover it fully — checkSummary reflects only correctness of what was actually
                    // said, kept to 1-2 sentences so it reads naturally in a public description.
                    const pc = await postCheckVideo(notebook.id, node.id, language, undefined, segments.length === 0 ? autoPaceChecksFlat : undefined);
                    if (pc.checkSummary) {
                        feedbackBlock = `\n\n──────────────────\n🤖 ${vn ? 'KIỂM TRA AI' : 'AI CHECK'}: ${pc.checkSummary}\n──────────────────`;
                    }
                } catch { /* feedback is optional — keep the note even if this call fails */ }
            }

            const footer = buildFooter(node, notebook, uid);
            // Deterministic length handling: shrink the note (not the feedback/footer) to fit YouTube's
            // 5000-char cap. No second AI call — cheaper, faster, and avoids a different note on retry.
            const MAX_LEN = 5000;
            const fixedLen = feedbackBlock.length + footer.length + 4; // '\n\n' separators
            let note = noteResult.note;
            const noteBudget = MAX_LEN - fixedLen;
            if (note.length > noteBudget && noteBudget > 100) {
                note = note.slice(0, noteBudget - 1).trimEnd() + '…';
            }
            setDescription(note + feedbackBlock + '\n\n' + footer);
        } catch {
            // keep existing on error
        } finally {
            setIsGenNoteAndFeedback(false);
        }
    }, [node, notebook, uid, language, recordingTranscript, segments, speedChunks, vn]);

    // Thumbnail — managed via ThumbnailEditorModal
    const [showThumbEditor, setShowThumbEditor] = useState(false);
    const [thumbDataUrl, setThumbDataUrl] = useState<string>(() =>
        generateThumbnail({ title: initialTitle || node.title || 'StudyBoard', notebookTitle: notebook.title || '' })
    );

    // thumbnailBase64 = raw base64 for YouTube API (strip data URL prefix)
    const thumbnailBase64 = thumbDataUrl.replace(/^data:[^;]+;base64,/, '');
    const thumbnailMime = 'image/png';
    const thumbnailSrc = thumbDataUrl;

    // Note images for the thumbnail editor
    const noteImages = (() => {
        const imgs: { src: string; label: string }[] = [];
        const raw = node.imageBase64;
        if (raw) {
            const src = raw.startsWith('data:') || raw.startsWith('http')
                ? raw : `data:${node.imageMimeType || 'image/jpeg'};base64,${raw}`;
            imgs.push({ src, label: 'Main' });
        }
        (node.attachments ?? []).filter(a => a.type === 'image').forEach((a, i) => {
            const src = a.content.startsWith('data:') || a.content.startsWith('http')
                ? a.content : `data:${a.mimeType || 'image/jpeg'};base64,${a.content}`;
            imgs.push({ src, label: `Image ${i + 2}` });
        });
        return imgs;
    })();

    useEffect(() => {
        if (isYouTubeConnected()) getYouTubeChannel().then(ch => ch && setYtChannel(ch));
        const unsub = subscribeYouTube((connected) => {
            setYtConnected(connected);
            if (connected) getYouTubeChannel().then(ch => ch && setYtChannel(ch));
        });
        return () => {
            unsub();
            timerRef.current && clearInterval(timerRef.current);
            streamRef.current?.getTracks().forEach(t => t.stop());
            micStreamRef.current?.getTracks().forEach(t => t.stop());
            audioContextRef.current?.close();
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        };
    }, []);

    const [doneCountdown, setDoneCountdown] = useState(4);
    // Set when the server uploaded the video itself: nothing else will close the modal then
    // (the manual path closes through onDone), so the countdown has to.
    const serverUploadedRef = useRef(false);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (stage !== 'done') return;
        const closeAt = Date.now() + 4000; // wall clock, so a sleeping tab still ends at 0
        setDoneCountdown(4);
        const interval = setInterval(() => {
            const left = Math.max(0, Math.ceil((closeAt - Date.now()) / 1000));
            setDoneCountdown(left);
            if (left === 0) {
                clearInterval(interval);
                if (serverUploadedRef.current) onCloseRef.current();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [stage]);

    // Generate preview URL when file changes
    useEffect(() => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = file ? URL.createObjectURL(file) : null;
    }, [file]);

    const handlePickedFile = (f: File) => {
        if (!f.type.startsWith('video/')) return;
        setFile(f);
        setClips([]);
        setStage('review');
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) handlePickedFile(f);
    };

    // Shape segments the way Firestore's videoSegments field expects — shared by the slot-0
    // incremental save below and the upload-finalize append for a 2nd+ video (which has nowhere
    // incremental to write to until the new video's own entry exists — see there for why).
    const segmentsToSave = (currentSegments: SegmentStatus[]) => currentSegments
        .filter(s => s.status !== 'recording')
        .map(s => ({
            index: s.index, startSecs: s.startSecs, endSecs: s.endSecs,
            status: s.status, score: s.score, clarityScore: s.clarityScore,
            riskLevel: s.riskLevel, transcript: s.transcript || '',
            summary: s.summary, mainIssue: s.mainIssue,
            suggestedCorrection: s.suggestedCorrection, overridden: s.overridden,
        }));

    // Persist current segments to Firestore immediately — best-effort, called after each segment completes.
    // Only safe to stage into the note's top-level videoSegments field when this recording will become
    // the note's first video (slot 0) — if the note already has a video, staging here would corrupt slot
    // 0's Post-Check data once this recording finishes and gets appended as a 2nd+ video. A 2nd+ video's
    // segments have nowhere safe to land incrementally (its own youtubeVideos[] entry doesn't exist yet
    // mid-recording), so they're carried in-memory via segmentsRef and only written once, at upload time,
    // straight into that new entry — see the buildVideoAppendPatch call site.
    const saveSegmentsNow = useCallback(async (currentSegments: SegmentStatus[]) => {
        if (node.youtubeVideoId) return;
        try {
            const userId = auth.currentUser?.uid || uid;
            if (!userId || !notebook?.id || !node?.id) return;
            const nodeRef = doc(db, `users/${userId}/notebooks/${notebook.id}/nodes/${node.id}`);
            const toSave = segmentsToSave(currentSegments);
            if (toSave.length === 0) return;
            await updateDoc(nodeRef, { videoSegments: toSave });
        } catch { /* best-effort, don't interrupt recording */ }
    }, [auth, db, uid, notebook?.id, node?.id]);

    const IDB_DB_NAME = 'sb_recording_recovery';
    const IDB_STORE = 'pending';
    // Rolling buffer for Auto-Pace incremental persistence (finding 4) — separate store, separate
    // concern from IDB_STORE's single final-blob-per-node recovery slot. Parts are transient: written
    // during an in-progress auto-pace recording, read back + cleared once at doFullStop.
    const IDB_PARTS_STORE = 'autopace_parts';
    const IDB_VERSION = 2;
    const idbKey = `${notebook?.id}_${node?.id}${slot ? `__s${slot}` : ''}`;
    const partsFlushedRef = useRef(0); // count of chunk-parts already written to IDB_PARTS_STORE this session
    const ensureIdbStores = (db: IDBDatabase) => {
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
        if (!db.objectStoreNames.contains(IDB_PARTS_STORE)) db.createObjectStore(IDB_PARTS_STORE);
    };

    const idbSavePart = useCallback(async (partIndex: number, blob: Blob) => {
        try {
            await new Promise<void>((resolve, reject) => {
                const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
                req.onupgradeneeded = () => ensureIdbStores(req.result);
                req.onsuccess = () => {
                    const tx = req.result.transaction(IDB_PARTS_STORE, 'readwrite');
                    tx.objectStore(IDB_PARTS_STORE).put(blob, `${idbKey}__${partIndex}`);
                    tx.oncomplete = () => resolve();
                    tx.onerror = () => reject(tx.error);
                };
                req.onerror = () => reject(req.error);
            });
        } catch { /* best-effort — worst case this part's chunks stay in RAM (not cleared) instead of being lost */ }
    }, [idbKey]);

    // Same parts as idbLoadAndClearParts, left in place: the recording is still going.
    const idbPeekParts = useCallback(async (count: number): Promise<Blob[]> => {
        if (count <= 0) return [];
        return await new Promise<Blob[]>((resolve, reject) => {
            const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
            req.onupgradeneeded = () => ensureIdbStores(req.result);
            req.onsuccess = () => {
                const tx = req.result.transaction(IDB_PARTS_STORE, 'readonly');
                const store = tx.objectStore(IDB_PARTS_STORE);
                const parts: (Blob | undefined)[] = new Array(count);
                for (let i = 0; i < count; i++) {
                    const getReq = store.get(`${idbKey}__${i}`);
                    getReq.onsuccess = () => { parts[i] = getReq.result; };
                }
                tx.oncomplete = () => {
                    // A missing part would shift everything after it earlier in the playback, and a
                    // point picked there would cut the wrong moment. Better to refuse.
                    if (parts.some(b => !b)) reject(new Error('missing recorded part'));
                    else resolve(parts as Blob[]);
                };
                tx.onerror = () => reject(tx.error);
            };
            req.onerror = () => reject(req.error);
        });
    }, [idbKey]);

    const idbLoadAndClearParts = useCallback(async (count: number): Promise<Blob[]> => {
        if (count <= 0) return [];
        try {
            return await new Promise<Blob[]>((resolve, reject) => {
                const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
                req.onupgradeneeded = () => ensureIdbStores(req.result);
                req.onsuccess = () => {
                    const tx = req.result.transaction(IDB_PARTS_STORE, 'readwrite');
                    const store = tx.objectStore(IDB_PARTS_STORE);
                    const parts: Blob[] = new Array(count);
                    let remaining = count;
                    for (let i = 0; i < count; i++) {
                        const getReq = store.get(`${idbKey}__${i}`);
                        getReq.onsuccess = () => { parts[i] = getReq.result; store.delete(`${idbKey}__${i}`); };
                    }
                    tx.oncomplete = () => resolve(parts.filter(Boolean));
                    tx.onerror = () => reject(tx.error);
                };
                req.onerror = () => reject(req.error);
            });
        } catch { return []; } // best-effort — any part that failed to load is simply missing from the final video
    }, [idbKey]);

    const idbSaveRecording = useCallback(async (blob: Blob, filename: string) => {
        try {
            await new Promise<void>((resolve, reject) => {
                const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
                req.onupgradeneeded = () => ensureIdbStores(req.result);
                req.onsuccess = () => {
                    const tx = req.result.transaction(IDB_STORE, 'readwrite');
                    tx.objectStore(IDB_STORE).put({ blob, filename, savedAt: Date.now() }, idbKey);
                    tx.oncomplete = () => resolve();
                    tx.onerror = () => reject(tx.error);
                };
                req.onerror = () => reject(req.error);
            });
        } catch { /* best-effort */ }
    }, [idbKey]);

    const idbDeleteRecording = useCallback(async () => {
        try {
            await new Promise<void>((resolve) => {
                const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
                req.onupgradeneeded = () => ensureIdbStores(req.result);
                req.onsuccess = () => {
                    const tx = req.result.transaction(IDB_STORE, 'readwrite');
                    tx.objectStore(IDB_STORE).delete(idbKey);
                    tx.oncomplete = () => resolve();
                };
                req.onerror = () => resolve();
            });
        } catch { /* best-effort */ }
    }, [idbKey]);

    // On mount: check for a pending recovery from a previous crashed session
    useEffect(() => {
        if (!node?.id) return;
        const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => ensureIdbStores(req.result);
        req.onsuccess = () => {
            const tx = req.result.transaction(IDB_STORE, 'readonly');
            const getReq = tx.objectStore(IDB_STORE).get(idbKey);
            getReq.onsuccess = () => {
                if (getReq.result?.blob) {
                    const { blob, filename } = getReq.result;
                    const f = new File([blob], filename || 'recovered-recording.webm', { type: blob.type || 'video/webm' });
                    setRecoveredFile(f);
                }
            };
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node?.id]);

    // The server-side export job for this note, if one is running or finished-but-not-uploaded.
    // The IndexedDB backup above is the RAW recording (no Auto-Pace speed-up/cuts) — when the
    // processed version exists or is on its way, that's what the recovery banner must offer first,
    // otherwise "Restore" silently puts the unprocessed 1-hour take up for upload.
    const [serverJob, setServerJob] = useState<{ id: string; status: string; progress?: number; phase?: string; storagePathFinal?: string } | null>(null);
    const [openingServerJob, setOpeningServerJob] = useState(false);
    useEffect(() => {
        const userId = auth.currentUser?.uid || uid;
        if (!userId || !node?.id || !notebook?.id) return;
        const q = query(collection(db, `users/${userId}/videoExportJobs`), where('noteKey', '==', `${notebook.id}:${node.id}`));
        return onSnapshot(q, snap => {
            const jobs = snap.docs
                .map(d => ({ id: d.id, ...(d.data() as any) }))
                .filter(j => !liveExportJobIds.has(j.id))
                .filter(j => j.status === 'pending' || j.status === 'processing' || (j.status === 'done' && !j.consumed && j.storagePathFinal))
                .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
            setServerJob(jobs[0] ? { id: jobs[0].id, status: jobs[0].status, progress: jobs[0].progress, phase: jobs[0].phase, storagePathFinal: jobs[0].storagePathFinal } : null);
        }, () => setServerJob(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node?.id, notebook?.id]);

    const openProcessedFromServer = async () => {
        if (!serverJob?.storagePathFinal) return;
        setOpeningServerJob(true);
        try {
            const url = await getDownloadURL(storageRef(storage, serverJob.storagePathFinal));
            const blob = await (await fetch(url)).blob();
            const finalFile = new File([blob], `studyboard-${Date.now()}.mp4`, { type: 'video/mp4' });
            setFile(finalFile);
            finalStoragePathRef.current = serverJob.storagePathFinal;
            activeExportJobIdRef.current = serverJob.id;
            setRecoveredFile(null);
            setStage('review');
        } catch (e: any) {
            setExportError(e?.message || 'Failed to fetch the processed video');
        } finally {
            setOpeningServerJob(false);
        }
    };

    const analyzeSegmentAudio = useCallback(async (segIdx: number, audioBlob: Blob, segDurationSecs = 0) => {
        // Size guard: skip if audio blob > 7MB (base64 would exceed ~10MB payload limit)
        const MAX_AUDIO_BYTES = 7 * 1024 * 1024;
        if (audioBlob.size > MAX_AUDIO_BYTES) {
            setSegments(prev => prev.map(s => s.index === segIdx ? { ...s, status: 'skipped', mainIssue: `Audio too large to analyze (${(audioBlob.size / 1024 / 1024).toFixed(1)} MB)` } : s));
            return;
        }
        setSegments(prev => prev.map(s => s.index === segIdx ? { ...s, status: 'checking' } : s));
        try {
            const reader = new FileReader();
            const base64 = await new Promise<string>((resolve, reject) => {
                reader.onload = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(audioBlob);
            });
            const fn = httpsCallable(functions, 'analyzeVideoSegment');
            const res = await fn({
                audioBase64: base64,
                audioMimeType: audioBlob.type || 'audio/webm',
                noteTitle: node.title || '',
                noteContent: node.content || '',
                noteAiFeedback: node.correctnessFeedback || '',
                segmentIndex: segIdx,
                segmentDurationSecs: segDurationSecs,
                priorTranscript: [seriesPriorRef.current, ...segmentsRef.current
                    .filter(x => x.index < segIdx && x.transcript)
                    .sort((a, b) => a.index - b.index)
                    .map(x => x.transcript)].filter(Boolean).join('\n'),
                language: language === 'vietnamese' ? 'vietnamese' : 'english',
            });
            const d = res.data as any;
            setSegments(prev => {
                const updated = prev.map(s => s.index === segIdx ? {
                    ...s,
                    status: d.status || 'warning',
                    score: d.correctnessScore,
                    clarityScore: d.clarityScore,
                    confidence: d.confidence,
                    riskLevel: d.riskLevel,
                    transcript: d.transcript || '',
                    summary: d.summary,
                    whatWasRight: d.whatWasRight || '',
                    mainIssue: d.mainIssue,
                    suggestedCorrection: d.suggestedCorrection,
                } : s);
                saveSegmentsNow(updated);
                return updated;
            });
            cueProfessor(d);
        } catch {
            setSegments(prev => {
                const updated = prev.map(s => s.index === segIdx ? { ...s, status: 'warning', mainIssue: 'Analysis failed' } : s);
                saveSegmentsNow(updated);
                return updated;
            });
        }
    }, [node.title, node.content, node.correctnessFeedback, language, saveSegmentsNow, cueProfessor]);

    // Feeds a screen track's frames into the recording's video track (see screenPipeRef).
    const pipeScreenTrack = (track: MediaStreamTrack) => {
        const pipe = screenPipeRef.current;
        if (!pipe) return;
        pipe.reader?.cancel().catch(() => {});
        const reader = new (window as any).MediaStreamTrackProcessor({ track }).readable.getReader();
        pipe.reader = reader;
        (async () => {
            for (;;) {
                const { value, done } = await reader.read().catch(() => ({ value: null, done: true }));
                if (done || !value) break;
                if (screenPipeRef.current !== pipe || pipe.reader !== reader) { value.close(); break; }
                try { await pipe.writer.write(value); } catch { try { value.close(); } catch { /* already closed */ } break; }
            }
        })();
    };

    const startRecording = useCallback(async () => {
        setMicUnavailable(false);
        setClassmateOn(withClassmate);
        setClassmateJoinMidway(false);
        setClassmateSession(n => n + 1);
        mixDestRef.current = null;
        setRedoConfirm(null);
        try {
            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
                audio: true,
            } as any);

            // Try webcam if enabled
            let webcamStream: MediaStream | null = null;
            if (useWebcam) {
                try {
                    const camConstraints = selectedCameraId
                        ? { video: { deviceId: { exact: selectedCameraId } }, audio: false }
                        : { video: true, audio: false };
                    webcamStream = await navigator.mediaDevices.getUserMedia(camConstraints);
                    webcamStreamRef.current = webcamStream;
                } catch { /* webcam optional */ }
            }

            // Mix audio: screen audio + mic
            let audioTracks: MediaStreamTrack[] = [];
            try {
                const micConstraints = selectedMicId
                    ? { audio: { deviceId: { exact: selectedMicId } } }
                    : { audio: true };
                const mic = await navigator.mediaDevices.getUserMedia(micConstraints);
                micStreamRef.current = mic;
                const ctx = new AudioContext();
                audioContextRef.current = ctx;
                const dest = ctx.createMediaStreamDestination();
                mixDestRef.current = dest;
                if (screenStream.getAudioTracks().length > 0) {
                    ctx.createMediaStreamSource(new MediaStream(screenStream.getAudioTracks())).connect(dest);
                }
                ctx.createMediaStreamSource(mic).connect(dest);
                audioTracks = dest.stream.getAudioTracks();

                // Auto-Pace voice detection: a separate tap on the mic stream, not connected to any
                // destination (so it never doubles up audio in the actual recording).
                if (autoPaceEnabled) {
                    const analyser = ctx.createAnalyser();
                    analyser.fftSize = 512;
                    ctx.createMediaStreamSource(new MediaStream(mic.getAudioTracks())).connect(analyser);
                    autoPaceAnalyserRef.current = analyser;
                    autoPaceAnalyserBufRef.current = new Uint8Array(analyser.fftSize);
                }
            } catch { audioTracks = screenStream.getAudioTracks(); setMicUnavailable(true); }

            let recordStream: MediaStream;

            if (webcamStream) {
                // Canvas compositing: screen full + webcam PiP bottom-right
                const canvas = document.createElement('canvas');
                canvasRef.current = canvas;

                const screenVid = document.createElement('video');
                screenVid.srcObject = screenStream;
                screenVid.muted = true;
                await screenVid.play();
                screenVideoRef.current = screenVid;

                // Match canvas to actual stream dimensions to avoid aspect-ratio distortion
                const srcW = screenVid.videoWidth || 1920;
                const srcH = screenVid.videoHeight || 1080;
                canvas.width = srcW;
                canvas.height = srcH;

                const camVid = document.createElement('video');
                camVid.srcObject = webcamStream;
                camVid.muted = true;
                await camVid.play();
                webcamVideoRef.current = camVid;

                const ctx2d = canvas.getContext('2d')!;
                // PiP size scaled proportionally to canvas height
                const PIP_H = Math.round(srcH * 0.187);
                const PIP_W = Math.round(PIP_H * (16 / 9));
                const PIP_MARGIN = Math.round(srcH * 0.022);

                const drawFrame = () => {
                    ctx2d.drawImage(screenVid, 0, 0, canvas.width, canvas.height);
                    ctx2d.save();
                    ctx2d.beginPath();
                    if (webcamShapeRef.current === 'circle') {
                        // Square bounding box (PIP_H × PIP_H) so circle sits flush at bottom-right corner
                        const d = PIP_H;
                        const px_c = canvas.width - d - PIP_MARGIN;
                        const py_c = canvas.height - d - PIP_MARGIN;
                        const cx = px_c + d / 2;
                        const cy = py_c + d / 2;
                        const r = d / 2;
                        ctx2d.arc(cx, cy, r, 0, Math.PI * 2);
                        ctx2d.clip();
                        // Crop webcam source to center square (avoids face distortion)
                        const vw = camVid.videoWidth || d;
                        const vh = camVid.videoHeight || d;
                        const cropSize = Math.min(vw, vh);
                        const cropX = (vw - cropSize) / 2;
                        const cropY = (vh - cropSize) / 2;
                        ctx2d.drawImage(camVid, cropX, cropY, cropSize, cropSize, px_c, py_c, d, d);
                        ctx2d.restore();
                        ctx2d.strokeStyle = 'rgba(255,255,255,0.75)';
                        ctx2d.lineWidth = 2.5;
                        ctx2d.beginPath();
                        ctx2d.arc(cx, cy, r, 0, Math.PI * 2);
                        ctx2d.stroke();
                    } else {
                        const px = canvas.width - PIP_W - PIP_MARGIN;
                        const py = canvas.height - PIP_H - PIP_MARGIN;
                        ctx2d.roundRect(px, py, PIP_W, PIP_H, 10);
                        ctx2d.clip();
                        ctx2d.drawImage(camVid, px, py, PIP_W, PIP_H);
                        ctx2d.restore();
                        ctx2d.strokeStyle = 'rgba(255,255,255,0.6)';
                        ctx2d.lineWidth = 2;
                        ctx2d.beginPath();
                        ctx2d.roundRect(px, py, PIP_W, PIP_H, 10);
                        ctx2d.stroke();
                    }
                    rafRef.current = requestAnimationFrame(drawFrame);
                };
                rafRef.current = requestAnimationFrame(drawFrame);

                const canvasStream = canvas.captureStream(30);
                recordStream = new MediaStream([...canvasStream.getVideoTracks(), ...audioTracks]);
            } else if ((window as any).MediaStreamTrackProcessor && (window as any).MediaStreamTrackGenerator) {
                const gen = new (window as any).MediaStreamTrackGenerator({ kind: 'video' });
                screenPipeRef.current = { writer: gen.writable.getWriter(), reader: null };
                pipeScreenTrack(screenStream.getVideoTracks()[0]);
                recordStream = new MediaStream([gen, ...audioTracks]);
            } else {
                screenPipeRef.current = null;
                recordStream = new MediaStream([...screenStream.getVideoTracks(), ...audioTracks]);
            }

            streamRef.current = recordStream;
            screenStreamRef.current = screenStream;
            chunksRef.current = [];
            chunkBytesRef.current = 0;
            sizeWarn800Fired.current = false;
            partsFlushedRef.current = 0;
            setRecordingMB(0);
            // "Insert clip" timeline — independent of Auto-Pace/Checkpoint, reset every new recording.
            timelineRef.current = [];
            currentLiveStartRef.current = 0;
            setTimelineSegments([]);

            const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
                .find(m => MediaRecorder.isTypeSupported(m)) || '';
            mimeTypeRef.current = mimeType;

            // Checkpoint cycle: stop only audio, keep video running continuously.
            // This ensures the final video is ONE valid WebM stream, not fragmented blobs.
            const handleCheckpointCycle = () => {
                if (isCheckpointCycleRef.current) return;
                isCheckpointCycleRef.current = true;

                const segIdx = currentSegmentIndexRef.current;
                const segEnd = recordSecsRef.current;
                const segStart = segmentStartSecsRef.current;
                const dur = segEnd - segStart;

                // Record chunk boundary for potential redo splicing
                segChunkCountsRef.current.push(chunksRef.current.length);

                // Handle audio: stop → analyze → restart
                const restartAudio = () => {
                    if (micStreamRef.current && micStreamRef.current.getAudioTracks().length > 0) {
                        const audioMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
                        const newAudioMr = new MediaRecorder(new MediaStream(micStreamRef.current.getAudioTracks()), { mimeType: audioMime });
                        audioMrRef.current = newAudioMr;
                        newAudioMr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
                        newAudioMr.start(500);
                    }
                    isCheckpointCycleRef.current = false;
                };

                if (audioMrRef.current && audioMrRef.current.state !== 'inactive') {
                    audioMrRef.current.onstop = () => {
                        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                        audioChunksRef.current = [];
                        setSegments(prev => prev.map(s => s.index === segIdx ? { ...s, endSecs: segEnd, status: 'uploading' } : s));
                        analyzeSegmentAudio(segIdx, audioBlob, dur);
                        restartAudio();
                    };
                    audioMrRef.current.stop();
                } else {
                    setSegments(prev => prev.map(s => s.index === segIdx ? { ...s, endSecs: segEnd, status: 'skipped' } : s));
                    restartAudio();
                }

                // Advance segment index
                const newIdx = segIdx + 1;
                currentSegmentIndexRef.current = newIdx;
                segmentStartSecsRef.current = segEnd;
                setSegments(prev => [...prev, { index: newIdx, startSecs: segEnd, status: 'recording' }]);
            };

            const doFullStop = () => {
                timerRef.current && clearInterval(timerRef.current);
                checkpointTimerRef.current && clearInterval(checkpointTimerRef.current);
                checkpointTimerRef.current = null;
                if (autoPaceEnabled) {
                    // Close whatever chunk was open at the moment of Stop — using pauseOwnerRef rather
                    // than mediaRecorderRef.current.state (already 'inactive' here, post-stop) to decide
                    // whether the final stretch was actively recording or sitting paused.
                    closeAutoPaceChunk(getPreciseActiveMs(), true);
                    // A part still being marked when recording stops ends here; it can be dropped on the review screen.
                    {
                        const endMs = Math.round(getPreciseActiveMs());
                        setClipMarks(clipMarksRef.current
                            .map(m => (m.endMs === null ? { ...m, endMs } : m))
                            .filter(m => (m.endMs as number) - m.startMs >= MIN_CLIP_MARK_MS));
                    }
                    // closeAutoPaceChunk already stops/nulls autoPaceCheckMrRef on a natural close; this
                    // just clears the rotation timeout defensively in case Stop landed mid-window.
                    if (autoPaceCheckWindowTimerRef.current) { clearTimeout(autoPaceCheckWindowTimerRef.current); autoPaceCheckWindowTimerRef.current = null; }
                    autoPaceTickRef.current?.stop();
                    autoPaceTickRef.current = null;
                    autoPaceActivityCleanupRef.current?.();
                    autoPaceActivityCleanupRef.current = null;
                    autoPaceAnalyserRef.current?.disconnect();
                    autoPaceAnalyserRef.current = null;
                }
                if (timelineRef.current.length > 0) {
                    // Close the final live segment the same way Auto-Pace closes its last chunk —
                    // only if it actually used "Insert clip" at least once this session.
                    const now = getPreciseActiveMs();
                    if (now > currentLiveStartRef.current) {
                        timelineRef.current = [...timelineRef.current, { type: 'live', sourceStartMs: currentLiveStartRef.current, sourceEndMs: now }];
                    }
                    setTimelineSegments(timelineRef.current);
                }
                if (rafRef.current) cancelAnimationFrame(rafRef.current);
                audioMrRef.current?.state !== 'inactive' && audioMrRef.current?.stop();
                audioMrRef.current = null;
                screenStream.getTracks().forEach(t => t.stop());
                screenStreamRef.current?.getTracks().forEach(t => t.stop()); // a re-shared screen
                if (screenPipeRef.current) { screenPipeRef.current.reader?.cancel().catch(() => {}); screenPipeRef.current = null; }
                setScreenLost(false);
                webcamStreamRef.current?.getTracks().forEach(t => t.stop());
                webcamStreamRef.current = null;
                micStreamRef.current?.getTracks().forEach(t => t.stop());
                micStreamRef.current = null;
                audioContextRef.current?.close();
                audioContextRef.current = null;
                recordStream.getTracks().forEach(t => t.stop());

                const type = mimeType || 'video/webm';
                const segIdx = currentSegmentIndexRef.current;
                const segEnd = recordSecsRef.current;
                if (checkpointMode !== 'off') {
                    setSegments(prev => prev.map(s => s.index === segIdx ? { ...s, endSecs: segEnd, status: 'uploading' } : s));
                }

                const ext = type.includes('mp4') ? 'mp4' : 'webm';
                const totalDur = segEnd;
                const recFilename = `recording-${new Date().toISOString().slice(0, 16)}.${ext}`;

                const finish = (finalChunks: Blob[]) => {
                    const blob = new Blob(finalChunks, { type });
                    // Save to IndexedDB immediately — crash/upload-failure recovery
                    idbSaveRecording(blob, recFilename);
                    setClips([{ id: Date.now(), blob, durationSecs: totalDur, name: `Recording (${formatTime(totalDur)})` }]);
                    setRecordState('idle');
                    setSegments(prev => prev);
                };

                if (autoPaceEnabled) {
                    // Reassemble from IndexedDB-persisted parts (finding 4) + whatever tail is still in RAM.
                    // No redo-splice interaction here: checkpointMode (the only feature that slices chunksRef
                    // by index) is mutually exclusive with autoPaceEnabled, so the tail is always contiguous.
                    const tail = chunksRef.current;
                    idbLoadAndClearParts(partsFlushedRef.current).then(parts => finish([...parts, ...tail]));
                } else {
                    // Build final video blob — apply redo splices if any
                    let finalChunks: Blob[];
                    if (redoSplicesRef.current.length > 0) {
                        finalChunks = chunksRef.current.slice(0, redoSplicesRef.current[0].keepEnd);
                        for (let i = 0; i < redoSplicesRef.current.length; i++) {
                            const { redoStart } = redoSplicesRef.current[i];
                            const nextEnd = i + 1 < redoSplicesRef.current.length
                                ? redoSplicesRef.current[i + 1].keepEnd
                                : chunksRef.current.length;
                            finalChunks = [...finalChunks, ...chunksRef.current.slice(redoStart, nextEnd)];
                        }
                    } else {
                        finalChunks = chunksRef.current;
                    }
                    finish(finalChunks);
                }
            };

            handleCheckpointRef.current = handleCheckpointCycle;

            // Start one continuous video recorder — never restarted during checkpoints
            const mr = new MediaRecorder(recordStream, mimeType ? { mimeType } : undefined);
            mediaRecorderRef.current = mr;
            mr.ondataavailable = e => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                    chunkBytesRef.current += e.data.size;
                    const mb = Math.round(chunkBytesRef.current / (1024 * 1024));
                    setRecordingMB(mb);
                    // Auto-Pace: periodically flush the in-RAM chunk buffer to IndexedDB so a long
                    // (30-60+ min) actively-recording session doesn't hold the entire raw video in
                    // memory (finding 4). Not done for checkpointMode sessions — that feature's redo
                    // splicing needs chunksRef fully in-RAM and is mutually exclusive with autoPaceEnabled.
                    if (autoPaceEnabled && chunksRef.current.length >= 40) {
                        const partBlob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
                        const partIdx = partsFlushedRef.current;
                        partsFlushedRef.current += 1;
                        chunksRef.current = [];
                        const saving = idbSavePart(partIdx, partBlob);
                        pendingPartSavesRef.current.add(saving);
                        saving.finally(() => pendingPartSavesRef.current.delete(saving));
                    }
                    // Warning at 800MB
                    if (!sizeWarn800Fired.current && chunkBytesRef.current >= 800 * 1024 * 1024) {
                        sizeWarn800Fired.current = true;
                        setSizeBanner(language === 'vietnamese'
                            ? '⚠️ Video đã đạt 800MB — nên kết thúc sớm để tránh mất dữ liệu!'
                            : '⚠️ Recording reached 800MB — wrap up soon to avoid data loss!');
                    }
                    // Auto-stop at 2GB
                    if (chunkBytesRef.current >= sizeStop2GB) {
                        setSizeBanner(language === 'vietnamese'
                            ? '🛑 Tự động dừng: video đạt 2GB — đang lưu lại...'
                            : '🛑 Auto-stopped at 2GB RAM limit — saving now...');
                        if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
                    }
                }
            };
            mr.onstop = () => doFullStop();
            mr.start(500);

            clipStartSecsRef.current = recordSecs;
            currentSegmentIndexRef.current = 0;
            segmentStartSecsRef.current = 0;

            screenStream.getVideoTracks()[0].addEventListener('ended', () => onScreenLostRef.current());

            setRecordState('recording');
            activeMsRef.current = 0;
            lastResumeAtRef.current = performance.now();
            timerRef.current && clearInterval(timerRef.current);
            timerRef.current = setInterval(() => setRecordSecs(s => {
                const n = s + 1; recordSecsRef.current = n; return n;
            }), 1000);

            // Checkpoint mode setup
            if (checkpointMode !== 'off') {
                setSegments([{ index: 0, startSecs: 0, status: 'recording' }]);
                setShowSegmentPanel(true);

                // Audio-only recorder on mic for AI analysis
                if (micStreamRef.current && micStreamRef.current.getAudioTracks().length > 0) {
                    const audioMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
                    const audioMr = new MediaRecorder(new MediaStream(micStreamRef.current.getAudioTracks()), { mimeType: audioMime });
                    audioMrRef.current = audioMr;
                    audioChunksRef.current = [];
                    audioMr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
                    audioMr.start(500);
                } else {
                    setMicUnavailable(true);
                }

                // Auto-checkpoint interval (auto and hybrid only)
                if (checkpointMode === 'auto' || checkpointMode === 'hybrid') {
                    const intervalMs = checkpointMins * 60 * 1000;
                    checkpointTimerRef.current = setInterval(() => {
                        handleCheckpointRef.current?.();
                    }, intervalMs);
                }
            }

            // Auto-Pace state machine setup (step 6). Mutually exclusive with checkpointMode above.
            if (autoPaceEnabled) {
                const startNow = performance.now();
                autoPaceModeRef.current = 'speaking';
                setAutoPaceLiveMode('speaking');
                setAutoPaceLiveRms(0);
                isSpeakingRawRef.current = false;
                lastSpeechAtRef.current = startNow;       // assume speaking at t=0 — avoids a false
                lastInteractionAtRef.current = startNow;  // "idle" flash before any real samples exist
                lastAutoResumeAtRef.current = 0;
                pauseOwnerRef.current = null;
                micLostWarnedRef.current = false;
                speedChunksRef.current = [];
                setSpeedChunks([]);
                setClipMarks([]);
                setRewatchCuts([]);
                rewatchUndoRef.current = null;
                openAutoPaceChunk(0, 'speaking');

                const onActivity = (e: Event) => {
                    if (e.type === 'pointermove' && (e as PointerEvent).buttons === 0) return; // only while a button is held (drag)
                    lastInteractionAtRef.current = performance.now();
                };
                window.addEventListener('keydown', onActivity, true);
                window.addEventListener('pointerdown', onActivity, true);
                window.addEventListener('pointermove', onActivity, true);
                // Work in another app (Colab, VS Code…) sends no keys here, so while StudyBoard is
                // not in front a changing screen counts as activity instead. Checked about 4×/s.
                const screenTrack = screenStreamRef.current?.getVideoTracks()[0];
                screenProbeRef.current = screenTrack ? createScreenActivityProbe(screenTrack) : null;
                let screenSampledAt = 0;
                let screenWatching = false;
                const watchScreen = () => {
                    const screenProbe = screenProbeRef.current;
                    if (!screenProbe) return;
                    const away = document.visibilityState === 'hidden' || !document.hasFocus();
                    if (!away) { if (screenWatching) { screenWatching = false; screenProbe.reset(); } return; }
                    screenWatching = true;
                    const t = performance.now();
                    if (t - screenSampledAt < 250) return;
                    screenSampledAt = t;
                    screenProbe.sample().then(changed => { if (changed) lastInteractionAtRef.current = performance.now(); });
                };
                autoPaceActivityCleanupRef.current = () => {
                    window.removeEventListener('keydown', onActivity, true);
                    window.removeEventListener('pointerdown', onActivity, true);
                    window.removeEventListener('pointermove', onActivity, true);
                    screenProbeRef.current?.stop();
                    screenProbeRef.current = null;
                };

                const tick = () => {
                    watchScreen();
                    const analyser = autoPaceAnalyserRef.current;
                    const buf = autoPaceAnalyserBufRef.current;
                    if (analyser && buf) {
                        analyser.getByteTimeDomainData(buf as any);
                        let sumSq = 0;
                        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sumSq += v * v; }
                        const rms = Math.sqrt(sumSq / buf.length);
                        setAutoPaceLiveRms(rms);
                        if (!isSpeakingRawRef.current && rms > AUTOPACE_SPEECH_START_RMS) isSpeakingRawRef.current = true;
                        else if (isSpeakingRawRef.current && rms < AUTOPACE_SPEECH_END_RMS) isSpeakingRawRef.current = false;
                        if (isSpeakingRawRef.current) lastSpeechAtRef.current = performance.now();
                    }

                    // Manual pause always wins — the whole state machine stays frozen until a human resumes.
                    if (pauseOwnerRef.current === 'manual') return;

                    // Keep the mode in step with the recorder itself. A re-watch revert or its undo
                    // resets the mode while an idle pause is still holding the recorder, and a resume
                    // by hand leaves the mode at 'idle' while the recorder runs. Either way the old
                    // check (mode changed?) never fired again, so speaking did not resume the recording.
                    const mrState = mediaRecorderRef.current?.state;
                    if (mrState === 'paused' && pauseOwnerRef.current === 'auto-idle' && autoPaceModeRef.current !== 'idle') {
                        autoPaceModeRef.current = 'idle';
                    } else if (mrState === 'recording' && autoPaceModeRef.current === 'idle') {
                        autoPaceModeRef.current = 'active-silent';
                        setAutoPaceLiveMode('active-silent');
                        openAutoPaceChunk(getPreciseActiveMs(), 'silent');
                    }
                    // A suspended audio context feeds the meter silence, so speech could never be heard.
                    const actx = audioContextRef.current;
                    if (actx && actx.state === 'suspended') actx.resume().catch(() => {});
                    // Mic gone (headset unplugged, Bluetooth dropped): speech can no longer resume an idle pause.
                    const micTrack = micStreamRef.current?.getAudioTracks()[0];
                    if (micTrack && micTrack.readyState === 'ended' && !micLostWarnedRef.current) {
                        micLostWarnedRef.current = true;
                        setSizeBanner(vnRef.current
                            ? 'Mất tín hiệu micro (tai nghe ngắt kết nối?). Máy không nghe được anh nói nên sẽ không tự quay tiếp — bấm ▶ để tiếp tục, và nên dừng rồi quay lại để có tiếng.'
                            : 'The microphone was lost (headset disconnected?). Speech cannot resume the recording — press ▶, and restart the recording to get sound back.');
                    }

                    const now = performance.now();
                    const speaking = (now - lastSpeechAtRef.current) < AUTOPACE_SPEECH_HANGOVER_MS;
                    const recentlyActive = (now - lastInteractionAtRef.current) < AUTOPACE_ACTIVE_WINDOW_MS;
                    const idleFor = now - Math.max(lastSpeechAtRef.current, lastInteractionAtRef.current);
                    const cooledDown = (now - lastAutoResumeAtRef.current) > AUTOPACE_AUTO_PAUSE_COOLDOWN_MS;

                    let nextMode: AutoPaceMode;
                    if (speaking) nextMode = 'speaking';
                    else if (recentlyActive) nextMode = 'active-silent';
                    else if (idleFor > idleThresholdSecsRef.current * 1000 && cooledDown) nextMode = 'idle';
                    else nextMode = autoPaceModeRef.current; // keep current mode — no undefined gap between windows

                    if (nextMode === autoPaceModeRef.current) return;
                    const prevMode = autoPaceModeRef.current;
                    autoPaceModeRef.current = nextMode;
                    setAutoPaceLiveMode(nextMode);

                    if (nextMode === 'idle') {
                        // Entering TRUE_IDLE: close the active chunk first, then pause. No idle chunk is ever created.
                        closeAutoPaceChunk(getPreciseActiveMs());
                        pauseRecording('auto-idle');
                        return;
                    }
                    if (prevMode === 'idle') {
                        // Leaving TRUE_IDLE: only the owner of the pause may auto-resume it.
                        if (pauseOwnerRef.current !== 'auto-idle' || mediaRecorderRef.current?.state !== 'paused') { autoPaceModeRef.current = prevMode; return; }
                        resumeRecording();
                        lastAutoResumeAtRef.current = performance.now();
                        openAutoPaceChunk(getPreciseActiveMs(), nextMode === 'speaking' ? 'speaking' : 'silent');
                        return;
                    }
                    // speaking <-> active-silent, no pause involved: close then reopen at the same instant —
                    // except entering "speaking", where the boundary is backdated (see
                    // AUTOPACE_SPEECH_BACKDATE_MS above) to reclaim the bit of real speech that
                    // already happened before detection fired. Clamped to the current chunk's own
                    // start so a silent stretch shorter than the backdate just closes at zero length
                    // (closeAutoPaceChunk already no-ops on that) instead of going negative.
                    const t = getPreciseActiveMs();
                    if (nextMode === 'speaking') {
                        const backdatedT = Math.max(currentChunkStartMsRef.current, t - AUTOPACE_SPEECH_BACKDATE_MS);
                        closeAutoPaceChunk(backdatedT);
                        openAutoPaceChunk(backdatedT, 'speaking');
                    } else {
                        closeAutoPaceChunk(t);
                        openAutoPaceChunk(t, 'silent');
                    }
                };
                autoPaceTickRef.current?.stop();
                autoPaceTickRef.current = steadyInterval(tick, AUTOPACE_POLL_MS);

                // Minimal read-only debug hook for verifying this state machine — only ever installed
                // while the Beta Auto-Pace flag is on. No app behavior reads or depends on this.
                try {
                    (window as any).__autoPaceDebug = {
                        getMode: () => autoPaceModeRef.current,
                        getPauseOwner: () => pauseOwnerRef.current,
                        getChunks: () => speedChunksRef.current,
                        getPreciseActiveMs,
                    };
                } catch {}
            }
        } catch (err: any) {
            if (err?.name !== 'NotAllowedError') {
                setErrorMsg(err?.message || 'Screen capture failed');
                setStage('error');
            }
        }
    }, [recordSecs, useWebcam, selectedCameraId, selectedMicId, autoPaceEnabled, checkpointMode, checkpointMins, withClassmate]);

    // `owner` defaults to 'manual' for anything that isn't the literal 'auto-idle' string — this makes
    // it safe for pauseRecording to be passed directly as a JSX onClick handler (which would otherwise
    // invoke it with a MouseEvent argument). pauseOwnerRef itself is declared earlier, alongside
    // getPreciseActiveMs, which depends on it.
    const pauseRecording = useCallback((owner?: 'auto-idle') => {
        if (mediaRecorderRef.current?.state === 'recording') {
            pauseOwnerRef.current = owner === 'auto-idle' ? 'auto-idle' : 'manual';
            mediaRecorderRef.current.pause();
            audioMrRef.current?.state === 'recording' && audioMrRef.current.pause();
            autoPaceCheckMrRef.current?.state === 'recording' && autoPaceCheckMrRef.current.pause();
            // Stop the max-window clock while paused — it's wall-clock based and would otherwise
            // keep counting down dead pause time toward a rotation that shouldn't happen yet.
            if (autoPaceCheckWindowTimerRef.current) { clearTimeout(autoPaceCheckWindowTimerRef.current); autoPaceCheckWindowTimerRef.current = null; }
            activeMsRef.current += performance.now() - lastResumeAtRef.current;
            timerRef.current && clearInterval(timerRef.current);
            // Pause checkpoint timer so it doesn't fire while paused
            checkpointTimerRef.current && clearInterval(checkpointTimerRef.current);
            checkpointTimerRef.current = null;
            setRecordState('paused');
        }
    }, []);

    const resumeRecording = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'paused') {
            pauseOwnerRef.current = null;
            rewatchUndoRef.current = null; // a discard can only be taken back before recording goes on
            mediaRecorderRef.current.resume();
            audioMrRef.current?.state === 'paused' && audioMrRef.current.resume();
            if (autoPaceCheckMrRef.current?.state === 'paused') {
                autoPaceCheckMrRef.current.resume();
                // Re-arm a fresh full-length window timeout — slightly under/over-counts true speaking
                // time across the pause, an acceptable v1 simplification (activeMsRef already excludes
                // paused time elsewhere the same way).
                if (autoPaceCheckWindowCtlRef.current) armAutoPaceCheckWindowTimer(autoPaceCheckMrRef.current, autoPaceCheckWindowCtlRef.current);
            }
            lastResumeAtRef.current = performance.now();
            // Auto-Pace: treat any resume (manual or auto) as fresh activity. Without this, a manual
            // pause held longer than the idle threshold (e.g. user steps away, then manually resumes)
            // would leave lastSpeechAtRef/lastInteractionAtRef stale — wall-clock time (performance.now())
            // keeps advancing during the pause, so the very next tick would see idleFor already past
            // the idle threshold and immediately auto-pause again, looking like the resume did
            // nothing. Harmless for the auto-triggered resume path too, since those refs were just
            // updated moments earlier anyway (that's why it's resuming).
            lastSpeechAtRef.current = performance.now();
            lastInteractionAtRef.current = performance.now();
            timerRef.current = setInterval(() => setRecordSecs(s => { const n = s + 1; recordSecsRef.current = n; return n; }), 1000);
            // Restart checkpoint timer from full interval — only for auto/hybrid
            if ((checkpointMode === 'auto' || checkpointMode === 'hybrid') && checkpointTimerRef.current === null) {
                const intervalMs = checkpointMins * 60 * 1000;
                checkpointTimerRef.current = setInterval(() => {
                    handleCheckpointRef.current?.();
                }, intervalMs);
            }
            setRecordState('recording');
        }
    }, [checkpointMode, checkpointMins, armAutoPaceCheckWindowTimer]);

    onScreenLostRef.current = () => {
        const mr = mediaRecorderRef.current;
        if (!mr || mr.state === 'inactive') return;
        // A swappable screen source: pause and ask to share again. Otherwise the old behaviour
        // (stop; "Continue this video" is offered afterwards).
        if (screenPipeRef.current || (canvasRef.current && screenVideoRef.current)) {
            // Auto-Pace must not resume by itself onto a dead screen.
            if (mr.state === 'paused') pauseOwnerRef.current = 'manual';
            pauseRecording();
            setScreenLost(true);
        } else {
            mr.stop();
        }
    };

    const reshareScreen = async () => {
        setResharing(true);
        try {
            const s: MediaStream = await navigator.mediaDevices.getDisplayMedia({
                video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
                audio: true,
            } as any);
            const track = s.getVideoTracks()[0];
            if (!track || mediaRecorderRef.current?.state === 'inactive') { s.getTracks().forEach(t => t.stop()); return; }
            screenStreamRef.current?.getTracks().forEach(t => t.stop());
            screenStreamRef.current = s;
            if (screenPipeRef.current) {
                pipeScreenTrack(track);
            } else if (screenVideoRef.current) {
                screenVideoRef.current.srcObject = s;
                await screenVideoRef.current.play().catch(() => {});
            }
            // Computer sound of the new share goes into the same mix as before.
            if (s.getAudioTracks().length > 0 && audioContextRef.current && mixDestRef.current) {
                audioContextRef.current.createMediaStreamSource(new MediaStream(s.getAudioTracks())).connect(mixDestRef.current);
            }
            track.addEventListener('ended', () => onScreenLostRef.current());
            if (screenProbeRef.current) { screenProbeRef.current.stop(); screenProbeRef.current = createScreenActivityProbe(track); }
            setScreenLost(false);
            resumeRecording();
        } catch {
            // Picker cancelled: stay paused, the banner stays up.
        } finally {
            setResharing(false);
        }
    };

    // Videos available to insert — this recording's own notebook only for v1 (already fully loaded
    // via the `notebook` prop, so no extra Firestore fetch needed); only ones with a permanent
    // backup (`videoStoragePath`) can actually be reused. Cross-notebook search can follow later.
    const insertPickerVideos = notebook.nodes.flatMap(n =>
        getVideosForNode(n)
            .filter(v => v.videoStoragePath)
            .map(v => ({ node: n, video: v }))
    ).filter(item =>
        !insertPickerSearch.trim() ||
        (item.video.youtubeTitle || item.video.videoTitle || '').toLowerCase().includes(insertPickerSearch.trim().toLowerCase()) ||
        item.node.title.toLowerCase().includes(insertPickerSearch.trim().toLowerCase())
    );

    const handleOpenInsertPicker = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'recording') pauseRecording();
        setInsertPickerSearch('');
        setShowInsertPicker(true);
    }, [pauseRecording]);

    // Cancel/error semantics: nothing is pushed to timelineRef unless an insertion actually
    // completes — closing the picker without picking anything just resumes, as if "Insert clip"
    // was never clicked. A failed insert attempt must never corrupt or lose the in-progress recording.
    const handleCancelInsert = useCallback(() => {
        setShowInsertPicker(false);
        if (mediaRecorderRef.current?.state === 'paused') resumeRecording();
    }, [resumeRecording]);

    const handleConfirmInsert = useCallback((pickedNode: MindMapNode, video: YouTubeVideoEntry) => {
        if (!video.videoStoragePath) { handleCancelInsert(); return; } // defensive — picker already filters these out
        const now = getPreciseActiveMs();
        const next = [...timelineRef.current];
        if (now > currentLiveStartRef.current) {
            next.push({ type: 'live', sourceStartMs: currentLiveStartRef.current, sourceEndMs: now });
        }
        next.push({
            type: 'inserted',
            sourceClip: { notebookId: notebook.id, nodeId: pickedNode.id, videoId: video.youtubeVideoId },
            title: video.youtubeTitle || video.videoTitle || video.youtubeVideoId,
            thumbnail: `https://i.ytimg.com/vi/${video.youtubeVideoId}/mqdefault.jpg`,
        });
        timelineRef.current = next;
        currentLiveStartRef.current = now;
        setTimelineSegments(next);
        setShowInsertPicker(false);
        resumeRecording();
    }, [getPreciseActiveMs, notebook.id, resumeRecording, handleCancelInsert]);

    // Global Ctrl/Cmd+Shift+[toggleOverlay key] to toggle drawing overlay
    // Global Shift+Q to pause/resume recording
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!e.shiftKey || e.altKey) return;
            const key = e.key.toLowerCase();

            // Shift+Q: pause / resume recording
            if (!e.ctrlKey && !e.metaKey && key === 'q') {
                e.preventDefault();
                const mrState = mediaRecorderRef.current?.state;
                if (mrState === 'recording') pauseRecording();
                else if (mrState === 'paused') resumeRecording();
                return;
            }

            // Ctrl/Cmd+Shift+[toggleOverlay key]: toggle drawing overlay. Needs a modifier beyond
            // plain Shift — a bare Shift+letter while a text editor is focused gets typed as a
            // literal capital letter (the editor's own keydown handling runs and inserts the
            // character before this window-level listener even sees the event), so switching a
            // note while recording never reliably toggled drawing. Ctrl/Cmd+letter never produces
            // literal text input, so it reaches us regardless of where focus is.
            if (e.ctrlKey || e.metaKey) {
                const hk = loadDrawHk();
                const toggleKey = hk.toggleOverlay || 'd';
                if (key === toggleKey.toLowerCase()) {
                    e.preventDefault();
                    setShowDrawing(v => !v);
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [pauseRecording, resumeRecording]);

    useEffect(() => {
        if (recordState === 'idle' || classmateOn) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== '`' || e.ctrlKey || e.metaKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            e.preventDefault();
            toggleClassmate();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [recordState, classmateOn, toggleClassmate]);

    const stopRecording = useCallback(() => {
        checkpointTimerRef.current && clearInterval(checkpointTimerRef.current);
        checkpointTimerRef.current = null;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            isCheckpointCycleRef.current = false; // ensure full stop path
            mediaRecorderRef.current.stop();
        }
    }, []);

    // Stop needs a second click within 3 s, so a stray click does not end the recording.
    const [stopArmed, setStopArmed] = useState(false);
    const stopArmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onStopClick = () => {
        if (stopArmTimerRef.current) { clearTimeout(stopArmTimerRef.current); stopArmTimerRef.current = null; }
        if (stopArmed) { setStopArmed(false); stopRecording(); return; }
        setStopArmed(true);
        stopArmTimerRef.current = setTimeout(() => { setStopArmed(false); stopArmTimerRef.current = null; }, 3000);
    };
    useEffect(() => { if (recordState === 'idle') setStopArmed(false); }, [recordState]);

    const uploadResumePart = (jobId: string) => {
        const userId = auth.currentUser?.uid || uid;
        const blob = resumeBlobsRef.current.get(jobId);
        if (!userId || !blob) return Promise.reject(new Error(vn ? 'Không còn bản quay trước trên máy này.' : 'The earlier recording is no longer on this computer.'));
        const task = uploadBytesResumable(storageRef(storage, `recordings/${userId}/${jobId}/raw.webm`), blob, { contentType: blob.type || 'video/webm' });
        const p = new Promise<void>((resolve, reject) => task.on('state_changed', undefined, reject, () => resolve()))
            .then(() => setResumeParts(prev => prev.map(x => (x.jobId === jobId ? { ...x, uploaded: true } : x))));
        resumeUploadsRef.current.set(jobId, p);
        p.catch(() => resumeUploadsRef.current.delete(jobId));
        return p;
    };

    // The recording just stopped, as export chunks on its own clock.
    const lastRecordingChunks = (): ExportChunk[] => {
        if (autoPaceEnabled && speedChunksRef.current.length > 0) {
            return sanitizeChunksForExport(reviewChunks.length > 0 ? reviewChunks : speedChunksRef.current);
        }
        if (timelineSegments.length > 0) return buildTimelineChunks(timelineSegments);
        return [{ startMs: 0, endMs: 4 * 60 * 60 * 1000, speed: 1, kind: 'speaking' as const }];
    };
    const lastRecordingMs = () => {
        const own = lastRecordingChunks().filter(c => !c.sourceClip);
        const end = own.length ? own[own.length - 1].endMs : 0;
        return end < 4 * 60 * 60 * 1000 ? end : (clips[0]?.durationSecs || 0) * 1000;
    };

    const continueRecording = async (cutMs: number) => {
        const earlier = clips[0];
        if (!earlier || recordState !== 'idle') return;
        const chunks = cutChunksAt(lastRecordingChunks(), cutMs);
        const jobId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        setResumePickMs(null);
        await startRecording();
        // Picker cancelled or the screen could not be shared: nothing changes.
        if (mediaRecorderRef.current?.state !== 'recording') return;
        if (chunks.length > 0) {
            resumeBlobsRef.current.set(jobId, earlier.blob);
            setResumeParts(prev => [...prev, { jobId, chunks, keptMs: cutMs, uploaded: false }]);
            uploadResumePart(jobId).catch(e => console.error('[resume] upload failed, retried at export', e));
        }
        setReviewChunks([]);
    };

    const redoFromSegment = useCallback((segIdx: number) => {
        // Record redo splice: keep chunks up to end of segment segIdx-1, skip current bad chunks
        const keepEnd = segIdx > 0 ? (segChunkCountsRef.current[segIdx - 1] ?? 0) : 0;
        const redoStart = chunksRef.current.length;
        redoSplicesRef.current.push({ keepEnd, redoStart });

        // Update UI state — don't touch video chunks
        setClips(prev => prev.slice(0, segIdx));
        setSegments(prev => [
            ...prev.filter(s => s.index < segIdx),
            { index: segIdx, startSecs: segmentStartSecsRef.current, status: 'recording' },
        ]);
        currentSegmentIndexRef.current = segIdx;
        segChunkCountsRef.current = segChunkCountsRef.current.slice(0, segIdx);

        // Restart audio only
        if (audioMrRef.current && audioMrRef.current.state !== 'inactive') {
            audioMrRef.current.onstop = () => {
                audioChunksRef.current = [];
                if (micStreamRef.current && micStreamRef.current.getAudioTracks().length > 0) {
                    const audioMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
                    const newAudioMr = new MediaRecorder(new MediaStream(micStreamRef.current.getAudioTracks()), { mimeType: audioMime });
                    audioMrRef.current = newAudioMr;
                    newAudioMr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
                    newAudioMr.start(500);
                }
            };
            audioMrRef.current.stop();
        } else {
            audioChunksRef.current = [];
        }

        // Reset checkpoint timer
        if (checkpointTimerRef.current) {
            clearInterval(checkpointTimerRef.current);
            const intervalMs = checkpointMins * 60 * 1000;
            checkpointTimerRef.current = setInterval(() => {
                handleCheckpointRef.current?.();
            }, intervalMs);
        }
    }, [checkpointMins]);

    // Auto-Pace's equivalent of redoFromSegment above, at per-sentence (per-check) granularity
    // instead of per whole Checkpoint segment. Unlike Checkpoint's approach — which splices the
    // raw video blob's chunk array at export time — Auto-Pace already assembles its final video by
    // walking selected [startMs,endMs] ranges of the raw recording (see buildTimelineChunks /
    // sanitizeChunksForExport), so reverting only needs to stop describing the discarded stretch:
    // whatever ms range between the flagged moment and the next chunk that opens after resuming
    // simply has no chunk covering it, and the export step skips ranges nothing covers, on its own.
    // The raw MediaRecorder itself is untouched here — it just sits paused, same as any manual
    // pause, so nothing new is even captured for the stretch we're discarding.
    const requestAutoPaceRevert = useCallback((chunkIndex: number, targetMs: number) => {
        if (recordState !== 'recording') return;
        pauseRecording();
        setPendingAutoPaceRevert({ chunkIndex, targetMs });
    }, [recordState, pauseRecording]);

    const cancelAutoPaceRevert = useCallback(() => {
        setPendingAutoPaceRevert(null);
        resumeRecording();
    }, [resumeRecording]);

    const confirmAutoPaceRevert = useCallback(() => {
        if (!pendingAutoPaceRevert) return;
        const { chunkIndex, targetMs } = pendingAutoPaceRevert;

        // Discard any in-flight quality-check window silently (onstop cleared first) — it was
        // recording audio from the stretch we're about to erase, and letting it complete later
        // would attach a stale result to whatever new chunk lands on its old index.
        if (autoPaceCheckWindowTimerRef.current) { clearTimeout(autoPaceCheckWindowTimerRef.current); autoPaceCheckWindowTimerRef.current = null; }
        if (autoPaceCheckMrRef.current) {
            autoPaceCheckMrRef.current.onstop = null;
            if (autoPaceCheckMrRef.current.state !== 'inactive') autoPaceCheckMrRef.current.stop();
            autoPaceCheckMrRef.current = null;
        }
        autoPaceCheckWindowCtlRef.current = null;

        // Truncate the flagged chunk to end exactly at the revert point (dropping its own checks
        // from that moment on too) and drop every chunk recorded after it — that stretch, and its
        // raw video/audio, is about to be excluded from the exported timeline entirely.
        const kept = speedChunksRef.current.slice(0, chunkIndex + 1).map((c, i) =>
            i !== chunkIndex ? c : { ...c, endMs: Math.min(c.endMs, targetMs), checking: false, checks: (c.checks || []).filter(chk => chk.startMs < targetMs) }
        );
        speedChunksRef.current = kept;
        setSpeedChunks(kept);

        // Re-open a fresh chunk at the recording's CURRENT position — where the re-take will start
        // once we resume (the recorder is paused right now, so this is the moment the revert was
        // requested). It must NOT start at targetMs: the raw file still contains the flagged
        // take between targetMs and now, and a chunk starting at targetMs would run straight
        // through it (closeAutoPaceChunk spans start → close time), putting the wrong take back
        // into the export. Leaving [targetMs, now) uncovered is what removes it.
        openAutoPaceChunk(getPreciseActiveMs(), 'speaking');

        setPendingAutoPaceRevert(null);
        resumeRecording();
    }, [pendingAutoPaceRevert, openAutoPaceChunk, resumeRecording, getPreciseActiveMs]);

    // ── Re-watch and re-record from a chosen moment ──
    // The user pauses, plays back what was recorded, and picks the exact moment a mistake began.
    // Same principle as the revert above: the raw recording is not touched, the stretch from that
    // moment to the pause simply stops being described by any chunk, so the export leaves it out.
    const closeRewatch = useCallback(() => {
        setRewatch(prev => { if (prev) URL.revokeObjectURL(prev.url); return null; });
    }, []);
    useEffect(() => { if (recordState === 'idle') closeRewatch(); }, [recordState, closeRewatch]);

    const openRewatch = useCallback(async () => {
        if (!autoPaceEnabled || rewatchLoading || rewatch) return;
        if (recordState === 'recording') pauseRecording();
        setRewatchLoading(true);
        try {
            const mr = mediaRecorderRef.current;
            if (mr && mr.state !== 'inactive') {
                // Ask for whatever is still buffered (data arrives every 500 ms otherwise).
                await new Promise<void>(resolve => {
                    const done = () => { mr.removeEventListener('dataavailable', done); resolve(); };
                    mr.addEventListener('dataavailable', done);
                    try { mr.requestData(); } catch { done(); }
                    setTimeout(done, 1000);
                });
            }
            await Promise.allSettled([...pendingPartSavesRef.current]);
            const parts = await idbPeekParts(partsFlushedRef.current);
            const blob = new Blob([...parts, ...chunksRef.current], { type: mr?.mimeType || 'video/webm' });
            setRewatch({ url: URL.createObjectURL(blob), endMs: getPreciseActiveMs() });
        } catch (e) {
            console.error('[rewatch] could not assemble the recording so far', e);
            setSizeBanner(vn ? 'Không mở được phần đã quay để xem lại. Việc quay không bị ảnh hưởng.' : 'Could not open the recording so far for review. The recording itself is not affected.');
            setTimeout(() => setSizeBanner(null), 6000);
        } finally {
            setRewatchLoading(false);
        }
    }, [autoPaceEnabled, rewatchLoading, rewatch, recordState, pauseRecording, idbPeekParts, getPreciseActiveMs, vn]);

    const revertRecordingTo = useCallback((targetMs: number) => {
        const nowMs = getPreciseActiveMs();
        const t = Math.max(0, Math.min(targetMs, nowMs));
        if (nowMs - t < 100) return;

        // The quality-check window in flight was listening to the stretch being discarded.
        if (autoPaceCheckWindowTimerRef.current) { clearTimeout(autoPaceCheckWindowTimerRef.current); autoPaceCheckWindowTimerRef.current = null; }
        if (autoPaceCheckMrRef.current) {
            autoPaceCheckMrRef.current.onstop = null;
            if (autoPaceCheckMrRef.current.state !== 'inactive') autoPaceCheckMrRef.current.stop();
            autoPaceCheckMrRef.current = null;
        }
        autoPaceCheckWindowCtlRef.current = null;
        pendingAutoPaceUpdatesRef.current = [];

        rewatchUndoRef.current = {
            chunks: speedChunksRef.current,
            openStartMs: currentChunkStartMsRef.current,
            openKind: currentChunkKindRef.current,
            openSpeed: currentChunkSpeedRef.current,
            openThumb: currentChunkThumbnailRef.current,
        };

        const kept = chunksAfterRevert(speedChunksRef.current, {
            startMs: currentChunkStartMsRef.current, speed: currentChunkSpeedRef.current,
            kind: currentChunkKindRef.current, thumbnail: currentChunkThumbnailRef.current,
        }, t);
        speedChunksRef.current = kept;
        setSpeedChunks(kept);

        // The re-take starts where the recording is now, never at `t`: the raw file still holds
        // the discarded take between `t` and now, and only leaving it uncovered removes it.
        autoPaceModeRef.current = 'speaking';
        setAutoPaceLiveMode('speaking');
        openAutoPaceChunk(nowMs, 'speaking');
        setRewatchCuts(prev => [...prev, { fromMs: t, toMs: nowMs }]);
    }, [getPreciseActiveMs, openAutoPaceChunk]);

    const undoRewatchRevert = useCallback(() => {
        const snap = rewatchUndoRef.current;
        if (!snap) return;
        rewatchUndoRef.current = null;
        // Drop the check window opened for the re-take that will now not happen from here.
        if (autoPaceCheckWindowTimerRef.current) { clearTimeout(autoPaceCheckWindowTimerRef.current); autoPaceCheckWindowTimerRef.current = null; }
        if (autoPaceCheckMrRef.current) {
            autoPaceCheckMrRef.current.onstop = null;
            if (autoPaceCheckMrRef.current.state !== 'inactive') autoPaceCheckMrRef.current.stop();
            autoPaceCheckMrRef.current = null;
        }
        autoPaceCheckWindowCtlRef.current = null;
        pendingAutoPaceUpdatesRef.current = [];
        speedChunksRef.current = snap.chunks;
        setSpeedChunks(snap.chunks);
        currentChunkStartMsRef.current = snap.openStartMs;
        currentChunkKindRef.current = snap.openKind;
        currentChunkSpeedRef.current = snap.openSpeed;
        currentChunkThumbnailRef.current = snap.openThumb;
        const mode: AutoPaceMode = snap.openKind === 'speaking' ? 'speaking' : 'active-silent';
        autoPaceModeRef.current = mode;
        setAutoPaceLiveMode(mode);
        setRewatchCuts(prev => prev.slice(0, -1));
    }, []);

    const manualCheckpoint = useCallback(() => {
        if (!handleCheckpointRef.current) return;
        // Reset interval so next auto-checkpoint fires N minutes from now
        if (checkpointTimerRef.current) {
            clearInterval(checkpointTimerRef.current);
            const intervalMs = checkpointMins * 60 * 1000;
            checkpointTimerRef.current = setInterval(() => {
                handleCheckpointRef.current?.();
            }, intervalMs);
        }
        handleCheckpointRef.current();
    }, [checkpointMins]);

    const removeClip = (id: number) => {
        setClips(prev => prev.filter(c => c.id !== id));
    };

    const goToReview = () => {
        if (clips.length === 0) return;
        if (clips.length > 1) {
            setStage('clip-review');
        } else {
            setFile(mergeClips(clips));
            setStage('review');
        }
    };

    // Chunk review (step 7): a local editable copy of speedChunks so the user's speed overrides
    // don't get lost if speedChunks were ever recomputed, and so Cancel-without-saving is trivial.
    const [reviewChunks, setReviewChunks] = useState<SpeedChunk[]>([]);
    const [previewingChunkIdx, setPreviewingChunkIdx] = useState<number | null>(null);
    const chunkPreviewVideoRef = useRef<HTMLVideoElement | null>(null);
    const chunkPreviewUrlRef = useRef<string | null>(null);
    useEffect(() => {
        if (stage !== 'chunk-review') return;
        setReviewChunks(speedChunksRef.current);
        if (clips[0]) {
            chunkPreviewUrlRef.current && URL.revokeObjectURL(chunkPreviewUrlRef.current);
            chunkPreviewUrlRef.current = URL.createObjectURL(clips[0].blob);
        }
        return () => {
            if (chunkPreviewUrlRef.current) { URL.revokeObjectURL(chunkPreviewUrlRef.current); chunkPreviewUrlRef.current = null; }
        };
    }, [stage]);

    const previewChunk = (chunk: SpeedChunk) => {
        const v = chunkPreviewVideoRef.current;
        if (!v) return;
        v.currentTime = chunk.startMs / 1000;
        v.playbackRate = chunk.speed;
        setPreviewingChunkIdx(chunk.index);
        v.play().catch(() => {});
        const onTime = () => {
            if (v.currentTime >= chunk.endMs / 1000) {
                v.pause();
                v.removeEventListener('timeupdate', onTime);
                setPreviewingChunkIdx(null);
            }
        };
        v.addEventListener('timeupdate', onTime);
    };

    const toggleChunkSpeed = (idx: number) => {
        setReviewChunks(prev => prev.map(c => c.index === idx
            ? (c.kind === 'speaking' ? { ...c, kind: 'silent', speed: silentSpeed } : { ...c, kind: 'speaking', speed: speakingSpeed })
            : c));
    };

    const mergeAndContinue = () => {
        if (clips.length === 0) return;
        setFile(mergeClips(clips));
        setStage('review');
    };

    // Server-side export (plan step 8): upload the raw recording, ask requestVideoExport to
    // validate + kick off the Cloud Run job, then watch the Firestore job doc until it's done and
    // swap in the processed file — from there the rest of the flow (stage 'review', handleUpload)
    // is completely unchanged.
    type ExportPhase = 'idle' | 'uploading' | 'pending' | 'processing' | 'done' | 'error';
    const [exportPhase, setExportPhase] = useState<ExportPhase>('idle');
    // Autopilot (functions/src/autopilot.ts): when on, the server uploads the export to YouTube and
    // processes it overnight, so the tab can be closed once the raw recording is in Storage.
    const [autopilotOn, setAutopilotOn] = useState(false);
    const [autoUploading, setAutoUploading] = useState(false);
    useEffect(() => {
        const userId = auth.currentUser?.uid || uid;
        if (!userId) return;
        return onSnapshot(doc(db, `users/${userId}/autopilot/config`), snap => setAutopilotOn(snap.data()?.enabled === true), () => {});
    }, [uid]);
    const [exportProgressPct, setExportProgressPct] = useState(0);
    // Server-side render progress written by the Cloud Run worker (0-100 + phase). Absent on jobs
    // rendered by an older worker image — the label then just says "Processing…" with no number,
    // rather than the old fixed "~a few minutes" guess that was badly wrong for long recordings.
    const [exportJobProgress, setExportJobProgress] = useState<{ pct: number; phase?: string } | null>(null);
    const processingLabel = () => {
        const p = exportJobProgress;
        if (autoUploading) return vn ? 'Server đang upload lên YouTube...' : 'The server is uploading to YouTube...';
        if (!p) return vn ? 'Đang xử lý...' : 'Processing...';
        const phases: Record<string, [string, string]> = {
            downloading: ['đang tải', 'downloading'], normalizing: ['chuẩn hoá', 'normalizing'],
            cutting: ['cắt đoạn', 'cutting'], merging: ['ghép', 'merging'], uploading: ['lưu kết quả', 'saving'],
        };
        const ph = p.phase && phases[p.phase] ? ` · ${phases[p.phase][vn ? 0 : 1]}` : '';
        return vn ? `Đang xử lý ${p.pct}%${ph}` : `Processing ${p.pct}%${ph}`;
    };

    // Restore the full dialog once the wait is over, so success/error screens (and the existing
    // auto-close countdown) always display normally — minimize only ever covers the waiting period.
    useEffect(() => {
        if (stage === 'done' || stage === 'error') setModalMinimized(false);
    }, [stage]);
    const [exportError, setExportError] = useState('');
    const exportUnsubRef = useRef<(() => void) | null>(null);
    // The videoExportJobs doc id whose output is currently under review, if any — either the job
    // this modal's own startExport() just created, or one handed off via sourceJobId (Combine /
    // Manager recovery). Used only to mark that job `consumed: true` after a real YouTube upload
    // success (handleUpload) — never touched just by opening/reviewing/closing this modal.
    const activeExportJobIdRef = useRef<string | null>(sourceJobId ?? null);
    // Permanent Firebase Storage path of the video actually being uploaded — set either by
    // startExport (Auto-Pace: the Cloud Run job's final output) or, for a plain recording with no
    // export step, uploaded directly in handleUpload. Kept forever (no cleanup/expiry anywhere in
    // this app) so clips remain available for future reuse — YouTube's API has no video-download
    // endpoint, so this is the only way to get the bytes back later.
    const finalStoragePathRef = useRef<string | null>(null);

    useEffect(() => () => { exportUnsubRef.current?.(); }, []); // unsubscribe if the modal unmounts mid-export
    const ownedJobIdsRef = useRef<string[]>([]);
    const claimExportJob = (jobId: string) => { liveExportJobIds.add(jobId); ownedJobIdsRef.current.push(jobId); };
    useEffect(() => {
        if (sourceJobId) claimExportJob(sourceJobId);
        return () => { ownedJobIdsRef.current.forEach(id => liveExportJobIds.delete(id)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Real long sessions with many mode transitions (70+ chunks) can accumulate sub-second timing
    // noise — separate `performance.now()` calls a few microseconds apart at each pause/resume
    // boundary, or setInterval throttling while the tab is backgrounded — occasionally producing a
    // chunk pair that's off by a hair (e.g. next chunk starting a few ms before the previous one
    // technically ended). Rather than chase every possible timing-jitter source in the state
    // machine, guarantee strictly sorted/non-overlapping output here, right before it's sent to the
    // server's stricter validator, by clamping each chunk to start no earlier than the previous
    // one's end and dropping anything that degenerates to zero/negative length as a result.
    function sanitizeChunksForExport(chunks: SpeedChunk[]): { startMs: number; endMs: number; speed: number; kind: 'speaking' | 'silent' }[] {
        const sorted = [...chunks].sort((a, b) => a.startMs - b.startMs);
        const out: { startMs: number; endMs: number; speed: number; kind: 'speaking' | 'silent' }[] = [];
        let prevEnd = 0;
        for (const c of sorted) {
            const startMs = Math.max(c.startMs, prevEnd);
            const endMs = c.endMs;
            if (endMs <= startMs) continue; // this chunk was fully absorbed by clamping — drop it
            out.push({ startMs, endMs, speed: c.speed, kind: c.kind });
            prevEnd = endMs;
        }
        return out;
    }

    // Builds the export chunk list for the "Insert clip" timeline: live segments slice this
    // session's own raw recording (startMs/endMs from sourceStartMs/sourceEndMs — the recording's
    // own clock, not the final output's timeline, which the server derives by concatenation order);
    // inserted segments reuse a whole other video via sourceClip, always full-duration/full-speed —
    // a generous 4h sentinel endMs stands in for "the whole clip" since ffmpeg's trim filters clamp
    // to the source's actual duration on their own.
    function buildTimelineChunks(segments: TimelineSegment[]): { startMs: number; endMs: number; speed: number; kind: 'speaking' | 'silent'; sourceClip?: { notebookId: string; nodeId: string; videoId: string } }[] {
        return segments.map(seg => seg.type === 'live'
            ? { startMs: seg.sourceStartMs, endMs: seg.sourceEndMs, speed: 1, kind: 'speaking' as const }
            : { startMs: 0, endMs: 4 * 60 * 60 * 1000, speed: 1, kind: 'speaking' as const, sourceClip: seg.sourceClip }
        );
    }

    // The export chunks that fall inside a marked part, clipped to it — same speeds as the full video.
    function chunksForMark<T extends { startMs: number; endMs: number }>(chunks: T[], m: { startMs: number; endMs: number | null }): T[] {
        const lo = m.startMs, hi = m.endMs ?? m.startMs;
        return chunks
            .filter(c => c.endMs > lo && c.startMs < hi)
            .map(c => ({ ...c, startMs: Math.max(c.startMs, lo), endMs: Math.min(c.endMs, hi) }))
            .filter(c => c.endMs - c.startMs >= 1);
    }
    const clipOutputSecs = (m: { startMs: number; endMs: number | null }) =>
        Math.round(chunksForMark(sanitizeChunksForExport(reviewChunks), m).reduce((t, c) => t + (c.endMs - c.startMs) / c.speed, 0) / 1000);

    // chunks: defaults to the Auto-Pace speed-chunk timeline (sanitizeChunksForExport(reviewChunks))
    // when omitted — the "Insert clip" timeline-review stage passes its own mixed live/inserted
    // chunk list instead. Both share every step after chunk-building: upload this session's own raw
    // recording, request the export, wait for the result, land on 'review' with the file swapped in.
    const startExport = async (chunks?: { startMs: number; endMs: number; speed: number; kind: 'speaking' | 'silent'; sourceClip?: { notebookId: string; nodeId: string; videoId: string } }[]) => {
        const rawClip = clips[0];
        const userId = auth.currentUser?.uid || uid;
        if (!rawClip || !userId) return;
        setExportPhase('uploading');
        setExportProgressPct(0);
        setExportJobProgress(null);
        setExportError('');
        try {
            // Earlier parts from "continue recording" must be in Storage before the export starts.
            for (const p of resumeParts) {
                if (p.uploaded) continue;
                const pending = resumeUploadsRef.current.get(p.jobId);
                if (pending) { try { await pending; continue; } catch { /* retried below */ } }
                await uploadResumePart(p.jobId);
            }
            const jobId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            claimExportJob(jobId);
            const storagePath = `recordings/${userId}/${jobId}/raw.webm`;
            const task = uploadBytesResumable(storageRef(storage, storagePath), rawClip.blob, { contentType: rawClip.blob.type || 'video/webm' });
            await new Promise<void>((resolve, reject) => {
                task.on('state_changed',
                    snap => setExportProgressPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
                    reject,
                    () => resolve());
            });

            setExportPhase('pending');
            const requestExport = httpsCallable(functions, 'requestVideoExport');
            await requestExport({
                storagePath,
                // Earlier parts of a series first, then the recording before "continue recording", then this one.
                chunks: [...seriesChunks(), ...resumeExportChunks(resumeParts), ...(chunks ?? sanitizeChunksForExport(reviewChunks))],
                notebookId: notebook.id,
                nodeId: node.id,
                autopilot: autopilotOn,
                language,
            });
            activeExportJobIdRef.current = jobId;

            // Spin-off clips of the marked parts: separate jobs on the same raw file. Only for the
            // Auto-Pace timeline (an "Insert clip" timeline passes its own chunks and has no marks).
            if (!chunks && clipMarksRef.current.length > 0) {
                const all = sanitizeChunksForExport(reviewChunks);
                const started: { jobId: string; title: string; mainJobId: string }[] = [];
                for (const [i, m] of clipMarksRef.current.entries()) {
                    const part = chunksForMark(all, m);
                    if (part.reduce((t, c) => t + (c.endMs - c.startMs), 0) < MIN_CLIP_MARK_MS) continue; // e.g. the whole part was re-taken
                    const clipTitle = m.title.trim() || `${node.title || 'Untitled'} — ${vn ? 'phần tách' : 'part'} ${i + 1}`;
                    try {
                        const r: any = await requestExport({ storagePath, chunks: part, notebookId: notebook.id, nodeId: node.id, asNewJob: true, clipOf: jobId, clipTitle, autopilot: autopilotOn, language });
                        if (r?.data?.jobId) { claimExportJob(r.data.jobId); started.push({ jobId: r.data.jobId, title: clipTitle, mainJobId: jobId }); }
                    } catch (e) {
                        console.error('[clips] could not start a spin-off clip', e); // the full video is unaffected
                    }
                }
                if (started.length) onClipsStarted?.(started);
            }

            const jobRef = doc(db, `users/${userId}/videoExportJobs/${jobId}`);
            exportUnsubRef.current = onSnapshot(jobRef, async (snap) => {
                const data = snap.data();
                if (!data) return;
                if (data.status === 'processing' || data.status === 'pending') {
                    setExportPhase(data.status);
                    setExportJobProgress(typeof data.progress === 'number' ? { pct: data.progress, phase: data.phase } : null);
                } else if (data.status === 'done' && data.autopilot && !['error', 'skipped'].includes(data.autoUpload?.state)) {
                    // The server uploads this one itself; uploading it here as well would make a duplicate.
                    if (data.autoUpload?.state === 'done' && data.autoUpload.videoId) {
                        exportUnsubRef.current?.();
                        setAutoUploading(false);
                        setExportPhase('done');
                        setResultUrl(`https://www.youtube.com/watch?v=${data.autoUpload.videoId}`);
                        serverUploadedRef.current = true;
                        setStage('done');
                    } else {
                        setAutoUploading(true);
                    }
                } else if (data.status === 'done') {
                    exportUnsubRef.current?.();
                    setAutoUploading(false);
                    try {
                        const url = await getDownloadURL(storageRef(storage, data.storagePathFinal));
                        const res = await fetch(url);
                        const blob = await res.blob();
                        const finalFile = new File([blob], `studyboard-${Date.now()}.mp4`, { type: 'video/mp4' });
                        setFile(finalFile);
                        finalStoragePathRef.current = data.storagePathFinal;
                        setExportPhase('done');
                        setStage('review');
                    } catch (e: any) {
                        setExportPhase('error');
                        setExportError(e?.message || 'Failed to fetch exported video');
                    }
                } else if (data.status === 'error') {
                    exportUnsubRef.current?.();
                    setExportPhase('error');
                    setExportError(data.error || 'Export failed');
                }
            });
        } catch (e: any) {
            setExportPhase('error');
            setExportError(e?.message || 'Export failed to start');
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        // A spin-off clip's file IS its export job's output, already kept in Storage — point the
        // backup at it instead of uploading the same bytes a second time after the YouTube upload.
        if (!finalStoragePathRef.current && clipOfJobId && sourceJobId) {
            const ownerId = auth.currentUser?.uid || uid;
            if (ownerId) finalStoragePathRef.current = `recordings/${ownerId}/${sourceJobId}/final.mp4`;
        }
        setStage('uploading');
        setProgress(0);
        try {
            const result = await uploadToYouTube({
                file,
                title,
                description,
                tags: node.tags?.map(t => t.replace('#', '')),
                thumbnailBase64: thumbnailBase64,
                thumbnailMime,
                visibility,
                madeForKids: madeForKids ?? undefined,
                onProgress: setProgress,
            });
            setResultUrl(result.videoUrl);
            setStage('done');
            idbDeleteRecording(); // upload succeeded — no need to keep recovery copy

            // Real upload success is the only thing (besides the user dismissing it in Manager)
            // allowed to resolve a videoExportJobs doc — never set just by opening/reviewing this
            // modal, or a closed-tab-before-uploading case would look "handled" when it wasn't.
            if (activeExportJobIdRef.current) {
                const userId = auth.currentUser?.uid || uid;
                if (userId) {
                    updateDoc(doc(db, `users/${userId}/videoExportJobs/${activeExportJobIdRef.current}`), { consumed: true }).catch(() => {});
                }
            }

            // Order matters: the note link and playlist go first, the (slow, large) permanent backup
            // goes last. The backup can take minutes for a long video while the UI already says
            // "done" — closing the tab in that window used to lose the link to the note entirely.
            // Write directly to Firestore immediately — don't rely on onDone closure being fresh
            let linkedNode: typeof node | null = null;
            if (result.videoId) {
                try {
                    const userId = auth.currentUser?.uid || uid;
                    if (userId && notebook?.id && node?.id) {
                        const nodeRef = doc(db, `users/${userId}/notebooks/${notebook.id}/nodes/${node.id}`);
                        // For a 2nd+ video, this is the FIRST time its own entry exists — the only
                        // chance to attach the live "AI Checkpoint" segments it collected, since
                        // saveSegmentsNow() couldn't write them anywhere safe mid-recording (see there).
                        // For slot 0, this just re-confirms what saveSegmentsNow already wrote incrementally.
                        const finalSegments = segmentsToSave(segmentsRef.current);
                        // Read the note fresh: the `node` prop can predate another session's upload to
                        // the same note, and appending against a stale copy overwrites that video's link.
                        const freshSnap = await getDoc(nodeRef).catch(() => null);
                        const fresh = freshSnap?.exists() ? (freshSnap.data() as Partial<typeof node>) : null;
                        const base = fresh ? { ...node, youtubeVideoId: fresh.youtubeVideoId, youtubeVideos: fresh.youtubeVideos } : node;
                        const patch = buildVideoAppendPatch(base, {
                            youtubeVideoId: result.videoId,
                            youtubeStatus: 'private',
                            youtubeTitle: result.title,
                            youtubeUploadedAt: Date.now(),
                            videoUrl: result.videoUrl,
                            videoTitle: result.title,
                            ...(description ? { youtubeDescription: description } : {}),
                            ...(finalStoragePathRef.current ? { videoStoragePath: finalStoragePathRef.current } : {}),
                            ...(result.thumbnailSet ? { thumbnailPushedAt: Date.now() } : {}),
                            // Ties a full video to the clips marked in it: the full video carries its
                            // export job id, each clip carries that same id as clipOfJobId.
                            ...(clipOfJobId ? { clipOfJobId } : (activeExportJobIdRef.current ? { exportJobId: activeExportJobIdRef.current } : {})),
                            ...(finalSegments.length > 0 ? { videoSegments: finalSegments } : {}),
                        });
                        await updateDoc(nodeRef, patch);
                        linkedNode = { ...base, ...patch } as typeof node;
                        // Whichever of the family is uploaded last completes the links: the full video's
                        // description lists every clip of it that is on YouTube by now.
                        const familyId = clipOfJobId || activeExportJobIdRef.current;
                        if (familyId) {
                            try {
                                const after = await getDoc(nodeRef);
                                const nowNode = after.exists() ? (after.data() as typeof node) : null;
                                const vids = nowNode ? getVideosForNode(nowNode) : [];
                                const full = vids.find(v => v.exportJobId === familyId);
                                const clipVids = vids.filter(v => v.clipOfJobId === familyId);
                                if (nowNode && full && clipVids.length > 0) {
                                    const next = mergeClipLinksIntoDescription(full.youtubeDescription || '', clipVids.map(c => ({ title: c.youtubeTitle || c.videoTitle || 'Video', url: c.videoUrl || `https://www.youtube.com/watch?v=${c.youtubeVideoId}` })), vn);
                                    if (next !== (full.youtubeDescription || '')) {
                                        await updateYouTubeVideo(full.youtubeVideoId, { description: next });
                                        await updateDoc(nodeRef, buildVideoUpdatePatch(nowNode, full.youtubeVideoId, { youtubeDescription: next, youtubeEditedAt: Date.now() }));
                                    }
                                }
                            } catch (e) { console.error('[clips] could not add clip links to the full video', e); }
                        }
                    }
                } catch (e) { console.error('[upload] linking the video to its note failed; onDone links it instead', e); }
            }

            // Playlist assignment is best-effort — never fail the upload over it.
            if (result.videoId) {
                try {
                    let playlistId = selectedPlaylistId;
                    if (!playlistId && newPlaylistTitle.trim()) {
                        const pl = await createYouTubePlaylist(newPlaylistTitle.trim());
                        playlistId = pl.id;
                    }
                    if (playlistId) await addVideoToPlaylist(playlistId, result.videoId);
                } catch { /* non-fatal */ }
            }

            // Permanent backup copy — best-effort, never fail the upload over it. Auto-Pace already
            // has one (the Cloud Run job's final output, captured in finalStoragePathRef); a plain
            // recording never touches Storage otherwise, so upload it now. Kept forever: YouTube's
            // API has no way to download a video back, so this is the only path to the raw bytes
            // later (e.g. reusing a clip inside a future bigger video).
            if (!finalStoragePathRef.current) {
                try {
                    const userId = auth.currentUser?.uid || uid;
                    if (userId) {
                        const backupId = `job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                        // Filename must be raw.webm per storage.rules (client can only write that
                        // exact name under this path) — the export-pipeline convention, reused as-is
                        // even though no processing happens here; it's just the permanent original.
                        const backupPath = `recordings/${userId}/${backupId}/raw.webm`;
                        await uploadBytesResumable(storageRef(storage, backupPath), file, { contentType: file.type || 'video/webm' });
                        finalStoragePathRef.current = backupPath;
                        if (linkedNode && result.videoId && notebook?.id && node?.id) {
                            await updateDoc(
                                doc(db, `users/${userId}/notebooks/${notebook.id}/nodes/${node.id}`),
                                buildVideoUpdatePatch(linkedNode, result.videoId, { videoStoragePath: backupPath }),
                            );
                        }
                    }
                } catch { /* non-fatal — YouTube upload already succeeded either way */ }
            }

            setTimeout(() => onDone(result.videoUrl, result.title, result.videoId, description, segmentsRef.current.filter(s => s.status !== 'recording'), {
                videoStoragePath: finalStoragePathRef.current || undefined,
                ...(clipOfJobId ? { clipOfJobId } : (activeExportJobIdRef.current ? { exportJobId: activeExportJobIdRef.current } : {})),
            }), 4000);
        } catch (err: any) {
            if (err?.message === 'not_connected') { setYtConnected(false); setStage('review'); }
            else { setErrorMsg(err?.message || 'Upload failed'); setStage('error'); }
        }
    };

    // Videos that can go in front of this recording: this notebook's, with a backup copy, newest
    // first (this note's own first), not already in the series.
    const seriesCandidates = notebook.nodes.flatMap(n => getVideosForNode(n).filter(v => v.videoStoragePath).map(v => ({ node: n, video: v })))
        .filter(({ node: n, video: v }) => !seriesParts.some(p => p.nodeId === n.id && p.videoId === v.youtubeVideoId))
        .filter(({ node: n, video: v }) => {
            const q = seriesSearch.trim().toLowerCase();
            return !q || (v.youtubeTitle || v.videoTitle || '').toLowerCase().includes(q) || (n.title || '').toLowerCase().includes(q);
        })
        .sort((a, b) => (Number(b.node.id === node.id) - Number(a.node.id === node.id)) || ((b.video.youtubeUploadedAt || 0) - (a.video.youtubeUploadedAt || 0)));
    const addSeriesPart = (n: MindMapNode, v: YouTubeVideoEntry) => {
        setSeriesParts(prev => [...prev, { key: `${n.id}::${v.youtubeVideoId}`, nodeId: n.id, videoId: v.youtubeVideoId, title: v.youtubeTitle || v.videoTitle || n.title || v.youtubeVideoId, startMs: 0, endMs: null }]);
        setShowSeriesPicker(false);
    };
    const moveSeriesPart = (i: number, dir: -1 | 1) => setSeriesParts(prev => {
        const j = i + dir;
        if (j < 0 || j >= prev.length) return prev;
        const next = [...prev]; [next[i], next[j]] = [next[j], next[i]]; return next;
    });
    const seriesTrimPart = seriesParts.find(p => p.key === seriesTrimKey) || null;
    const seriesTrimVideo = seriesTrimPart
        ? (() => { const n = notebook.nodes.find(x => x.id === seriesTrimPart.nodeId); return n ? getVideosForNode(n).find(v => v.youtubeVideoId === seriesTrimPart.videoId) : undefined; })()
        : undefined;

    const totalSecs = clips.reduce((s, c) => s + c.durationSecs, 0);

    // ── Background flow: collapsed to a corner pill ───────────────────────
    // A flow that is not in the foreground (another one was started, or it was minimized) never pops
    // its dialog open by itself — not even when its export finishes — it waits as a pill the user
    // opens on purpose. A flow that is recording keeps its own floating recording bar instead.
    if (!isForeground && recordState === 'idle') {
        const waiting = exportPhase === 'pending' || exportPhase === 'processing' || exportPhase === 'uploading' || stage === 'uploading';
        const pillLabel = exportPhase === 'uploading' ? (vn ? `Đang tải lên ${exportProgressPct}%` : `Uploading ${exportProgressPct}%`)
            : exportPhase === 'pending' ? (vn ? 'Đang khởi động...' : 'Starting...')
            : exportPhase === 'processing' ? processingLabel()
            : stage === 'uploading' ? (progress < 95 ? (vn ? 'Đang upload...' : 'Uploading...') : (vn ? 'Đang xử lý trên YouTube...' : 'Processing on YouTube...'))
            : stage === 'review' ? (vn ? 'Sẵn sàng upload — bấm để mở' : 'Ready to upload — tap to open')
            : stage === 'done' ? (vn ? 'Đã upload xong' : 'Upload complete')
            : stage === 'error' ? (vn ? 'Có lỗi — bấm để xem' : 'Something went wrong — tap to view')
            : (vn ? 'Đang chuẩn bị — bấm để tiếp tục' : 'In progress — tap to continue');
        const pillIcon = waiting ? <Loader2 size={18} className="animate-spin text-red-500 flex-shrink-0" />
            : stage === 'error' ? <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
            : <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />;
        return (
            <button
                onClick={() => { setModalMinimized(false); onRequestForeground?.(); }}
                style={{ bottom: 20 + pillIndex * 68 }}
                className="fixed left-5 z-[401] flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.25)] border border-gray-100 dark:border-slate-700 hover:scale-[1.02] transition-transform text-left"
            >
                {pillIcon}
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate max-w-[180px]">{clipOfJobId ? `✂ ${initialTitle || title}` : (node.title || 'Untitled')}</p>
                    <p className="text-[11px] text-gray-400">{pillLabel}</p>
                </div>
            </button>
        );
    }

    // ── Recording floating bar ─────────────────────────────────────────────────
    if (recordState !== 'idle') {
        const fmtRange = (a: number, b?: number) => `${formatTime(a)}${b != null ? `–${formatTime(b)}` : '–...'}`;
        const checkItems: CheckItem[] = (checkpointMode !== 'off' && segments.length > 0)
            ? segments.map(seg => ({
                key: `seg-${seg.index}`, label: `Seg ${seg.index + 1}`, range: fmtRange(seg.startSecs, seg.endSecs),
                status: seg.status === 'uploading' ? 'checking' as const : seg.status,
                score: seg.score, clarityScore: seg.clarityScore, summary: seg.summary, whatWasRight: seg.whatWasRight,
                mainIssue: seg.mainIssue, suggestion: seg.suggestedCorrection, transcript: seg.transcript,
                segIndex: seg.index, overridden: seg.overridden,
            })).reverse()
            : (autoPaceEnabled
                ? speedChunks.flatMap(c => {
                    const rows: CheckItem[] = (c.checks || []).map((chk, i) => ({
                        key: `ap-${c.index}-${i}`, label: `#${c.index + 1}`, range: fmtRange(Math.round(chk.startMs / 1000), Math.round(chk.endMs / 1000)),
                        status: chk.status, score: chk.score, clarityScore: chk.clarityScore, summary: chk.summary, whatWasRight: chk.whatWasRight,
                        mainIssue: chk.mainIssue, suggestion: chk.suggestion, transcript: chk.transcript,
                        chunkIndex: c.index, revertMs: chk.startMs,
                    }));
                    if (c.checking) rows.push({ key: `ap-${c.index}-pending`, label: `#${c.index + 1}`, range: fmtRange(Math.round(c.startMs / 1000), Math.round(c.endMs / 1000)), status: 'checking' });
                    return rows;
                }).reverse()
                : []);
        const hasWarning = segments.some(s => s.status === 'needs_redo' && !s.overridden);
        const lagCount = segments.filter(s => s.status === 'checking' || s.status === 'uploading').length;
        const currentSegmentSecs = recordSecs - segmentStartSecsRef.current;
        const isManualLike = checkpointMode === 'manual' || checkpointMode === 'hybrid';
        const isLongSegWarn = isManualLike && currentSegmentSecs >= 300 && recordState === 'recording';
        const isLongSegCrit = isManualLike && currentSegmentSecs >= 480 && recordState === 'recording';
        return (
            <>
                {drawingMounted && (
                    <ScreenDrawingOverlay
                        visible={showDrawing}
                        onClose={() => setShowDrawing(false)}
                        onSaveToNote={handleSaveDrawingToNote}
                        // Drawings of the note open in the expanded view (else this recording's note), to reopen.
                        noteDrawings={((liveNote?.attachments ?? node.attachments) || []).filter(a => a.type === 'drawing')}
                        loadDrawingVector={loadDrawingVector}
                        stash={drawingStashRef}
                        onStashed={setUnsavedDrawing}
                    />
                )}
                {classmateOn && (
                    <RecordingClassmate
                        key={`${classmateSession}-${companionRole}`}
                        joinMidway={classmateJoinMidway}
                        role={companionRole}
                        correction={professorCue}
                        // Follows the note open in the expanded view; falls back to this recording's note.
                        nodeId={liveNote?.nodeId ?? node.id}
                        nodeTitle={liveNote?.title ?? node.title ?? ''}
                        nodeContent={liveNote?.content ?? node.content ?? ''}
                        language={language}
                        paused={recordState === 'paused' && pauseOwnerRef.current === 'manual'}
                        connectAudio={connectClassmateAudio}
                        // Counts as activity, so an Auto-Pace idle pause resumes before his first word.
                        onWillSpeak={() => { lastInteractionAtRef.current = performance.now(); }}
                        onClose={() => setClassmateOn(false)}
                    />
                )}
                {showInsertPicker && (
                    <div className="fixed inset-0 z-[800] flex items-center justify-center">
                        <div className="absolute inset-0 bg-black/60" onClick={handleCancelInsert} />
                        <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-[440px] max-w-[90vw] max-h-[70vh] flex flex-col overflow-hidden">
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                                <Search size={14} className="text-gray-400 flex-shrink-0" />
                                <input
                                    autoFocus
                                    type="text"
                                    value={insertPickerSearch}
                                    onChange={e => setInsertPickerSearch(e.target.value)}
                                    placeholder={vn ? 'Tìm video...' : 'Search videos...'}
                                    className="flex-1 text-sm bg-transparent outline-none text-gray-800 dark:text-gray-200"
                                />
                                <button onClick={handleCancelInsert} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg flex-shrink-0">
                                    <X size={14} className="text-gray-400" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
                                {insertPickerVideos.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-10">{vn ? 'Không có video nào để chèn' : 'No videos available to insert'}</p>
                                ) : insertPickerVideos.map(({ node: n, video }) => (
                                    <button
                                        key={`${n.id}-${video.youtubeVideoId}`}
                                        onClick={() => handleConfirmInsert(n, video)}
                                        className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <img
                                            src={`https://i.ytimg.com/vi/${video.youtubeVideoId}/mqdefault.jpg`}
                                            className="w-16 h-9 rounded-md object-cover bg-gray-200 dark:bg-slate-600 flex-shrink-0"
                                            onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{video.youtubeTitle || video.videoTitle || video.youtubeVideoId}</p>
                                            <p className="text-[10px] text-gray-400 truncate">{n.title}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {drawingSaveMsg && (
                    <div className={`pointer-events-none fixed top-4 left-1/2 -translate-x-1/2 z-[701] px-4 py-2 ${drawingSaveFailed ? 'bg-red-600' : 'bg-green-500'} text-white text-xs font-bold rounded-xl shadow-lg animate-in fade-in duration-150`}>
                        {drawingSaveMsg}
                    </div>
                )}

                {/* Self-view webcam during recording */}
                {useWebcam && (
                    <video
                        ref={el => {
                            recordingSelfViewRef.current = el;
                            if (el && webcamStreamRef.current && el.srcObject !== webcamStreamRef.current) {
                                el.srcObject = webcamStreamRef.current;
                            }
                        }}
                        autoPlay
                        muted
                        playsInline
                        className={`fixed bottom-5 right-5 object-cover shadow-2xl border-2 border-white/20 z-[401] ${
                            webcamShape === 'circle'
                                ? 'w-24 h-24 rounded-full'
                                : 'w-44 h-[99px] rounded-2xl'
                        }`}
                        style={{ transform: 'scaleX(-1)' }}
                    />
                )}

                {/* Draggable recording bar — icon-only buttons, drag anywhere */}
                <div
                    className="pointer-events-auto fixed flex items-center gap-2 px-3 py-2 bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-red-500/40 cursor-grab active:cursor-grabbing select-none animate-in fade-in slide-in-from-bottom-2 duration-200"
                    style={{left: barPos.x, top: barPos.y, zIndex: showDrawing ? 700 : 500}}
                    onPointerDown={e => {
                        if ((e.target as HTMLElement).closest('button')) return;
                        const r = dragRef.current;
                        r.active = true; r.ox = e.clientX; r.oy = e.clientY; r.bx = barPos.x; r.by = barPos.y;
                        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={e => {
                        if (!dragRef.current.active) return;
                        const {ox, oy, bx, by} = dragRef.current;
                        setBarPos({
                            x: Math.max(0, Math.min(window.innerWidth - 220, bx + e.clientX - ox)),
                            y: Math.max(0, Math.min(window.innerHeight - 56, by + e.clientY - oy)),
                        });
                    }}
                    onPointerUp={() => { dragRef.current.active = false; }}
                >
                    {recordState === 'recording'
                        ? <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                        : <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 flex-shrink-0" />
                    }
                    <span className="text-white font-bold font-mono text-sm tracking-widest">{formatTime(recordSecs)}</span>
                    {recordingMB > 0 && (
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${recordingMB >= 1800 ? 'bg-red-500/30 text-red-300 animate-pulse' : recordingMB >= 800 ? 'bg-amber-500/20 text-amber-300' : 'text-gray-500'}`}>
                            {recordingMB >= 1024 ? `${(recordingMB / 1024).toFixed(1)}GB` : `${recordingMB}MB`}
                        </span>
                    )}
                    {checkpointMode !== 'off' && segments.length > 0 && (
                        <span className="text-gray-400 text-[10px] flex items-center gap-0.5">
                            S{currentSegmentIndexRef.current + 1}
                            {hasWarning && <span className="text-red-400">⚠</span>}
                            {isLongSegCrit && !hasWarning && <span className="text-amber-400">⏱</span>}
                        </span>
                    )}
                    {autoPaceEnabled && recordState !== 'idle' && (
                        <div
                            className="flex items-center gap-1"
                            title={
                                autoPaceLiveMode === 'speaking' ? (vn ? `Đang nói → ${speakingSpeed}x` : `Speaking → ${speakingSpeed}x`)
                                : autoPaceLiveMode === 'active-silent' ? (vn ? `Im lặng nhưng đang gõ/vẽ → ${silentSpeed}x` : `Silent + interacting → ${silentSpeed}x`)
                                : (vn ? 'Im lặng, không tương tác → tạm dừng' : 'Idle → paused')
                            }
                        >
                            {/* Live volume meter — fill height reflects mic RMS against the speech threshold */}
                            <div className="w-1.5 h-4 rounded-full bg-white/10 overflow-hidden flex items-end">
                                <div
                                    className={`w-full rounded-full transition-[height] duration-100 ${autoPaceLiveMode === 'speaking' ? 'bg-indigo-400' : 'bg-white/30'}`}
                                    style={{ height: `${Math.max(6, Math.min(100, (autoPaceLiveRms / 0.15) * 100))}%` }}
                                />
                            </div>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                                autoPaceLiveMode === 'speaking' ? 'bg-indigo-500/30 text-indigo-300'
                                : autoPaceLiveMode === 'active-silent' ? 'bg-amber-500/30 text-amber-300'
                                : 'bg-gray-500/30 text-gray-400'
                            }`}>
                                {autoPaceLiveMode === 'speaking' ? `${speakingSpeed}x` : autoPaceLiveMode === 'active-silent' ? `${silentSpeed}x` : '⏸'}
                            </span>
                        </div>
                    )}
                    {!checkpointMode && clips.length > 0 && (
                        <span className="text-gray-400 text-[10px]">×{clips.length + 1}</span>
                    )}
                    <div className="w-px h-3.5 bg-white/20" />
                    <button
                        onClick={() => setShowDrawing(d => !d)}
                        title={vn ? 'Vẽ lên màn hình' : 'Draw on screen'}
                        className={`p-1.5 rounded-lg transition-colors ${showDrawing ? 'bg-violet-500 hover:bg-violet-600 text-white' : 'bg-white/10 hover:bg-white/20 text-gray-300'}`}
                    >
                        <Pen size={12} />
                    </button>
                    <button
                        onClick={toggleClassmate}
                        title={companionRole === 'professor'
                            ? (classmateOn ? (vn ? 'Mời giáo sư nghỉ' : 'Send the professor away') : (vn ? 'Mời giáo sư vào (phím `)' : 'Call the professor in (` key)'))
                            : (classmateOn ? (vn ? 'Cho Tèo nghỉ' : 'Send Tèo away') : (vn ? 'Gọi Tèo vào hỏi (phím `)' : 'Call Tèo in to ask (` key)'))}
                        className={`px-1.5 py-1 rounded-lg transition-colors text-[11px] leading-none ${classmateOn ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-white/10 hover:bg-white/20 text-gray-300'}`}
                    >
                        {companionRole === 'professor' ? '👨‍🏫' : '🧑‍🎓'}
                    </button>
                    {!autoPaceEnabled && (
                        <button
                            onClick={handleOpenInsertPicker}
                            title={vn ? 'Chèn video có sẵn' : 'Insert existing clip'}
                            className="p-1.5 rounded-lg transition-colors bg-white/10 hover:bg-white/20 text-gray-300"
                        >
                            <ListVideo size={12} />
                        </button>
                    )}
                    {autoPaceEnabled && (
                        <button
                            onClick={toggleClipMark}
                            title={openClipMark
                                ? (vn ? 'Kết thúc đoạn tách riêng' : 'End the spin-off part')
                                : (vn ? 'Bắt đầu một đoạn sẽ thành video riêng (bấm lần nữa để kết thúc)' : 'Start a part that becomes its own video (tap again to end it)')}
                            className={`flex items-center gap-1 p-1.5 rounded-lg transition-colors ${openClipMark ? 'bg-emerald-500 hover:bg-emerald-600 text-white animate-pulse' : 'bg-white/10 hover:bg-white/20 text-gray-300'}`}
                        >
                            <Scissors size={12} />
                            {(openClipMark || clipMarks.length > 0) && (
                                <span className="text-[10px] font-bold leading-none">{openClipMark ? (vn ? 'đang đánh dấu' : 'marking') : clipMarks.length}</span>
                            )}
                        </button>
                    )}
                    {(checkpointMode === 'manual' || checkpointMode === 'hybrid') && recordState === 'recording' && (
                        <button
                            onClick={manualCheckpoint}
                            title={vn ? 'Kết thúc đoạn này, bắt đầu đoạn mới' : 'End segment'}
                            className={`p-1.5 text-sm rounded-lg transition-colors border ${isLongSegCrit ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border-amber-500/30' : 'bg-white/8 hover:bg-white/15 text-gray-400 hover:text-gray-200 border-white/10'}`}
                        >✂</button>
                    )}
                    {autoPaceEnabled && (
                        <button
                            onClick={openRewatch}
                            disabled={rewatchLoading}
                            title={vn ? 'Tạm dừng, xem lại phần vừa quay và quay lại từ một điểm' : 'Pause, re-watch what you recorded and re-record from a point'}
                            className="relative p-1.5 rounded-lg transition-colors border bg-white/8 hover:bg-white/15 text-gray-300 hover:text-white border-white/10 disabled:opacity-50"
                        >
                            {rewatchLoading ? <Loader2 size={12} className="animate-spin" /> : <History size={12} />}
                            {rewatchCuts.length > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold leading-[14px] text-center">{rewatchCuts.length}</span>
                            )}
                        </button>
                    )}
                    {recordState === 'recording' ? (
                        <button onClick={pauseRecording} title={vn ? 'Tạm dừng' : 'Pause'} className="p-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors">
                            <Pause size={12} fill="white" />
                        </button>
                    ) : (
                        <button onClick={resumeRecording} title={vn ? 'Tiếp tục' : 'Resume'} className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors">
                            <Play size={12} fill="white" />
                        </button>
                    )}
                    <button onClick={onStopClick} title={vn ? 'Dừng (bấm 2 lần)' : 'Stop (click twice)'} className={`flex items-center gap-1 p-1.5 text-white rounded-lg transition-colors ${stopArmed ? 'bg-red-700 ring-2 ring-red-300 px-2' : 'bg-red-500 hover:bg-red-600'}`}>
                        <Square size={11} fill="white" />
                        {stopArmed && <span className="text-[11px] font-bold whitespace-nowrap">{vn ? 'Bấm lần nữa để dừng' : 'Click again to stop'}</span>}
                    </button>
                </div>

                {/* Above the drawing board (z 10000), so it is seen while drawing */}
                {screenLost && (
                <div className="pointer-events-auto fixed top-4 left-1/2 -translate-x-1/2" style={{ zIndex: 10050 }}>
                        <div className="w-[460px] max-w-[92vw] px-4 py-3 bg-gray-900/95 backdrop-blur-md rounded-xl border border-red-500/50 shadow-2xl flex flex-col gap-2">
                            <p className="text-sm font-bold text-red-300">{vn ? 'Mất chia sẻ màn hình — bản quay đang tạm dừng' : 'Screen sharing ended — recording is paused'}</p>
                            <p className="text-xs text-gray-300">
                                {vn ? 'Thường do màn hình hoặc bút Wacom vừa kết nối lại. Chia sẻ lại màn hình là quay tiếp ngay trong cùng video, không mất gì.'
                                    : 'Usually a display or tablet reconnecting. Share the screen again to keep recording into the same video — nothing is lost.'}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={reshareScreen} disabled={resharing}
                                    className="flex-[2] py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-bold flex items-center justify-center gap-1.5">
                                    {resharing ? <Loader2 size={13} className="animate-spin" /> : <Play size={12} fill="white" />}
                                    {vn ? 'Chia sẻ lại màn hình & quay tiếp' : 'Share screen again & continue'}
                                </button>
                                <button onClick={() => { setScreenLost(false); stopRecording(); }}
                                    className="flex-1 py-2 rounded-lg border border-white/20 text-gray-200 text-xs font-semibold hover:bg-white/10">
                                    {vn ? 'Kết thúc quay' : 'Finish'}
                                </button>
                            </div>
                        </div>
                </div>
                )}
                {/* Transient warnings + redo confirm — top center */}
                <div className="pointer-events-auto fixed top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{zIndex: showDrawing ? 700 : 500}}>
                    {isLongSegWarn && (
                        <div className={`w-[440px] max-w-[90vw] px-4 py-2.5 backdrop-blur-md rounded-xl border flex items-center gap-3 ${isLongSegCrit ? 'bg-amber-900/60 border-amber-600/40' : 'bg-amber-950/50 border-amber-700/30'}`}>
                            <span className="text-amber-400 text-sm flex-shrink-0">⏱</span>
                            <div className="flex-1 min-w-0">
                                <p className={`text-xs font-semibold ${isLongSegCrit ? 'text-amber-300' : 'text-amber-400'}`}>
                                    {vn
                                        ? `Seg ${currentSegmentIndexRef.current + 1} đã dài ${formatTime(currentSegmentSecs)} — khó redo hơn.`
                                        : `Seg ${currentSegmentIndexRef.current + 1} is ${formatTime(currentSegmentSecs)} long — harder to redo if AI flags an issue.`}
                                </p>
                            </div>
                            <button onClick={manualCheckpoint} className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold rounded-lg bg-amber-500/25 hover:bg-amber-500/40 text-amber-300 transition-colors">
                                ✂ {vn ? 'Cắt đoạn' : 'End segment'}
                            </button>
                        </div>
                    )}
                    {sizeBanner && (
                        <div className="w-[440px] max-w-[90vw] px-4 py-2.5 bg-red-950/90 backdrop-blur-md rounded-xl border border-red-700/50 flex items-center gap-2.5">
                            <p className="text-xs text-red-300 font-semibold">{sizeBanner}</p>
                        </div>
                    )}
                    {checkpointMode !== 'off' && micUnavailable && (
                        <div className="w-[440px] max-w-[90vw] px-4 py-2.5 bg-amber-950/80 backdrop-blur-md rounded-xl border border-amber-700/40 flex items-center gap-2.5">
                            <span className="text-amber-400 text-sm flex-shrink-0">⚠</span>
                            <p className="text-xs text-amber-300">
                                {vn ? 'AI check không khả dụng: không nhận được audio từ microphone. Recording vẫn tiếp tục bình thường.' : 'AI check unavailable: microphone audio not detected. Recording continues normally.'}
                            </p>
                        </div>
                    )}
                    {checkpointMode !== 'off' && lagCount > 1 && (
                        <div className="w-[440px] max-w-[90vw] px-4 py-2.5 bg-indigo-950/80 backdrop-blur-md rounded-xl border border-indigo-700/40 flex items-center gap-2.5">
                            <span className="text-indigo-300 text-sm flex-shrink-0">⏳</span>
                            <p className="text-xs text-indigo-300">
                                {vn
                                    ? `AI check đang bị lag ${lagCount} đoạn. Nếu đoạn trước đó sai, bạn có thể mất thêm nội dung.`
                                    : `AI checks are ${lagCount} segments behind. If an earlier segment fails, later segments may be discarded.`}
                            </p>
                        </div>
                    )}
                    {redoConfirm !== null && (
                        <div className="w-[440px] max-w-[90vw] bg-gray-900/98 backdrop-blur-md rounded-2xl shadow-2xl border border-red-500/30 px-5 py-4 animate-in fade-in zoom-in-95 duration-150">
                            <p className="text-sm font-bold text-white mb-1">
                                {vn ? `Redo từ Seg ${redoConfirm + 1}?` : `Redo from Seg ${redoConfirm + 1}?`}
                            </p>
                            <p className="text-xs text-gray-400 mb-4">
                                {vn
                                    ? `Sẽ xóa Seg ${redoConfirm + 1}${segments.filter(s => s.index > redoConfirm).length > 0 ? ` và ${segments.filter(s => s.index > redoConfirm).length} đoạn sau` : ''}. Không thể hoàn tác.`
                                    : `Will discard Seg ${redoConfirm + 1}${segments.filter(s => s.index > redoConfirm).length > 0 ? ` and ${segments.filter(s => s.index > redoConfirm).length} later segment${segments.filter(s => s.index > redoConfirm).length > 1 ? 's' : ''}` : ''}. Cannot be undone.`}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { redoFromSegment(redoConfirm); setRedoConfirm(null); }}
                                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors"
                                >
                                    <RotateCcw size={11} /> {vn ? `Redo từ Seg ${redoConfirm + 1}` : `Redo from Seg ${redoConfirm + 1}`}
                                </button>
                                <button onClick={() => setRedoConfirm(null)} className="px-4 py-2 text-xs font-bold rounded-xl bg-white/8 hover:bg-white/15 text-gray-400 transition-colors">
                                    {vn ? 'Hủy — nói nốt đã' : 'Cancel — finish thought first'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Auto-Pace per-sentence revert confirm — recording is already paused (see
                    requestAutoPaceRevert) the instant the icon is clicked, unlike Checkpoint's
                    redoConfirm above which leaves recording running while it asks. A dimmed modal
                    fits that "everything is stopped, decide now" state better than a top banner. */}
                {rewatch && (
                    <RecordingRewatchPanel
                        key={rewatch.url}
                        url={rewatch.url}
                        endMs={rewatch.endMs}
                        vn={vn}
                        cuts={rewatchCuts}
                        onPick={revertRecordingTo}
                        onUndo={undoRewatchRevert}
                        onClose={closeRewatch}
                        onResume={() => { closeRewatch(); resumeRecording(); }}
                    />
                )}
                {pendingAutoPaceRevert && (
                    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="w-[420px] max-w-[92vw] bg-gray-900/98 rounded-2xl shadow-2xl border border-red-500/30 px-5 py-4 animate-in fade-in zoom-in-95 duration-150">
                            <p className="text-sm font-bold text-white mb-1">
                                {vn ? `Quay lại mốc ${formatTime(Math.round(pendingAutoPaceRevert.targetMs / 1000))}?` : `Revert to ${formatTime(Math.round(pendingAutoPaceRevert.targetMs / 1000))}?`}
                            </p>
                            <p className="text-xs text-gray-400 mb-4">
                                {vn
                                    ? 'Ghi hình đang tạm dừng. Xác nhận sẽ xóa toàn bộ hình và tiếng ghi được sau mốc này — kể cả thao tác trên màn hình. Không thể hoàn tác.'
                                    : 'Recording is paused. Confirming discards everything recorded after this moment — screen and audio both. Cannot be undone.'}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={confirmAutoPaceRevert}
                                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors"
                                >
                                    <RotateCcw size={11} /> {vn ? 'Quay lại, ghi tiếp' : 'Revert & keep recording'}
                                </button>
                                <button onClick={cancelAutoPaceRevert} className="px-4 py-2 text-xs font-bold rounded-xl bg-white/8 hover:bg-white/15 text-gray-400 transition-colors">
                                    {vn ? 'Hủy — tiếp tục ghi' : 'Cancel — keep going'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* AI Checkpoints panel — collapsible, top right */}
                {checkpointMode !== 'off' && segments.length > 0 && (
                    <div className="fixed top-4 right-4 bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 overflow-hidden" style={{ zIndex: showDrawing ? 700 : 500 }}>
                        {/* Minimized: single AI pill */}
                        {panelMinimized && (
                            <button
                                onClick={() => setPanelMinimized(false)}
                                className="flex items-center gap-1.5 px-3 py-2 hover:bg-white/5 transition-colors"
                            >
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">AI</span>
                                {segments.some(s => (s.status === 'needs_redo' || s.status === 'warning') && !s.overridden) && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                )}
                                <ChevronDown size={10} className="text-gray-500" />
                            </button>
                        )}
                        {/* Collapsed: compact chip row */}
                        {!panelMinimized && !showSegmentPanel && (
                            <div className="flex items-center">
                                <button
                                    onClick={() => setShowSegmentPanel(true)}
                                    className="flex items-center gap-1.5 px-3 py-2 hover:bg-white/5 transition-colors flex-1"
                                >
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">AI</span>
                                    <ChipStrip count={segments.length}>
                                        {segments.map(seg => {
                                            const isRedo = seg.status === 'needs_redo' && !seg.overridden;
                                            const isWarn = seg.status === 'warning' && !seg.overridden;
                                            const isGood = seg.status === 'good' || seg.overridden;
                                            const isChecking = seg.status === 'checking' || seg.status === 'uploading';
                                            const isRec = seg.status === 'recording';
                                            return (
                                                <span key={seg.index} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-mono font-bold ${isRedo ? 'bg-red-900/60 text-red-300' : isWarn ? 'bg-amber-900/60 text-amber-300' : isGood ? 'bg-emerald-900/40 text-emerald-300' : isChecking ? 'bg-indigo-900/40 text-indigo-300' : 'bg-white/5 text-gray-400'}`}>
                                                    {isRec && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block mr-0.5" />}
                                                    {isChecking ? '⏳' : isGood ? '✓' : isWarn ? '⚠' : isRedo ? '✗' : ''}
                                                    {seg.score != null ? seg.score : `S${seg.index + 1}`}
                                                </span>
                                            );
                                        })}
                                    </ChipStrip>
                                    <ChevronDown size={10} className="text-gray-500 ml-1 flex-shrink-0" />
                                </button>
                                <button
                                    onClick={() => setPanelMinimized(true)}
                                    className="px-2 py-2 hover:bg-white/5 transition-colors text-gray-600 hover:text-gray-400"
                                    title={vn ? 'Thu nhỏ' : 'Minimize'}
                                >
                                    <ChevronUp size={10} />
                                </button>
                            </div>
                        )}
                        {/* Expanded: full panel */}
                        {showSegmentPanel && (
                            <>
                                <button
                                    onClick={() => setShowSegmentPanel(false)}
                                    className="w-full px-4 py-2 border-b border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
                                >
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{vn ? 'AI Kiểm Tra' : 'AI Checkpoints'}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-gray-500">
                                            {checkpointMode === 'manual' ? (vn ? 'Thủ công' : 'Manual') : checkpointMode === 'hybrid' ? `${vn ? 'Kết hợp' : 'Hybrid'} · ${checkpointMins === 0.5 ? '30s' : `${checkpointMins}m`}` : `${vn ? 'Tự động' : 'Auto'} · ${checkpointMins === 0.5 ? '30s' : `${checkpointMins}m`}`}
                                        </span>
                                        <button
                                            onClick={e => { e.stopPropagation(); setShowChecksModal(true); }}
                                            className="text-gray-500 hover:text-white transition-colors"
                                            title={vn ? 'Mở rộng để đọc' : 'Open larger to read'}
                                        >
                                            <Maximize2 size={11} />
                                        </button>
                                        <ChevronUp size={10} className="text-gray-500" />
                                        <span className="w-px h-3 bg-white/10" />
                                        <button
                                            onClick={e => { e.stopPropagation(); setPanelMinimized(true); setShowSegmentPanel(false); }}
                                            className="text-gray-600 hover:text-gray-400 transition-colors"
                                            title={vn ? 'Thu nhỏ' : 'Minimize'}
                                        >
                                            <X size={10} />
                                        </button>
                                    </div>
                                </button>
                                <div className="divide-y divide-white/5 max-h-72 overflow-y-auto w-[320px] max-w-[45vw]">
                                    {segments.map(seg => {
                                        const isRecording = seg.status === 'recording';
                                        const isChecking = seg.status === 'checking' || seg.status === 'uploading';
                                        const isGood = seg.status === 'good';
                                        const isWarning = seg.status === 'warning';
                                        const isRedo = seg.status === 'needs_redo';
                                        const isSkipped = seg.status === 'skipped';
                                        const isOverridden = seg.overridden;
                                        const lowConfidence = seg.confidence != null && seg.confidence < 0.6;
                                        const isExpanded = expandedSegIdx === seg.index;
                                        return (
                                            <div key={seg.index} onClick={() => setExpandedSegIdx(isExpanded ? null : seg.index)} className={`px-4 py-2.5 cursor-pointer hover:bg-white/5 transition-colors ${isRedo && !isOverridden ? 'bg-red-950/30' : isWarning && !isOverridden ? 'bg-amber-950/20' : ''}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className="shrink-0">
                                                        {isRecording && <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse block" />}
                                                        {isChecking && <Loader2 size={12} className="text-indigo-400 animate-spin" />}
                                                        {(isGood || isOverridden) && <ShieldCheck size={13} className={isOverridden ? 'text-gray-500' : 'text-emerald-400'} />}
                                                        {isWarning && !isOverridden && <ShieldAlert size={13} className="text-amber-400" />}
                                                        {isRedo && !isOverridden && <ShieldX size={13} className="text-red-400" />}
                                                        {isSkipped && <span className="text-[9px] text-gray-600 font-mono">—</span>}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-[11px] font-bold text-gray-300">Seg {seg.index + 1}</span>
                                                            <span className="text-[10px] text-gray-500 font-mono">
                                                                {formatTime(seg.startSecs)}{seg.endSecs != null ? `–${formatTime(seg.endSecs)}` : '–...'}
                                                            </span>
                                                            {seg.score != null && (
                                                                <span className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded-full ${seg.score >= 80 ? 'bg-emerald-900/50 text-emerald-300' : seg.score >= 60 ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300'}`}>
                                                                    {seg.score}
                                                                </span>
                                                            )}
                                                            {lowConfidence && <span className="text-[9px] text-gray-500 font-mono">{vn ? 'tin cậy thấp' : 'low confidence'}</span>}
                                                            {isOverridden && <span className="text-[9px] text-gray-600 italic">{vn ? 'đã giữ' : 'kept'}</span>}
                                                            {isRecording && <span className="text-[10px] text-red-400 italic">{vn ? 'Đang quay...' : 'Recording...'}</span>}
                                                            {isChecking && <span className="text-[10px] text-indigo-400 italic">{vn ? 'Đang kiểm tra...' : 'Checking...'}</span>}
                                                        </div>
                                                        {seg.summary && (
                                                            <p className={`text-[10px] text-gray-400 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>{seg.summary}</p>
                                                        )}
                                                        {seg.whatWasRight && (
                                                            <p className={`text-[10px] text-emerald-300 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>✓ {seg.whatWasRight}</p>
                                                        )}
                                                        {seg.mainIssue && !isOverridden && (
                                                            <p className={`text-[10px] text-red-300 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>⚠ {seg.mainIssue}</p>
                                                        )}
                                                        {seg.suggestedCorrection && !isOverridden && (
                                                            <p className={`text-[10px] text-indigo-300 mt-0.5 italic ${isExpanded ? '' : 'line-clamp-2'}`}>💡 {seg.suggestedCorrection}</p>
                                                        )}
                                                    </div>
                                                    {(isRedo || isWarning) && !isOverridden && recordState === 'recording' && (
                                                        <div className="shrink-0 flex flex-col gap-1" onClick={e => e.stopPropagation()}>
                                                            <button
                                                                onClick={() => setRedoConfirm(seg.index)}
                                                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 transition-colors"
                                                            >
                                                                <RotateCcw size={9} /> {vn ? 'Làm lại' : 'Redo'}
                                                            </button>
                                                            <button
                                                                onClick={() => setSegments(prev => prev.map(s => s.index === seg.index ? { ...s, overridden: true } : s))}
                                                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-lg bg-white/5 hover:bg-white/10 text-gray-500 transition-colors"
                                                            >
                                                                {vn ? 'Giữ' : 'Keep'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Auto-Pace content-check panel — collapsible, top right. Independent of the AI
                    Checkpoints panel above (mutually exclusive: this only ever renders when Auto-Pace
                    is on, that one only when checkpointMode is on) but mirrors its exact position/
                    shape for a consistent feel. */}
                {autoPaceEnabled && speedChunks.some(c => autoPaceCheckDot(c)) && (
                    <div
                        ref={autoPacePanelRef}
                        className={`fixed ${autoPacePanelPos ? '' : 'top-4 right-4'} flex items-stretch bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/10 overflow-hidden`}
                        style={{
                            zIndex: showDrawing ? 700 : 500,
                            ...(autoPacePanelPos ? {
                                left: Math.min(autoPacePanelPos.x, Math.max(0, window.innerWidth - 80)),
                                top: Math.min(autoPacePanelPos.y, Math.max(0, window.innerHeight - 40)),
                            } : {}),
                        }}
                    >
                        <div
                            onPointerDown={startAutoPacePanelDrag}
                            onDoubleClick={resetAutoPacePanelPos}
                            title={vn ? 'Kéo để di chuyển · nhấp đúp để về góc' : 'Drag to move · double-click to reset'}
                            className="flex items-center px-0.5 cursor-move text-gray-600 hover:text-gray-300 hover:bg-white/5 touch-none select-none"
                        >
                            <GripVertical size={12} />
                        </div>
                        <div className="min-w-0">
                        {autoPaceCheckPanelMinimized && (
                            <button
                                onClick={() => setAutoPaceCheckPanelMinimized(false)}
                                className="flex items-center gap-1.5 px-3 py-2 hover:bg-white/5 transition-colors"
                            >
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">AI</span>
                                {speedChunks.some(c => (c.checks || []).some(chk => chk.status === 'needs_redo' || chk.status === 'warning')) && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                )}
                                <ChevronDown size={10} className="text-gray-500" />
                            </button>
                        )}
                        {!autoPaceCheckPanelMinimized && !showAutoPaceCheckPanel && (
                            <div className="flex items-center">
                                <button
                                    onClick={() => setShowAutoPaceCheckPanel(true)}
                                    className="flex items-center gap-1.5 px-3 py-2 hover:bg-white/5 transition-colors flex-1"
                                >
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">AI</span>
                                    <ChipStrip count={speedChunks.filter(c => autoPaceCheckDot(c)).length}>
                                        {speedChunks.filter(c => autoPaceCheckDot(c)).map(c => {
                                            const worst = worstAutoPaceCheck(c.checks);
                                            const isChecking = c.checking;
                                            const chipClass = worst?.status === 'needs_redo' ? 'bg-red-900/60 text-red-300'
                                                : worst?.status === 'warning' ? 'bg-amber-900/60 text-amber-300'
                                                : worst?.status === 'good' ? 'bg-emerald-900/40 text-emerald-300'
                                                : 'bg-indigo-900/40 text-indigo-300';
                                            return (
                                                <span key={c.index} title={autoPaceCheckDot(c)?.title} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-mono font-bold ${chipClass}`}>
                                                    {isChecking ? '⏳' : worst?.status === 'good' ? '✓' : worst?.status === 'warning' ? '⚠' : worst?.status === 'needs_redo' ? '✗' : ''}
                                                    {worst?.score != null ? worst.score : `#${c.index + 1}`}
                                                </span>
                                            );
                                        })}
                                    </ChipStrip>
                                    <ChevronDown size={10} className="text-gray-500 ml-1 flex-shrink-0" />
                                </button>
                                <button
                                    onClick={() => setAutoPaceCheckPanelMinimized(true)}
                                    className="px-2 py-2 hover:bg-white/5 transition-colors text-gray-600 hover:text-gray-400"
                                    title={vn ? 'Thu nhỏ' : 'Minimize'}
                                >
                                    <ChevronUp size={10} />
                                </button>
                            </div>
                        )}
                        {showAutoPaceCheckPanel && (
                            <>
                                <button
                                    onClick={() => setShowAutoPaceCheckPanel(false)}
                                    className="w-full px-4 py-2 border-b border-white/10 flex items-center justify-between hover:bg-white/5 transition-colors"
                                >
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{vn ? 'AI Kiểm Tra' : 'AI Check'}</span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={e => { e.stopPropagation(); setShowChecksModal(true); }}
                                            className="text-gray-500 hover:text-white transition-colors"
                                            title={vn ? 'Mở rộng để đọc' : 'Open larger to read'}
                                        >
                                            <Maximize2 size={11} />
                                        </button>
                                        <ChevronUp size={10} className="text-gray-500" />
                                        <span className="w-px h-3 bg-white/10" />
                                        <button
                                            onClick={e => { e.stopPropagation(); setAutoPaceCheckPanelMinimized(true); setShowAutoPaceCheckPanel(false); }}
                                            className="text-gray-600 hover:text-gray-400 transition-colors"
                                            title={vn ? 'Thu nhỏ' : 'Minimize'}
                                        >
                                            <X size={10} />
                                        </button>
                                    </div>
                                </button>
                                <div className="divide-y divide-white/5 max-h-72 overflow-y-auto w-[320px] max-w-[45vw]">
                                    {speedChunks.filter(c => autoPaceCheckDot(c)).slice().reverse().map(c => {
                                        const worst = worstAutoPaceCheck(c.checks);
                                        const isExpanded = expandedAutoPaceCheckIdx === c.index;
                                        return (
                                            <div
                                                key={c.index}
                                                onClick={() => setExpandedAutoPaceCheckIdx(isExpanded ? null : c.index)}
                                                className={`px-4 py-2.5 cursor-pointer hover:bg-white/5 transition-colors ${worst?.status === 'needs_redo' ? 'bg-red-950/30' : worst?.status === 'warning' ? 'bg-amber-950/20' : ''}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="shrink-0">
                                                        {c.checking && <Loader2 size={12} className="text-indigo-400 animate-spin" />}
                                                        {!c.checking && worst?.status === 'good' && <ShieldCheck size={13} className="text-emerald-400" />}
                                                        {!c.checking && worst?.status === 'warning' && <ShieldAlert size={13} className="text-amber-400" />}
                                                        {!c.checking && worst?.status === 'needs_redo' && <ShieldX size={13} className="text-red-400" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-[11px] font-bold text-gray-300">#{c.index + 1}</span>
                                                            <span className="text-[10px] text-gray-500 font-mono">
                                                                {formatTime(Math.round(c.startMs / 1000))}–{formatTime(Math.round(c.endMs / 1000))}
                                                            </span>
                                                            {worst?.score != null && (
                                                                <span className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded-full ${worst.score >= 80 ? 'bg-emerald-900/50 text-emerald-300' : worst.score >= 60 ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300'}`}>
                                                                    {worst.score}
                                                                </span>
                                                            )}
                                                            {c.checking && <span className="text-[10px] text-indigo-400 italic">{vn ? 'Đang kiểm tra...' : 'Checking...'}</span>}
                                                            {worst && (worst.status === 'warning' || worst.status === 'needs_redo') && recordState === 'recording' && (
                                                                <button
                                                                    onClick={e => { e.stopPropagation(); requestAutoPaceRevert(c.index, worst.startMs); }}
                                                                    title={vn ? 'Tạm dừng và quay lại đúng trước câu này' : 'Pause and revert to right before this sentence'}
                                                                    className="ml-auto flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-red-500/20 hover:bg-red-500/40 text-red-300 transition-colors flex-shrink-0"
                                                                >
                                                                    <RotateCcw size={10} /> {vn ? 'Quay lại' : 'Revert'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {worst?.summary && (
                                                            <p className={`text-[10px] text-gray-400 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>{worst.summary}</p>
                                                        )}
                                                        {worst?.whatWasRight && (
                                                            <p className={`text-[10px] text-emerald-300 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>✓ {worst.whatWasRight}</p>
                                                        )}
                                                        {worst?.mainIssue && (
                                                            <p className={`text-[10px] text-red-300 mt-0.5 ${isExpanded ? '' : 'line-clamp-2'}`}>⚠ {worst.mainIssue}</p>
                                                        )}
                                                        {worst?.suggestion && (
                                                            <p className={`text-[10px] text-indigo-300 mt-0.5 italic ${isExpanded ? '' : 'line-clamp-2'}`}>💡 {worst.suggestion}</p>
                                                        )}
                                                        {isExpanded && (c.checks?.length ?? 0) > 1 && (
                                                            <div className="mt-1.5 pt-1.5 border-t border-white/5 space-y-1">
                                                                <p className="text-[9px] text-gray-500 uppercase tracking-wider">{vn ? `${c.checks!.length} cửa sổ kiểm tra` : `${c.checks!.length} check windows`}</p>
                                                                {c.checks!.map((chk, i) => (
                                                                    <p key={i} className="text-[10px] text-gray-400">
                                                                        {formatTime(Math.round(chk.startMs / 1000))}–{formatTime(Math.round(chk.endMs / 1000))}
                                                                        {chk.score != null && <span className="ml-1 font-mono">· {chk.score}</span>}
                                                                        {chk.mainIssue && <span className="block text-red-300">⚠ {chk.mainIssue}</span>}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        </div>
                    </div>
                )}
                {/* Large reading view of every AI check — recording keeps running underneath. */}
                {showChecksModal && (
                    <div className="fixed inset-0 z-[800] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowChecksModal(false)}>
                        <div className="w-[780px] max-w-full max-h-[88vh] bg-gray-900 rounded-2xl border border-white/10 shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/10 flex-shrink-0">
                                <h3 className="text-sm font-bold text-white flex-1">{vn ? 'AI Kiểm Tra — chi tiết' : 'AI Check — details'}</h3>
                                <span className="text-[11px] text-gray-400 hidden sm:block">
                                    <span className="text-emerald-400">✓ {checkItems.filter(i => i.status === 'good').length}</span>
                                    {' · '}<span className="text-amber-400">⚠ {checkItems.filter(i => i.status === 'warning').length}</span>
                                    {' · '}<span className="text-red-400">✗ {checkItems.filter(i => i.status === 'needs_redo').length}</span>
                                </span>
                                {recordState === 'recording' && <span className="text-[11px] text-red-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />{vn ? 'Vẫn đang quay' : 'Still recording'}</span>}
                                <button onClick={() => setShowChecksModal(false)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400"><X size={15} /></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                {checkItems.length === 0 && <p className="text-sm text-gray-500 text-center py-8">{vn ? 'Chưa có kết quả kiểm tra nào.' : 'No check results yet.'}</p>}
                                {checkItems.map(it => {
                                    const tone = it.status === 'good' || it.overridden ? 'border-emerald-500/30 bg-emerald-950/20'
                                        : it.status === 'warning' ? 'border-amber-500/40 bg-amber-950/20'
                                        : it.status === 'needs_redo' ? 'border-red-500/40 bg-red-950/25'
                                        : 'border-white/10 bg-white/5';
                                    const scoreCls = (n: number) => n >= 80 ? 'bg-emerald-900/50 text-emerald-300' : n >= 60 ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300';
                                    return (
                                        <div key={it.key} className={`rounded-xl border p-4 ${tone}`}>
                                            <div className="flex items-center gap-2.5 flex-wrap">
                                                {it.status === 'checking' && <Loader2 size={14} className="text-indigo-400 animate-spin" />}
                                                {it.status === 'recording' && <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />}
                                                {(it.status === 'good' || it.overridden) && <ShieldCheck size={15} className="text-emerald-400" />}
                                                {it.status === 'warning' && !it.overridden && <ShieldAlert size={15} className="text-amber-400" />}
                                                {it.status === 'needs_redo' && !it.overridden && <ShieldX size={15} className="text-red-400" />}
                                                <span className="text-sm font-bold text-gray-100">{it.label}</span>
                                                <span className="text-xs text-gray-500 font-mono">{it.range}</span>
                                                {it.score != null && <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded-full ${scoreCls(it.score)}`}>{vn ? 'Đúng' : 'Correct'} {it.score}</span>}
                                                {it.clarityScore != null && <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-white/10 text-gray-300">{vn ? 'Rõ' : 'Clear'} {it.clarityScore}</span>}
                                                {it.overridden && <span className="text-[11px] text-gray-500 italic">{vn ? 'đã giữ' : 'kept'}</span>}
                                                {it.status === 'checking' && <span className="text-xs text-indigo-400 italic">{vn ? 'Đang kiểm tra...' : 'Checking...'}</span>}
                                                {it.segIndex != null && (it.status === 'warning' || it.status === 'needs_redo') && !it.overridden && recordState === 'recording' && (
                                                    <div className="ml-auto flex gap-2">
                                                        <button onClick={() => { setShowChecksModal(false); setRedoConfirm(it.segIndex!); }} className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 transition-colors"><RotateCcw size={11} /> {vn ? 'Làm lại' : 'Redo'}</button>
                                                        <button onClick={() => setSegments(prev => prev.map(s => s.index === it.segIndex ? { ...s, overridden: true } : s))} className="px-2.5 py-1 text-xs font-bold rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 transition-colors">{vn ? 'Giữ' : 'Keep'}</button>
                                                    </div>
                                                )}
                                                {it.chunkIndex != null && it.revertMs != null && (it.status === 'warning' || it.status === 'needs_redo') && recordState === 'recording' && (
                                                    <div className="ml-auto">
                                                        <button
                                                            onClick={() => { setShowChecksModal(false); requestAutoPaceRevert(it.chunkIndex!, it.revertMs!); }}
                                                            title={vn ? 'Tạm dừng và quay lại đúng trước câu này' : 'Pause and revert to right before this sentence'}
                                                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 transition-colors"
                                                        >
                                                            <RotateCcw size={11} /> {vn ? 'Quay lại' : 'Revert'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            {it.summary && <p className="text-[13px] text-gray-300 leading-relaxed mt-2.5">{it.summary}</p>}
                                            {it.whatWasRight && <p className="text-[13px] text-emerald-300 leading-relaxed mt-2">✓ {it.whatWasRight}</p>}
                                            {it.mainIssue && !it.overridden && <p className="text-[13px] text-red-300 leading-relaxed mt-2">⚠ {it.mainIssue}</p>}
                                            {it.suggestion && !it.overridden && <p className="text-[13px] text-indigo-300 italic leading-relaxed mt-2">💡 {it.suggestion}</p>}
                                            {it.transcript && (
                                                <details className="mt-2.5">
                                                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300">{vn ? 'Bản chép lời' : 'Transcript'}</summary>
                                                    <p className="text-xs text-gray-400 leading-relaxed mt-1.5 whitespace-pre-wrap">{it.transcript}</p>
                                                </details>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // Minimized corner pill during a long export/upload wait — the component stays mounted (no
    // backdrop/dialog rendered at all here), so the canvas underneath is fully clickable again.
    const isLongWait = exportPhase === 'pending' || exportPhase === 'processing' || exportPhase === 'uploading' || stage === 'uploading';
    if (modalMinimized && isLongWait) {
        const minimizedLabel = exportPhase === 'uploading' ? (vn ? `Đang tải lên ${exportProgressPct}%` : `Uploading ${exportProgressPct}%`)
            : exportPhase === 'pending' ? (vn ? 'Đang khởi động...' : 'Starting...')
            : exportPhase === 'processing' ? processingLabel()
            : progress < 95 ? (vn ? 'Đang upload...' : 'Uploading...')
            : (vn ? 'Đang xử lý trên YouTube...' : 'Processing on YouTube...');
        return (
            <button
                onClick={() => { setModalMinimized(false); onRequestForeground?.(); }}
                style={{ bottom: 20 + pillIndex * 68 }}
                className="fixed left-5 z-[401] flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-800 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.25)] border border-gray-100 dark:border-slate-700 hover:shadow-[0_16px_48px_rgba(0,0,0,0.3)] transition-shadow text-left"
            >
                <Loader2 size={18} className="animate-spin text-red-500 flex-shrink-0" />
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate max-w-[180px]">{clipOfJobId ? `✂ ${initialTitle || title}` : (node.title || 'Untitled')}</p>
                    <p className="text-[11px] text-gray-400">{minimizedLabel}</p>
                </div>
            </button>
        );
    }

    // ── Main modal ─────────────────────────────────────────────────────────────
    return (
        <>
            {/* Webcam preview (pick stage only, when useWebcam enabled) */}
            {camPreviewStream && stage === 'pick' && (
                <video
                    ref={el => {
                        camPreviewRef.current = el;
                        if (el && el.srcObject !== camPreviewStream) el.srcObject = camPreviewStream;
                    }}
                    autoPlay
                    muted
                    playsInline
                    className={`fixed bottom-5 right-5 object-cover border-2 border-white/25 shadow-2xl z-[401] ${
                        webcamShape === 'circle' ? 'w-[99px] h-[99px] rounded-full' : 'w-44 h-[99px] rounded-2xl'
                    }`}
                    style={{ transform: 'scaleX(-1)' }}
                    title={vn ? 'Preview webcam — kéo thả để di chuyển' : 'Webcam preview'}
                />
            )}

            {showThumbEditor && (
                <ThumbnailEditorModal
                    title={node.title || 'StudyBoard'}
                    notebookTitle={notebook.title || ''}
                    noteImages={noteImages}
                    notebookId={notebook.id}
                    language={language}
                    noteContent={node.content}
                    noteImageBase64={node.imageBase64}
                    noteImageMimeType={node.imageMimeType}
                    onClose={() => setShowThumbEditor(false)}
                    onThumbnailChange={setThumbDataUrl}
                />
            )}
            <div className="fixed inset-0 z-[399] bg-black/50 backdrop-blur-sm" onClick={stage === 'uploading' ? undefined : requestClose} />
            <div className={`fixed z-[400] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.4)] border border-gray-100 dark:border-slate-700 max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 ${stage === 'review' || stage === 'chunk-review' || stage === 'timeline-review' ? 'w-[920px] max-w-[95vw]' : 'w-[480px] max-w-[96vw]'}`}>

                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                    <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
                        <Youtube size={18} className="text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-bold text-gray-900 dark:text-white text-base">
                            {vn ? 'Upload lên YouTube' : 'Upload to YouTube'}
                        </h2>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{node.title || 'Untitled'}</p>
                    </div>
                    {isLongWait && (
                        <button onClick={() => setModalMinimized(true)} title={vn ? 'Thu nhỏ' : 'Minimize'} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                            <ChevronDown size={16} className="text-gray-400" />
                        </button>
                    )}
                    {stage !== 'uploading' && (
                        <button onClick={requestClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                            <X size={16} className="text-gray-400" />
                        </button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">

                    {/* ── Pick stage ── */}
                    {/* Recovery banner — shown when a previous recording was saved to IndexedDB */}
                    {/* Processed version on the server takes priority over the raw local backup. */}
                    {serverJob && stage === 'pick' && clips.length === 0 && recordState === 'idle' && (() => {
                        const phases: Record<string, [string, string]> = {
                            downloading: ['đang tải video gốc', 'downloading'], normalizing: ['chuẩn hoá', 'normalizing'],
                            cutting: ['cắt đoạn', 'cutting'], merging: ['ghép', 'merging'], uploading: ['lưu kết quả', 'saving'],
                        };
                        const done = serverJob.status === 'done';
                        const pct = !done && typeof serverJob.progress === 'number' ? serverJob.progress : null;
                        return (
                            <div className="mx-5 mt-4 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl">
                                <div className="flex items-center gap-3">
                                    {done ? <CheckCircle2 size={15} className="text-indigo-500 flex-shrink-0" /> : <Loader2 size={15} className="text-indigo-500 animate-spin flex-shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                                            {done ? (vn ? 'Bản đã xử lý (Auto-Pace) sẵn sàng để upload' : 'Processed (Auto-Pace) version is ready to upload')
                                                : serverJob.status === 'pending' ? (vn ? 'Đang khởi động máy xử lý…' : 'Starting the worker…')
                                                : `${vn ? 'Video đang được xử lý trên server' : 'Video is being processed on the server'}${pct != null ? ` — ${pct}%${serverJob.phase && phases[serverJob.phase] ? ` · ${phases[serverJob.phase][vn ? 0 : 1]}` : ''}` : ''}`}
                                        </p>
                                        {!done && <p className="text-[11px] text-indigo-500/80">{vn ? 'Xong sẽ hiện nút mở bản đã xử lý ngay tại đây.' : 'An "Open processed version" button appears here when it finishes.'}</p>}
                                    </div>
                                    {done && (
                                        <button onClick={openProcessedFromServer} disabled={openingServerJob}
                                            className="px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white text-xs font-bold rounded-lg flex-shrink-0 flex items-center gap-1">
                                            {openingServerJob && <Loader2 size={11} className="animate-spin" />}
                                            {vn ? 'Mở bản đã xử lý' : 'Open processed version'}
                                        </button>
                                    )}
                                </div>
                                {pct != null && (
                                    <div className="mt-2 h-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 overflow-hidden">
                                        <div className="h-full bg-indigo-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    {recoveredFile && stage === 'pick' && (
                        <div className="mx-5 mt-4 flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                            <AlertCircle size={15} className="text-amber-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                                    {serverJob ? (vn ? 'Bản ghi thô (chưa tăng tốc / chưa cắt)' : 'Raw recording (not sped up / not cut)') : (vn ? '🔄 Tìm thấy video chưa upload' : '🔄 Unfinished recording found')}
                                </p>
                                <p className="text-xs text-amber-600 dark:text-amber-400 truncate">{recoveredFile.name}</p>
                            </div>
                            <button
                                onClick={() => { setClips([{ id: Date.now(), blob: recoveredFile, durationSecs: 0, name: recoveredFile.name }]); setFile(recoveredFile); setStage('review'); setRecoveredFile(null); }}
                                className={serverJob
                                    ? 'px-3 py-1.5 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-xs font-semibold rounded-lg flex-shrink-0'
                                    : 'px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg flex-shrink-0'}
                            >
                                {serverJob ? (vn ? 'Dùng bản thô' : 'Use raw') : (vn ? 'Khôi phục' : 'Restore')}
                            </button>
                            <button onClick={() => { idbDeleteRecording(); setRecoveredFile(null); }} className="text-xs text-amber-400 hover:text-amber-600 flex-shrink-0">
                                {vn ? 'Bỏ' : 'Dismiss'}
                            </button>
                        </div>
                    )}

                    {stage === 'pick' && (
                        <div className="p-5 flex flex-col gap-4">
                            {!ytConnected ? (
                                <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl">
                                    <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
                                    <p className="text-sm text-amber-700 dark:text-amber-300 flex-1">
                                        {vn ? 'Cần kết nối YouTube trước' : 'Connect YouTube account first'}
                                    </p>
                                    <button onClick={() => {
                                        connectYouTube();
                                        setTimeout(async () => {
                                            const ok = isYouTubeConnected();
                                            setYtConnected(ok);
                                            if (ok) getYouTubeChannel().then(ch => ch && setYtChannel(ch));
                                        }, 3000);
                                    }} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-colors">
                                        {vn ? 'Kết nối' : 'Connect'}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-xl">
                                    {ytChannel?.thumbnail
                                        ? <img src={ytChannel.thumbnail} className="w-7 h-7 rounded-full flex-shrink-0" alt="" />
                                        : <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />
                                    }
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-green-700 dark:text-green-300 truncate">
                                            {ytChannel?.name ?? (vn ? 'Đã kết nối YouTube' : 'YouTube connected')}
                                        </p>
                                        <p className="text-[11px] text-green-600/70 dark:text-green-400/60">{vn ? 'Sẵn sàng upload' : 'Ready to upload'}</p>
                                    </div>
                                    <button onClick={() => { connectYouTube(); }} className="text-[11px] text-green-600 dark:text-green-400 hover:underline">
                                        {vn ? 'Đổi' : 'Switch'}
                                    </button>
                                </div>
                            )}

                            {/* The drawing board held strokes not yet saved to the note when recording stopped. */}
                            {unsavedDrawing && drawingStashRef.current?.dataUrl && (
                                <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10">
                                    <img src={drawingStashRef.current.dataUrl} alt="" className="w-14 h-9 object-contain rounded bg-slate-800 flex-shrink-0" />
                                    <p className="flex-1 text-[12px] text-amber-800 dark:text-amber-200">
                                        {vn ? 'Bảng vẽ còn hình chưa lưu vào note. Lần quay sau hình vẫn còn trên bảng.' : 'The drawing board has strokes not saved to the note. They will still be on the board next recording.'}
                                    </p>
                                    <button
                                        onClick={async () => {
                                            const kept = drawingStashRef.current;
                                            if (!kept?.dataUrl) return;
                                            setSavingStashedDrawing(true);
                                            const ok = await handleSaveDrawingToNote(kept.dataUrl, kept.json || undefined);
                                            setSavingStashedDrawing(false);
                                            if (ok) { if (drawingStashRef.current) drawingStashRef.current.dirty = false; setUnsavedDrawing(false); }
                                        }}
                                        disabled={savingStashedDrawing}
                                        className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white flex-shrink-0"
                                    >
                                        {savingStashedDrawing ? (vn ? 'Đang lưu…' : 'Saving…') : (vn ? 'Lưu vào note' : 'Save to note')}
                                    </button>
                                    <button onClick={() => setUnsavedDrawing(false)} className="text-[12px] text-amber-700 dark:text-amber-300 hover:underline flex-shrink-0">
                                        {vn ? 'Để sau' : 'Later'}
                                    </button>
                                </div>
                            )}

                            {/* Continue a video: earlier videos placed in front of this recording */}
                            {(seriesParts.length > 0 || seriesCandidates.length > 0) && (
                                <div className="rounded-xl border border-teal-200 dark:border-teal-700/50 bg-teal-50/60 dark:bg-teal-900/10 p-3 flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <ListVideo size={14} className="text-teal-600 dark:text-teal-400 flex-shrink-0" />
                                        <p className="flex-1 text-xs font-bold text-teal-800 dark:text-teal-200">
                                            {vn ? 'Quay tiếp video trước' : 'Continue an earlier video'}
                                        </p>
                                        {exportPhase === 'idle' || exportPhase === 'error' ? (
                                            <button onClick={() => { setSeriesSearch(''); setShowSeriesPicker(v => !v); }}
                                                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-800/30">
                                                <Plus size={11} /> {vn ? 'Chọn video' : 'Add video'}
                                            </button>
                                        ) : null}
                                    </div>
                                    {seriesParts.length === 0 ? (
                                        <p className="text-[11px] text-teal-700/80 dark:text-teal-300/70">
                                            {vn ? 'Chọn video đã quay hôm trước. Quay xong, app ghép các video đó với phần mới thành một video (video cũ không bị thay đổi).'
                                                : 'Pick videos you recorded before. When you finish, the app joins them with the new part into one video (the originals are not changed).'}
                                        </p>
                                    ) : (
                                        <div className="flex flex-col gap-1.5">
                                            {seriesParts.map((p, i) => {
                                                const busyExport = !(exportPhase === 'idle' || exportPhase === 'error');
                                                const trimmed = p.startMs > 0 || p.endMs != null;
                                                return (
                                                    <div key={p.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-teal-100 dark:border-slate-700">
                                                        <span className="w-5 text-center text-[11px] font-bold text-teal-600 dark:text-teal-400 flex-shrink-0">{i + 1}</span>
                                                        <img src={`https://i.ytimg.com/vi/${p.videoId}/mqdefault.jpg`} alt="" className="w-14 h-8 object-cover rounded bg-black flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[12px] font-medium text-gray-700 dark:text-gray-200 truncate">{p.title}</p>
                                                            <p className="text-[10px] text-gray-400">
                                                                {trimmed
                                                                    ? `${vn ? 'Giữ' : 'Keeping'} ${formatTime(Math.round(p.startMs / 1000))}–${p.endMs != null ? formatTime(Math.round(p.endMs / 1000)) : (vn ? 'hết' : 'end')}`
                                                                    : (vn ? 'Cả video' : 'Whole video')}
                                                            </p>
                                                        </div>
                                                        {!busyExport && (<>
                                                            <button onClick={() => moveSeriesPart(i, -1)} disabled={i === 0} title={vn ? 'Lên trước' : 'Move up'}
                                                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronUp size={13} className="text-gray-500" /></button>
                                                            <button onClick={() => moveSeriesPart(i, 1)} disabled={i === seriesParts.length - 1} title={vn ? 'Xuống sau' : 'Move down'}
                                                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronDown size={13} className="text-gray-500" /></button>
                                                            <button onClick={() => setSeriesTrimKey(p.key)} title={vn ? 'Cắt bớt đầu/cuối' : 'Trim start/end'}
                                                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700"><Scissors size={13} className={trimmed ? 'text-teal-600' : 'text-gray-500'} /></button>
                                                            <button onClick={() => setSeriesParts(prev => prev.filter(x => x.key !== p.key))} title={vn ? 'Bỏ khỏi chuỗi (video gốc vẫn còn)' : 'Remove from the series (the original stays)'}
                                                                className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 group"><X size={13} className="text-gray-400 group-hover:text-red-500" /></button>
                                                        </>)}
                                                    </div>
                                                );
                                            })}
                                            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-dashed border-teal-300 dark:border-teal-700">
                                                <span className="w-5 text-center text-[11px] font-bold text-red-500 flex-shrink-0">{seriesParts.length + 1}</span>
                                                <Circle size={12} className="text-red-500 fill-red-500 flex-shrink-0" />
                                                <p className="text-[12px] text-gray-600 dark:text-gray-300">
                                                    {clips.length > 0 ? (vn ? 'Phần vừa quay' : 'The part you just recorded') : (vn ? 'Phần bạn sắp quay' : 'The part you are about to record')}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {showSeriesPicker && (
                                        <div className="flex flex-col gap-1.5 pt-1">
                                            <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600">
                                                <Search size={12} className="text-gray-400" />
                                                <input autoFocus value={seriesSearch} onChange={e => setSeriesSearch(e.target.value)}
                                                    placeholder={vn ? 'Tìm video…' : 'Search videos…'}
                                                    className="flex-1 bg-transparent text-[12px] outline-none text-gray-700 dark:text-gray-200" />
                                            </div>
                                            <div className="max-h-56 overflow-y-auto flex flex-col gap-1">
                                                {seriesCandidates.length === 0 && (
                                                    <p className="text-[11px] text-gray-500 px-1 py-2">{vn ? 'Không còn video nào có bản sao lưu để ghép.' : 'No other videos with a backup copy to join.'}</p>
                                                )}
                                                {seriesCandidates.slice(0, 40).map(({ node: n, video: v }) => (
                                                    <button key={`${n.id}::${v.youtubeVideoId}`} onClick={() => addSeriesPart(n, v)}
                                                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-white dark:hover:bg-slate-800">
                                                        <img src={`https://i.ytimg.com/vi/${v.youtubeVideoId}/mqdefault.jpg`} alt="" className="w-14 h-8 object-cover rounded bg-black flex-shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[12px] text-gray-700 dark:text-gray-200 truncate">{v.youtubeTitle || v.videoTitle || n.title}</p>
                                                            <p className="text-[10px] text-gray-400 truncate">
                                                                {n.id === node.id ? (vn ? 'Note này' : 'This note') : n.title}
                                                                {v.youtubeUploadedAt ? ` · ${new Date(v.youtubeUploadedAt).toLocaleDateString(vn ? 'vi-VN' : 'en-US')}` : ''}
                                                            </p>
                                                        </div>
                                                        <Plus size={13} className="text-teal-600 flex-shrink-0" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {seriesParts.length > 0 && clips.length > 1 && (
                                        <p className="text-[11px] text-amber-700 dark:text-amber-300">
                                            {vn ? 'Chỉ ghép được với một bản quay. Hãy xoá bớt clip để còn một.' : 'Joining works with one recording. Remove clips until one is left.'}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Continue recording after Stop: the recording so far (cut at a chosen moment) goes in front of the next one */}
                            {((clips.length === 1 && recordState === 'idle') || resumeParts.length > 0) && (exportPhase === 'idle' || exportPhase === 'error') && (() => {
                                const totalMs = clips.length === 1 ? lastRecordingMs() : 0;
                                const canContinue = clips.length === 1 && recordState === 'idle' && totalMs >= 1000;
                                return (
                                    <div className="rounded-xl border border-indigo-200 dark:border-indigo-700/50 bg-indigo-50/60 dark:bg-indigo-900/10 p-3 flex flex-col gap-2">
                                        <div className="flex items-center gap-2">
                                            <History size={14} className="text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                                            <p className="flex-1 text-xs font-bold text-indigo-800 dark:text-indigo-200">
                                                {vn ? 'Lỡ bấm dừng? Quay tiếp video này' : 'Stopped by mistake? Continue this video'}
                                            </p>
                                            {canContinue && resumePickMs === null && (<>
                                                <button onClick={() => setResumePickMs(totalMs)}
                                                    className="px-2 py-1 text-[11px] font-semibold rounded-lg text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/30">
                                                    {vn ? 'Chọn thời điểm' : 'Pick a moment'}
                                                </button>
                                                <button onClick={() => continueRecording(totalMs)}
                                                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg bg-red-500 hover:bg-red-600 text-white">
                                                    <Circle size={9} className="fill-white" /> {vn ? 'Quay tiếp' : 'Continue'}
                                                </button>
                                            </>)}
                                        </div>
                                        {resumeParts.length > 0 && (
                                            <div className="flex flex-col gap-1">
                                                {resumeParts.map((p, i) => (
                                                    <div key={p.jobId} className="flex items-center gap-2 px-2 py-1 rounded-lg bg-white dark:bg-slate-800 border border-indigo-100 dark:border-slate-700">
                                                        <span className="w-5 text-center text-[11px] font-bold text-indigo-600 dark:text-indigo-400">{seriesParts.length + i + 1}</span>
                                                        <p className="flex-1 text-[12px] text-gray-700 dark:text-gray-200">
                                                            {vn ? `Phần quay trước · giữ 0:00–${formatTime(Math.round(p.keptMs / 1000))}` : `Earlier recording · keeping 0:00–${formatTime(Math.round(p.keptMs / 1000))}`}
                                                            <span className="text-[10px] text-gray-400"> · {p.uploaded ? (vn ? 'đã lưu' : 'saved') : (vn ? 'đang tải lên…' : 'uploading…')}</span>
                                                        </p>
                                                        {recordState === 'idle' && (
                                                            <button onClick={() => setResumeParts(prev => prev.filter(x => x.jobId !== p.jobId))} title={vn ? 'Bỏ phần này khỏi video' : 'Leave this part out'}
                                                                className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 group"><X size={13} className="text-gray-400 group-hover:text-red-500" /></button>
                                                        )}
                                                    </div>
                                                ))}
                                                <p className="text-[11px] text-indigo-700/80 dark:text-indigo-300/70">
                                                    {clips.length > 0
                                                        ? (vn ? 'Khi xuất, các phần này được nối vào trước phần vừa quay.' : 'On export these go in front of the part you just recorded.')
                                                        : (vn ? 'Bấm Quay màn hình để quay phần tiếp theo; khi xuất, các phần này được nối vào trước.' : 'Record the next part; on export these go in front of it.')}
                                                </p>
                                            </div>
                                        )}
                                        {canContinue && resumePickMs === null && resumeParts.length === 0 && (
                                            <p className="text-[11px] text-indigo-700/80 dark:text-indigo-300/70">
                                                {vn ? 'Quay thêm vào cuối video, hoặc chọn một thời điểm để quay lại từ đó (đoạn sau thời điểm đó bị bỏ). Khi xuất, app tự nối các phần thành một video.'
                                                    : 'Record more at the end, or pick a moment to redo from there (what follows it is dropped). On export the parts are joined into one video.'}
                                            </p>
                                        )}
                                        {canContinue && resumePickMs !== null && (
                                            <div className="flex flex-col gap-2">
                                                {resumePreviewUrl && (
                                                    <video ref={resumePreviewRef} src={resumePreviewUrl} muted preload="auto"
                                                        onLoadedData={e => { e.currentTarget.currentTime = resumePickMs / 1000; }}
                                                        className="w-full rounded-lg bg-black aspect-video" />
                                                )}
                                                <input type="range" min={0} max={totalMs} step={500} value={Math.min(resumePickMs, totalMs)}
                                                    onChange={e => {
                                                        const ms = Number(e.target.value);
                                                        setResumePickMs(ms);
                                                        if (resumePreviewRef.current) resumePreviewRef.current.currentTime = ms / 1000;
                                                    }}
                                                    className="w-full accent-indigo-600" aria-label={vn ? 'Thời điểm quay tiếp' : 'Moment to continue from'} />
                                                <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                                    <span>{vn ? 'Giữ' : 'Keep'} 0:00–{formatTime(Math.round(resumePickMs / 1000))}</span>
                                                    <span>{vn ? 'Bỏ' : 'Drop'} {formatTime(Math.round((totalMs - resumePickMs) / 1000))}</span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => setResumePickMs(null)}
                                                        className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-[12px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                                                        {vn ? 'Huỷ' : 'Cancel'}
                                                    </button>
                                                    <button onClick={() => continueRecording(resumePickMs)} disabled={resumePickMs < 1000}
                                                        className="flex-[2] flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-[12px] font-bold">
                                                        <Circle size={10} className="fill-white" /> {vn ? `Quay tiếp từ ${formatTime(Math.round(resumePickMs / 1000))}` : `Continue from ${formatTime(Math.round(resumePickMs / 1000))}`}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Clip list */}
                            {clips.length > 0 && (
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {vn ? `${clips.length} clip · Tổng ${formatTime(totalSecs)}` : `${clips.length} clip${clips.length > 1 ? 's' : ''} · Total ${formatTime(totalSecs)}`}
                                        </p>
                                    </div>
                                    {clips.map((clip, i) => (
                                        <div key={clip.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-600">
                                            <div className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                                <span className="text-xs font-bold text-red-600 dark:text-red-400">{i + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{clip.name}</p>
                                                <p className="text-xs text-gray-400">{(clip.blob.size / 1024 / 1024).toFixed(1)} MB</p>
                                            </div>
                                            <button onClick={() => removeClip(clip.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors group">
                                                <Trash2 size={13} className="text-gray-400 group-hover:text-red-500" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Device settings panel */}
                            <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
                                {/* Header row */}
                                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800">
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{vn ? 'Thiết bị ghi âm/hình' : 'Recording devices'}</span>
                                    <button
                                        onClick={async () => {
                                            const next = !showDevicePanel;
                                            setShowDevicePanel(next);
                                            if (next && cameras.length === 0) await enumerateDevices();
                                        }}
                                        className="text-xs text-indigo-500 hover:text-indigo-700 font-medium"
                                    >
                                        {showDevicePanel ? (vn ? 'Thu gọn' : 'Collapse') : (vn ? 'Chỉnh sửa' : 'Configure')}
                                    </button>
                                </div>

                                {/* Expanded device selectors */}
                                {showDevicePanel && (
                                    <div className="px-4 py-3 space-y-3 border-t border-gray-100 dark:border-slate-700">
                                        {/* Microphone */}
                                        <div>
                                            <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                                                🎙 {vn ? 'Microphone' : 'Microphone'}
                                            </label>
                                            <select
                                                value={selectedMicId}
                                                onChange={e => setSelectedMicId(e.target.value)}
                                                className="w-full text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300"
                                            >
                                                {mics.length === 0
                                                    ? <option value="">{vn ? '(mặc định hệ thống)' : '(system default)'}</option>
                                                    : mics.map((d, i) => (
                                                        <option key={d.deviceId} value={d.deviceId}>
                                                            {d.label || `Microphone ${i + 1}`}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>

                                        {/* Virtual classmate */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                    {companionRole === 'professor' ? '👨‍🏫' : '🧑‍🎓'} {vn ? (companionRole === 'professor' ? 'Có giáo sư ngay từ đầu' : 'Có Tèo ngay từ đầu') : (companionRole === 'professor' ? 'Start with the professor' : 'Start with the virtual classmate')}
                                                </label>
                                                <button
                                                    onClick={() => setWithClassmate(w => !w)}
                                                    className={`relative w-8 h-4 rounded-full transition-colors ${withClassmate ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-slate-600'}`}
                                                >
                                                    <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${withClassmate ? 'left-4' : 'left-0.5'}`} />
                                                </button>
                                            </div>
                                            <div className="flex gap-1 mb-1.5">
                                                {([['classmate', vn ? '🧑‍🎓 Tèo — hỏi' : '🧑‍🎓 Tèo — asks'], ['professor', vn ? '👨‍🏫 Giáo sư — sửa & giải thích' : '👨‍🏫 Professor — corrects & explains']] as const).map(([r, label]) => (
                                                    <button key={r} onClick={() => setCompanionRole(r)}
                                                        className={`flex-1 px-2 py-1 rounded-md text-[11px] border transition-colors ${companionRole === r ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            {companionRole === 'professor' && (
                                                <p className="text-[10px] text-gray-400 leading-relaxed mb-1">
                                                    {vn
                                                        ? 'Giáo sư ngồi nghe và có mặt trong video. Gọi "thầy ơi…" để hỏi, thầy trả lời bằng giọng nói. Khi phần chấm thời gian thực thấy lỗi nặng, thầy đợi bạn ngừng rồi chen vào sửa (tối đa 1 lần mỗi 2 phút). Nút "Im đã" để thầy chỉ nghe. Cần bật Auto-Pace hoặc AI Checkpoint để thầy biết bạn nói sai.'
                                                        : 'The professor listens and appears in the video. Say "professor…" to ask; he answers aloud. When the real-time check finds a serious mistake he waits for a pause and steps in (at most once every 2 minutes). "Hold" keeps him quiet. Needs Auto-Pace or AI Checkpoint on so he knows about mistakes.'}
                                                </p>
                                            )}
                                            {companionRole === 'classmate' && (
                                                <p className="text-[10px] text-gray-400 leading-relaxed">
                                                    {vn
                                                        ? 'Bạn ảo Tèo hỏi để làm rõ nội dung note; giọng Tèo được ghi vào video. Đang quay vẫn gọi Tèo vào / cho nghỉ được bằng nút 🧑‍🎓 trên thanh quay hoặc phím `. Khi Tèo có mặt, Tèo chỉ lên tiếng khi bạn mời ("Tèo ơi, mày hiểu chưa?", "có gì thắc mắc không?") hoặc khi bạn trả lời xong câu Tèo hỏi — dừng lại suy nghĩ hay vẽ thoải mái. Nên đeo tai nghe; chia sẻ tab/cửa sổ StudyBoard để Tèo hiện trong video.'
                                                        : 'Tèo asks questions that clarify the note; his voice is recorded into the video. During a recording, call him in or send him away with the 🧑‍🎓 button on the recording bar or the ` key. While he is there he only speaks when you invite him ("do you get it?", "any questions?") or when you have finished answering his question — pausing to think or draw is fine. Headphones recommended; share the StudyBoard tab/window so he appears in the video.'}
                                                </p>
                                            )}
                                        </div>

                                        {/* Webcam toggle + selector */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                                    📷 {vn ? 'Webcam PiP' : 'Webcam PiP'}
                                                </label>
                                                <button
                                                    onClick={() => setUseWebcam(w => !w)}
                                                    className={`relative w-8 h-4 rounded-full transition-colors ${useWebcam ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-slate-600'}`}
                                                >
                                                    <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${useWebcam ? 'left-4' : 'left-0.5'}`} />
                                                </button>
                                            </div>
                                            {useWebcam && (
                                                <select
                                                    value={selectedCameraId}
                                                    onChange={e => setSelectedCameraId(e.target.value)}
                                                    className="w-full text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-300"
                                                >
                                                    {cameras.length === 0
                                                        ? <option value="">{vn ? '(camera mặc định)' : '(default camera)'}</option>
                                                        : cameras.map((d, i) => (
                                                            <option key={d.deviceId} value={d.deviceId}>
                                                                {d.label || `Camera ${i + 1}`}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            )}
                                            {useWebcam && (
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[11px] text-gray-400">{vn ? 'Kiểu khung:' : 'Shape:'}</span>
                                                    <button
                                                        onClick={() => setWebcamShape('rect')}
                                                        className={`px-2 py-0.5 text-[11px] font-bold rounded-lg transition-colors ${webcamShape === 'rect' ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-slate-600 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-500'}`}
                                                    >⬛ {vn ? 'Chữ nhật' : 'Rect'}</button>
                                                    <button
                                                        onClick={() => setWebcamShape('circle')}
                                                        className={`px-2 py-0.5 text-[11px] font-bold rounded-lg transition-colors ${webcamShape === 'circle' ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-slate-600 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-500'}`}
                                                    >⚪ {vn ? 'Tròn' : 'Circle'}</button>
                                                </div>
                                            )}
                                            {!useWebcam && (
                                                <p className="text-[11px] text-gray-400">{vn ? 'Bật để hiện webcam ở góc phải video' : 'Enable to show webcam in bottom-right corner'}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Collapsed summary */}
                                {!showDevicePanel && (
                                    <div className="px-4 py-2 flex items-center gap-3 text-[11px] text-gray-400">
                                        <span>🎙 {mics.find(m => m.deviceId === selectedMicId)?.label || (vn ? 'Mặc định' : 'Default')}</span>
                                        <span className="text-gray-200 dark:text-slate-700">·</span>
                                        <span>{useWebcam ? `📷 ${cameras.find(c => c.deviceId === selectedCameraId)?.label || (vn ? 'Camera mặc định' : 'Default cam')}` : (vn ? '📷 Không dùng webcam' : '📷 No webcam')}</span>
                                    </div>
                                )}
                            </div>

                            {/* Checkpoint mode selector */}
                            <div className="px-4 pb-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShieldCheck size={11} className={checkpointMode !== 'off' ? 'text-indigo-500' : 'text-gray-400'} />
                                    <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">
                                        {vn ? 'AI kiểm tra từng đoạn' : 'AI checkpoint mode'}
                                    </span>
                                </div>
                                <div className="flex gap-1.5 flex-wrap">
                                    {(['off', 'auto', 'manual', 'hybrid'] as CheckpointMode[]).map(mode => (
                                        <button
                                            key={mode}
                                            disabled={autoPaceEnabled}
                                            onClick={() => { setCheckpointMode(mode); if (mode !== 'off') setAutoPaceEnabledState(false); }}
                                            className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-colors ${autoPaceEnabled ? 'opacity-40 cursor-not-allowed' : ''} ${
                                                checkpointMode === mode
                                                    ? 'bg-indigo-500 text-white'
                                                    : 'bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600'
                                            }`}
                                        >
                                            {mode === 'off' ? (vn ? 'Tắt' : 'Off')
                                            : mode === 'auto' ? 'Auto'
                                            : mode === 'manual' ? 'Manual'
                                            : 'Hybrid'}
                                        </button>
                                    ))}
                                </div>
                                {checkpointMode !== 'off' && (
                                    <p className="text-[10px] text-gray-400 mt-1.5">
                                        {checkpointMode === 'auto' && (vn ? 'Tự động cắt đoạn theo timer.' : 'Auto-cut at fixed interval.')}
                                        {checkpointMode === 'manual' && (vn ? 'Tự bấm cắt khi nói xong một ý. Có warning nếu đoạn quá dài.' : 'You cut each segment manually. Warning shows after 5 min.')}
                                        {checkpointMode === 'hybrid' && (vn ? 'Có timer tự cắt, nhưng có thể cắt sớm hơn bất cứ lúc nào.' : 'Auto-cut at interval, but you can cut early anytime.')}
                                    </p>
                                )}
                                {(checkpointMode === 'auto' || checkpointMode === 'hybrid') && (
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <span className="text-[10px] text-gray-400">
                                            {vn ? 'Cách' : 'Every'}
                                        </span>
                                        {[0.5, 1, 2, 3, 5].map(m => (
                                            <button
                                                key={m}
                                                onClick={() => setCheckpointMins(m)}
                                                className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${checkpointMins === m ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600'}`}
                                            >
                                                {m === 0.5 ? '30s' : `${m}m`}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Auto-Pace (Beta) toggle — mutually exclusive with AI checkpoint mode */}
                            <div className="px-4 pb-1">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={autoPaceEnabled}
                                        onChange={e => setAutoPaceEnabled(e.target.checked)}
                                        className="w-3.5 h-3.5 accent-indigo-500"
                                    />
                                    <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">
                                        {vn ? 'Tự động ghép nhịp (Beta)' : 'Auto-Pace (Beta)'}
                                    </span>
                                </label>
                                <p className="text-[10px] text-gray-400 mt-1">
                                    {vn
                                        ? `Im lặng + đang gõ/vẽ → tăng tốc ${silentSpeed}x. Im lặng hoàn toàn → tự pause. Không dùng chung với AI kiểm tra từng đoạn.`
                                        : `Silent + typing/drawing → speeds up ${silentSpeed}x. Fully idle → auto-pauses. Can't be combined with AI checkpoint mode.`}
                                </p>
                                {autoPaceEnabled && (
                                    <div className="flex flex-col gap-1.5 mt-2">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[9px] text-gray-400 w-14 flex-shrink-0">{vn ? 'Lúc nói' : 'Speaking'}</span>
                                            {[1, 1.1, 1.2, 1.5].map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => setSpeakingSpeed(s)}
                                                    className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${speakingSpeed === s ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600'}`}
                                                >
                                                    {s}x
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[9px] text-gray-400 w-14 flex-shrink-0">{vn ? 'Lúc im lặng' : 'Silent'}</span>
                                            {[4, 5, 6, 7].map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => setSilentSpeed(s)}
                                                    className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${silentSpeed === s ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600'}`}
                                                >
                                                    {s}x
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[9px] text-gray-400 w-14 flex-shrink-0">{vn ? 'Chờ trước khi pause' : 'Idle → pause'}</span>
                                            {[2, 3, 4, 5, 6].map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => setIdleThresholdSecs(s)}
                                                    className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-colors ${idleThresholdSecs === s ? 'bg-rose-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600'}`}
                                                >
                                                    {s}s
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Drawing hotkey config */}
                            <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
                                <button
                                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                                    onClick={() => { setShowDrawHkPanel(v => !v); setRemapDrawAction(null); }}
                                >
                                    <div className="flex items-center gap-2">
                                        <Keyboard size={12} className="text-indigo-500" />
                                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{vn ? 'Phím tắt vẽ' : 'Drawing hotkeys'}</span>
                                        <span className="text-[10px] text-gray-400 font-mono">Ctrl/⌘+⇧+{(drawHotkeys.toggleOverlay || 'd').toUpperCase()}</span>
                                    </div>
                                    {showDrawHkPanel ? <ChevronUp size={12} className="text-gray-400" /> : <ChevronDown size={12} className="text-gray-400" />}
                                </button>
                                {showDrawHkPanel && (
                                    <div className="px-4 py-3 flex flex-col gap-2">
                                        <p className="text-[10px] text-gray-400">{vn ? 'Tab = chuyển tool tiếp · Click key để đổi phím' : 'Tab = cycle tools · Click a key to remap it'}</p>
                                        {(['toggleOverlay','pen','eraser','line','arrow','circle','select','colorNext','colorPrev','sizeUp','sizeDown','savePage'] as DrawHkAction[]).map((action, i) => (
                                            <React.Fragment key={action}>
                                                {i === 1 && <div className="h-px bg-gray-100 dark:bg-slate-700 my-0.5" />}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[11px] text-gray-500 dark:text-gray-400">{DRAW_HK_LABELS[action]}</span>
                                                    <div className="flex items-center gap-1.5">
                                                        {action === 'toggleOverlay' && <span className="text-[9px] text-gray-400">Ctrl/⌘+⇧+</span>}
                                                        <button
                                                            onClick={() => setRemapDrawAction(action)}
                                                            className={`min-w-[28px] px-1.5 py-0.5 rounded text-[10px] font-mono font-bold text-center transition-colors ${
                                                                remapDrawAction === action
                                                                    ? 'bg-amber-400 text-gray-900 animate-pulse'
                                                                    : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-600'
                                                            }`}
                                                        >
                                                            {remapDrawAction === action ? '…' : (drawHotkeys[action]?.toUpperCase() || '—')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                        {remapDrawAction && (
                                            <p className="text-[10px] text-amber-500 text-center animate-pulse mt-1">
                                                {vn ? 'Bấm phím mới… (Esc để hủy)' : 'Press any key… (Esc to cancel)'}
                                            </p>
                                        )}
                                        <button
                                            onClick={() => { setDrawHotkeys({ ...DRAW_HK_DEFAULTS }); setRemapDrawAction(null); }}
                                            className="text-[10px] text-indigo-400 hover:text-indigo-600 text-right mt-1"
                                        >
                                            {vn ? 'Đặt lại mặc định' : 'Reset to defaults'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Record button — Auto-Pace only supports one continuous clip per session
                                (its chunk timeline is reset on every startRecording call, so a second
                                "Record another clip" would silently discard the first clip's chunks
                                while `clips` still held both — hide it once a clip already exists).
                                Same reasoning for "Insert clip" — its timeline is reset per recording too. */}
                            {!(autoPaceEnabled && clips.length > 0) && !(timelineSegments.length > 0 && clips.length > 0) && (
                            <button onClick={startRecording}
                                className="flex items-center gap-4 px-5 py-4 bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white rounded-2xl transition-all font-bold shadow-lg shadow-red-500/25">
                                <div className="p-2 bg-white/20 rounded-xl flex-shrink-0">
                                    {clips.length === 0
                                        ? <Circle size={20} fill="white" />
                                        : <Plus size={20} />
                                    }
                                </div>
                                <div className="text-left">
                                    <p className="text-base">
                                        {clips.length === 0
                                            ? (vn ? '⏺ Quay màn hình' : '⏺ Record Screen')
                                            : (vn ? '⏺ Thêm clip tiếp theo' : '⏺ Record another clip')
                                        }
                                    </p>
                                    <p className="text-xs text-red-100 font-normal mt-0.5">
                                        {useWebcam
                                            ? (vn ? 'Màn hình + webcam góc phải' : 'Screen + webcam in corner')
                                            : (vn ? 'Có thể pause và resume trong lúc quay' : 'Pause & resume anytime while recording')
                                        }
                                    </p>
                                </div>
                            </button>
                            )}

                            {/* Divider */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-gray-100 dark:bg-slate-700" />
                                <span className="text-xs text-gray-400 font-medium">{vn ? 'hoặc chọn file sẵn' : 'or pick existing file'}</span>
                                <div className="flex-1 h-px bg-gray-100 dark:bg-slate-700" />
                            </div>

                            {/* Drop zone */}
                            <div
                                className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center gap-2 transition-colors cursor-pointer ${isDragging ? 'border-red-400 bg-red-50 dark:bg-red-900/10' : 'border-gray-200 dark:border-slate-600 hover:border-red-300 hover:bg-red-50/30 dark:hover:bg-red-900/5'}`}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Film size={24} className="text-gray-300 dark:text-slate-500" />
                                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                                    {vn ? 'Kéo thả hoặc click chọn file' : 'Drop or click to pick file'}
                                </p>
                                <p className="text-xs text-gray-400">MP4, MOV, WebM...</p>
                                <input ref={fileInputRef} type="file" accept="video/*" className="hidden"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePickedFile(f); }} />
                            </div>

                            {/* Thumbnail compact row */}
                            <div className="px-3 py-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl flex items-center gap-3">
                                <div
                                    onClick={() => setShowThumbEditor(true)}
                                    className="relative w-24 h-[54px] rounded-lg overflow-hidden border border-amber-200 cursor-pointer group flex-shrink-0 bg-gray-100 dark:bg-slate-800"
                                >
                                    {thumbDataUrl
                                        ? <img src={thumbDataUrl} className="w-full h-full object-cover" alt="thumbnail" />
                                        : <div className="w-full h-full flex items-center justify-center"><Loader2 size={14} className="animate-spin text-gray-300" /></div>
                                    }
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                        <Edit3 size={12} className="text-white opacity-0 group-hover:opacity-100 drop-shadow" />
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Thumbnail</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{vn ? 'Nhấn ảnh để chỉnh sửa' : 'Click image to edit'}</p>
                                </div>
                                <button onClick={() => setShowThumbEditor(true)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold border border-amber-300 dark:border-amber-700 rounded-lg text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors">
                                    <Edit3 size={10} /> {vn ? 'Sửa' : 'Edit'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Clip review stage ── */}
                    {stage === 'clip-review' && (
                        <div className="p-4 flex flex-col gap-3">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {vn ? `Xem lại ${clips.length} clips — xóa clip không ưng trước khi ghép` : `Review ${clips.length} clips — delete any you want to re-record`}
                            </p>
                            <div className="flex flex-col gap-2 max-h-[52vh] overflow-y-auto">
                                {clips.map((clip) => {
                                    const url = URL.createObjectURL(clip.blob);
                                    return (
                                        <div key={clip.id} className="flex flex-col gap-1.5 p-3 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-600">
                                            <div className="flex items-center gap-2">
                                                <Film size={13} className="text-gray-400 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 flex-1 truncate">{clip.name}</span>
                                                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{formatTime(clip.durationSecs)}</span>
                                                <button
                                                    onClick={() => setClips(prev => prev.filter(c => c.id !== clip.id))}
                                                    title={vn ? 'Xóa clip này' : 'Delete this clip'}
                                                    className="p-1 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                            <video src={url} controls className="w-full rounded-lg max-h-32 bg-black" />
                                        </div>
                                    );
                                })}
                                {clips.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-6">{vn ? 'Không còn clip nào.' : 'No clips left.'}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Chunk review stage (Auto-Pace) ── */}
                    {stage === 'chunk-review' && (
                        <div className="p-4 flex flex-col gap-3">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {vn ? `Xem lại ${reviewChunks.length} đoạn — bấm để xem, bấm nhãn để đổi tốc độ` : `Review ${reviewChunks.length} chunks — click to preview, click the pill to change speed`}
                            </p>
                            <video
                                ref={chunkPreviewVideoRef}
                                src={chunkPreviewUrlRef.current || undefined}
                                controls
                                className="w-full rounded-xl max-h-64 bg-black"
                            />
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {reviewChunks.map(chunk => (
                                    <button
                                        key={chunk.index}
                                        onClick={() => previewChunk(chunk)}
                                        className={`flex-shrink-0 w-28 flex flex-col gap-1 p-1.5 rounded-xl border-2 transition-colors ${previewingChunkIdx === chunk.index ? 'border-indigo-500' : 'border-gray-100 dark:border-slate-600'} bg-gray-50 dark:bg-slate-700`}
                                    >
                                        {chunk.thumbnail ? (
                                            <img src={chunk.thumbnail} className="w-full h-16 object-cover rounded-lg bg-black" />
                                        ) : (
                                            <div className="w-full h-16 rounded-lg bg-gray-200 dark:bg-slate-600 flex items-center justify-center">
                                                <Film size={16} className="text-gray-400" />
                                            </div>
                                        )}
                                        <div className="flex items-center justify-center gap-1">
                                            <span
                                                role="button"
                                                onClick={(e) => { e.stopPropagation(); toggleChunkSpeed(chunk.index); }}
                                                className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${chunk.kind === 'silent' ? 'bg-amber-500 text-white' : 'bg-indigo-500 text-white'}`}
                                            >
                                                {chunk.speed}x
                                            </span>
                                            {(() => {
                                                const dot = autoPaceCheckDot(chunk);
                                                return dot ? <span title={dot.title} className={`w-2 h-2 rounded-full ${dot.className}`} /> : null;
                                            })()}
                                        </div>
                                        <span className="text-[10px] text-gray-400 text-center font-mono">
                                            {formatTime(Math.round((chunk.endMs - chunk.startMs) / 1000))}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {clipMarks.length > 0 && (
                                <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-900/10 p-3 flex flex-col gap-2">
                                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                                        <Scissors size={12} />
                                        {vn ? `${clipMarks.length} đoạn đã đánh dấu — mỗi đoạn sẽ thành một video riêng, ngoài video đầy đủ` : `${clipMarks.length} marked part${clipMarks.length > 1 ? 's' : ''} — each becomes its own video, besides the full one`}
                                    </p>
                                    {clipMarks.map((m, i) => {
                                        const out = clipOutputSecs(m);
                                        return (
                                            <div key={i} className="flex items-center gap-2">
                                                <button onClick={() => { const v = chunkPreviewVideoRef.current; if (v) { v.currentTime = m.startMs / 1000; v.playbackRate = 1; v.play().catch(() => {}); } }}
                                                    title={vn ? 'Nghe từ đầu đoạn' : 'Play from the start of the part'}
                                                    className="flex-shrink-0 text-[10px] font-mono text-emerald-700 dark:text-emerald-300 hover:underline">
                                                    {formatTime(Math.round(m.startMs / 1000))}–{formatTime(Math.round((m.endMs ?? m.startMs) / 1000))} · {formatTime(out)}
                                                </button>
                                                <input
                                                    value={m.title}
                                                    onChange={e => setClipMarks(clipMarksRef.current.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                                                    placeholder={vn ? `Tiêu đề video con ${i + 1} (để trống: đặt sau)` : `Title of clip ${i + 1} (blank: name it later)`}
                                                    disabled={exportPhase !== 'idle' && exportPhase !== 'error'}
                                                    maxLength={100}
                                                    className="flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-100 outline-none focus:border-emerald-400"
                                                />
                                                <button onClick={() => setClipMarks(clipMarksRef.current.filter((_, j) => j !== i))} disabled={exportPhase !== 'idle' && exportPhase !== 'error'}
                                                    title={vn ? 'Bỏ đoạn này (không tạo video con)' : 'Drop this part (no separate video)'} className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 disabled:opacity-40">
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400">{vn ? 'Thời gian là trên bản quay gốc; con số cuối là độ dài video con sau khi tăng tốc. Mỗi video con sẽ hiện ở góc màn hình khi xử lý xong, để bạn duyệt và upload riêng.' : 'Times are on the raw recording; the last number is the clip length after speed-up. Each clip appears in the corner when rendered, for you to review and upload separately.'}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Timeline review stage (Insert clip) ── */}
                    {stage === 'timeline-review' && (
                        <div className="p-4 flex flex-col gap-3">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {vn ? 'Chuỗi đoạn — quay bình thường xen kẽ video đã chèn' : 'Segment sequence — recorded footage interleaved with inserted clips'}
                            </p>
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {timelineSegments.map((seg, i) => (
                                    <div key={i} className="flex-shrink-0 w-28 flex flex-col gap-1 p-1.5 rounded-xl border-2 border-gray-100 dark:border-slate-600 bg-gray-50 dark:bg-slate-700">
                                        {seg.type === 'inserted' && seg.thumbnail ? (
                                            <img src={seg.thumbnail} className="w-full h-16 object-cover rounded-lg bg-black" />
                                        ) : (
                                            <div className="w-full h-16 rounded-lg bg-gray-200 dark:bg-slate-600 flex items-center justify-center">
                                                <Film size={16} className="text-gray-400" />
                                            </div>
                                        )}
                                        <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 self-center ${seg.type === 'inserted' ? 'bg-violet-500 text-white' : 'bg-indigo-500 text-white'}`}>
                                            {seg.type === 'inserted' ? (vn ? 'Đã chèn' : 'Inserted') : (vn ? 'Quay' : 'Recorded')}
                                        </span>
                                        <span className="text-[10px] text-gray-400 text-center truncate" title={seg.type === 'inserted' ? seg.title : undefined}>
                                            {seg.type === 'inserted' ? seg.title : formatTime(Math.round((seg.sourceEndMs - seg.sourceStartMs) / 1000))}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Review stage ── */}
                    {stage === 'review' && (
                        <div className="p-5 flex flex-col gap-4">
                            {!ytConnected && (
                                <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl">
                                    <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
                                    <p className="text-sm text-amber-700 dark:text-amber-300 flex-1">
                                        {vn ? 'YouTube chưa kết nối' : 'YouTube not connected'}
                                    </p>
                                    <button onClick={() => connectYouTube()} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg">
                                        {vn ? 'Kết nối' : 'Connect'}
                                    </button>
                                </div>
                            )}

                            {file && (
                                <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-600">
                                    <Film size={16} className="text-gray-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{file.name}</p>
                                        <p className="text-xs text-gray-400">
                                            {(file.size / 1024 / 1024).toFixed(1)} MB
                                            {clips.length > 1 && <span className="ml-2 text-blue-400">{vn ? `· ${clips.length} clips đã ghép` : `· ${clips.length} clips merged`}</span>}
                                        </p>
                                    </div>
                                    <button onClick={() => { setFile(null); setStage('pick'); }} className="text-xs text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                                        {vn ? 'Đổi' : 'Change'}
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Left column — media */}
                                <div className="flex flex-col gap-4">
                                    {/* Video preview */}
                                    {previewUrlRef.current && (
                                        <video src={previewUrlRef.current} controls className="w-full rounded-xl border border-gray-200 dark:border-slate-600 max-h-64 bg-black" />
                                    )}

                                    {/* Thumbnail compact row */}
                                    <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-600">
                                        <div
                                            onClick={() => setShowThumbEditor(true)}
                                            className="relative w-28 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 cursor-pointer group flex-shrink-0 bg-gray-100 dark:bg-slate-800"
                                        >
                                            {thumbDataUrl
                                                ? <img src={thumbDataUrl} className="w-full h-full object-cover" alt="thumbnail" />
                                                : <div className="w-full h-full flex items-center justify-center"><Loader2 size={14} className="animate-spin text-gray-300" /></div>
                                            }
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <Edit3 size={14} className="text-white opacity-0 group-hover:opacity-100 drop-shadow" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Thumbnail</p>
                                            <p className="text-[11px] text-gray-400 mt-0.5">{vn ? 'Nhấn ảnh để chỉnh sửa' : 'Click image to edit'}</p>
                                        </div>
                                        <button onClick={() => setShowThumbEditor(true)}
                                            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold border border-gray-300 dark:border-slate-500 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex-shrink-0">
                                            <Edit3 size={10} /> {vn ? 'Sửa' : 'Edit'}
                                        </button>
                                    </div>

                                    {/* Visibility */}
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">{vn ? 'Quyền xem' : 'Visibility'}</label>
                                        <div className="flex gap-2">
                                            {(['unlisted', 'public', 'private'] as const).map(v => (
                                                <button key={v} onClick={() => setVisibility(v)}
                                                    className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-colors ${visibility === v ? 'bg-red-500 border-red-500 text-white' : 'border-gray-200 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:border-red-300'}`}>
                                                    {v === 'unlisted' ? (vn ? '🔗 Link' : '🔗 Unlisted') : v === 'public' ? (vn ? '🌐 Công khai' : '🌐 Public') : (vn ? '🔒 Riêng tư' : '🔒 Private')}
                                                </button>
                                            ))}
                                        </div>
                                        {visibility === 'unlisted' && <p className="text-[11px] text-gray-400 mt-1">{vn ? 'Chỉ người có link mới xem được' : 'Only people with the link can watch'}</p>}
                                    </div>

                                    {/* Audience declaration — required by YouTube before a video can go public */}
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">{vn ? 'Video này có dành cho trẻ em không?' : 'Is this video made for kids?'}</label>
                                        <div className="flex gap-2">
                                            {([[false, vn ? 'Không' : 'No'], [true, vn ? 'Có' : 'Yes']] as const).map(([val, label]) => (
                                                <button key={String(val)} onClick={() => chooseMadeForKids(val)}
                                                    className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-colors ${madeForKids === val ? 'bg-red-500 border-red-500 text-white' : 'border-gray-200 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:border-red-300'}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        {madeForKids === null && <p className="text-[11px] text-amber-500 mt-1">{vn ? 'YouTube yêu cầu khai báo này trước khi upload' : 'YouTube requires this answer before uploading'}</p>}
                                    </div>

                                    {/* Playlist — pick a real YouTube playlist now instead of a separate trip to YouTube Manager */}
                                    {ytConnected && (
                                        <div>
                                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">{vn ? 'Playlist' : 'Playlist'}</label>
                                            <select
                                                value={selectedPlaylistId}
                                                onChange={e => { setSelectedPlaylistId(e.target.value); if (e.target.value) setNewPlaylistTitle(''); }}
                                                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700"
                                            >
                                                <option value="">{vn ? '(Không thêm vào playlist)' : "(Don't add to a playlist)"}</option>
                                                {playlists.map(pl => <option key={pl.id} value={pl.id}>{pl.title} ({pl.itemCount})</option>)}
                                            </select>
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <span className="text-[11px] text-gray-400 flex-shrink-0">{vn ? 'hoặc mới:' : 'or new:'}</span>
                                                <input
                                                    type="text"
                                                    value={newPlaylistTitle}
                                                    onChange={e => { setNewPlaylistTitle(e.target.value); if (e.target.value) setSelectedPlaylistId(''); }}
                                                    placeholder={vn ? 'Tên playlist mới...' : 'New playlist name...'}
                                                    className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-red-300 placeholder:text-gray-400"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Right column — metadata */}
                                <div className="flex flex-col gap-4">
                                    {recordingTranscript.trim() && (
                                        <div className="flex items-center gap-3 px-3.5 py-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/40 rounded-xl">
                                            <Sparkles size={16} className="text-violet-500 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">{vn ? 'Có bản ghi lời nói từ lúc quay' : 'Recording transcript available'}</p>
                                                <p className="text-[11px] text-violet-500 dark:text-violet-400/80">{vn ? 'AI có thể tạo title + mô tả dựa trên những gì bạn thực sự nói' : 'AI can generate title + description from what you actually said'}</p>
                                            </div>
                                            <button onClick={generateFromTranscript} disabled={isGenFromTranscript}
                                                className="flex items-center gap-1.5 px-3 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors flex-shrink-0">
                                                {isGenFromTranscript ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                                {isGenFromTranscript ? (vn ? 'Đang tạo...' : 'Generating...') : (vn ? 'Tạo từ bản ghi' : 'Generate from recording')}
                                            </button>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 block">{vn ? 'Tiêu đề' : 'Title'}</label>
                                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={100}
                                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700" />
                                        <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/100</p>
                                    </div>

                                    <div className="flex-1 flex flex-col">
                                        <div className="flex items-center justify-between mb-1 flex-wrap gap-1.5">
                                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{vn ? 'Mô tả' : 'Description'}</label>
                                            <div className="flex items-center gap-1.5">
                                                <button onClick={generateNoteAndFeedback} disabled={isGenNoteAndFeedback || (!node.content?.trim() && !recordingTranscript.trim())}
                                                    title={vn ? 'Tạo ghi chú kiểu Feynman + nhận xét AI (nếu có AI Checkpoint)' : 'Generate a Feynman-style note + AI feedback (if AI Checkpoints were used)'}
                                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                                    {isGenNoteAndFeedback ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                                                    {isGenNoteAndFeedback ? (vn ? 'Đang tạo...' : 'Generating...') : (vn ? 'Ghi chú + nhận xét AI' : 'Note + AI feedback')}
                                                </button>
                                                <button onClick={generateDescription} disabled={isGenDesc || !node.content?.trim()}
                                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-50 hover:bg-violet-100 dark:bg-violet-900/30 dark:hover:bg-violet-900/50 text-violet-600 dark:text-violet-400 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                                    {isGenDesc ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                                                    {isGenDesc ? (vn ? 'Đang tạo...' : 'Generating...') : (vn ? 'AI tóm tắt' : 'AI summary')}
                                                </button>
                                            </div>
                                        </div>
                                        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={8} maxLength={5000}
                                            className="w-full flex-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700 resize-none" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Uploading stage ── */}
                    {stage === 'uploading' && (
                        <div className="p-8 flex flex-col items-center gap-6">
                            <div className="relative w-24 h-24">
                                <div className="w-24 h-24 rounded-full border-4 border-red-100 dark:border-red-900/30 flex items-center justify-center">
                                    <Youtube size={36} className="text-red-500" />
                                </div>
                                <svg className="absolute inset-0 w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                                    <circle cx="48" cy="48" r="44" fill="none" stroke="#ef4444" strokeWidth="4"
                                        strokeDasharray={`${2 * Math.PI * 44}`}
                                        strokeDashoffset={`${2 * Math.PI * 44 * (1 - progress / 100)}`}
                                        className="transition-all duration-500" />
                                </svg>
                            </div>
                            <div className="text-center">
                                <p className="font-bold text-gray-900 dark:text-white text-2xl">{progress}%</p>
                                <p className="text-sm text-gray-500 mt-1">
                                    {progress < 95 ? (vn ? 'Đang upload...' : 'Uploading...') : (vn ? 'Đang xử lý trên YouTube...' : 'Processing on YouTube...')}
                                </p>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-2">
                                <div className="bg-red-500 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                            </div>
                            <p className="text-xs text-gray-400 text-center">{vn ? 'Có thể thu nhỏ cửa sổ này (nút ⌄ ở góc trên) để làm việc khác — chỉ đừng đóng tab.' : 'You can minimise this window (⌄ at the top) and keep working — just keep this tab open.'}</p>
                        </div>
                    )}

                    {/* ── Done stage ── */}
                    {stage === 'done' && (
                        <div className="p-8 flex flex-col items-center gap-4">
                            <CheckCircle2 size={60} className="text-green-500" />
                            <div className="text-center">
                                <p className="font-bold text-gray-900 dark:text-white text-xl">{vn ? 'Upload thành công! 🎉' : 'Upload complete! 🎉'}</p>
                                <p className="text-sm text-gray-500 mt-2">{vn ? 'Video đã gắn vào note.' : 'Video attached to note.'}</p>
                                {autopilotOn && !file && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">{vn ? '🤖 Server đã upload. Tiêu đề, mô tả, AI check, playlist… sẽ tự xong khi YouTube có phụ đề, xong sẽ có email báo.' : '🤖 Uploaded by the server. Title, description, AI check and playlist follow once YouTube has captions; an email arrives when done.'}</p>}
                                <p className="text-xs text-gray-400 mt-1">{vn ? `Đóng sau ${doneCountdown}s…` : `Closing in ${doneCountdown}s…`}</p>
                            </div>
                            <a href={resultUrl} target="_blank" rel="noopener noreferrer"
                                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold transition-colors">
                                ▶ {vn ? 'Xem trên YouTube' : 'Watch on YouTube'}
                            </a>
                        </div>
                    )}

                    {/* ── Error stage ── */}
                    {stage === 'error' && (
                        <div className="p-8 flex flex-col items-center gap-4">
                            <AlertCircle size={52} className="text-red-400" />
                            <div className="text-center">
                                <p className="font-bold text-gray-900 dark:text-white">{vn ? 'Upload thất bại' : 'Upload failed'}</p>
                                <p className="text-sm text-gray-500 mt-1">{errorMsg}</p>
                            </div>
                            <button onClick={() => setStage('review')} className="px-5 py-2.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-bold">
                                {vn ? 'Thử lại' : 'Try again'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {stage === 'pick' && clips.length > 0 && autoPaceEnabled && speedChunks.length > 0 && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <button onClick={() => setStage('chunk-review')}
                            className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                            <Upload size={15} />
                            {vn ? `Xem lại ${speedChunks.length} đoạn` : `Review ${speedChunks.length} chunks`}
                        </button>
                    </div>
                )}
                {stage === 'pick' && clips.length > 0 && !autoPaceEnabled && timelineSegments.length > 0 && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <button onClick={() => setStage('timeline-review')}
                            className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                            <Upload size={15} />
                            {vn ? `Xem lại ${timelineSegments.length} đoạn` : `Review ${timelineSegments.length} segments`}
                        </button>
                    </div>
                )}
                {stage === 'pick' && clips.length > 0 && !(autoPaceEnabled && speedChunks.length > 0) && !(!autoPaceEnabled && timelineSegments.length > 0) && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0 flex flex-col gap-2">
                        {(seriesParts.length > 0 || resumeParts.length > 0) && clips.length === 1 ? (<>
                            {exportPhase === 'error' && <p className="text-xs text-red-500 text-center">{exportError}</p>}
                            {autopilotOn && (exportPhase === 'pending' || exportPhase === 'processing') && (
                                <p className="text-[11px] text-center text-emerald-600 dark:text-emerald-400">
                                    {vn ? '🤖 Chế độ tự động: có thể đóng tab. Server sẽ tự upload lên YouTube và xử lý video.' : '🤖 Autopilot: you can close the tab. The server uploads and processes the video.'}
                                </p>
                            )}
                            {/* The whole recording (4h stands for "to its end") after the earlier parts. */}
                            <button onClick={() => startExport([{ startMs: 0, endMs: 4 * 60 * 60 * 1000, speed: 1, kind: 'speaking' }])}
                                disabled={exportPhase !== 'idle' && exportPhase !== 'error'}
                                className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                                {exportPhase === 'idle' || exportPhase === 'error' ? (
                                    <><ListVideo size={15} />{vn ? `Ghép ${seriesParts.length + resumeParts.length + 1} phần & xuất video` : `Join ${seriesParts.length + resumeParts.length + 1} parts & export`}</>
                                ) : exportPhase === 'uploading' ? (
                                    <><Loader2 size={15} className="animate-spin" />{vn ? `Đang tải lên ${exportProgressPct}%` : `Uploading ${exportProgressPct}%`}</>
                                ) : exportPhase === 'pending' ? (
                                    <><Loader2 size={15} className="animate-spin" />{vn ? 'Đang khởi động...' : 'Starting...'}</>
                                ) : (
                                    <><Loader2 size={15} className="animate-spin" />{processingLabel()}</>
                                )}
                            </button>
                        </>) : (
                        <button onClick={goToReview}
                            className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                            <Upload size={15} />
                            {vn ? `Tiếp tục với ${clips.length} clip${clips.length > 1 ? ' (sẽ ghép)' : ''}` : `Continue with ${clips.length} clip${clips.length > 1 ? 's (will merge)' : ''}`}
                        </button>
                        )}
                    </div>
                )}

                {stage === 'clip-review' && (
                    <div className="flex gap-2 p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <button onClick={() => setStage('pick')}
                            className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">←</button>
                        <button onClick={mergeAndContinue} disabled={clips.length === 0}
                            className="flex-[3] py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                            <Upload size={15} />
                            {vn ? `Ghép ${clips.length} clips & tiếp tục` : `Merge ${clips.length} clips & continue`}
                        </button>
                    </div>
                )}

                {stage === 'chunk-review' && (
                    <div className="flex flex-col gap-2 p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        {exportPhase === 'error' && (
                            <p className="text-xs text-red-500 text-center">{exportError}</p>
                        )}
                        {seriesParts.length > 0 && (
                            <p className="text-[11px] text-center text-teal-700 dark:text-teal-300">
                                {vn ? `Video xuất ra sẽ nối sau ${seriesParts.length} video trước: ${seriesParts.map(p => p.title).join(' → ')}` : `The export goes after ${seriesParts.length} earlier video(s): ${seriesParts.map(p => p.title).join(' → ')}`}
                            </p>
                        )}
                        {resumeParts.length > 0 && (
                            <p className="text-[11px] text-center text-indigo-600 dark:text-indigo-300">
                                {vn ? `Phần quay trước (${resumeParts.map(p => formatTime(Math.round(p.keptMs / 1000))).join(' + ')}) sẽ được nối vào đầu video.` : `The earlier recording (${resumeParts.map(p => formatTime(Math.round(p.keptMs / 1000))).join(' + ')}) goes at the start of the video.`}
                            </p>
                        )}
                        {autopilotOn && (
                            <p className="text-[11px] text-center text-emerald-600 dark:text-emerald-400">
                                {exportPhase === 'uploading'
                                    ? (vn ? '🤖 Chế độ tự động: đợi tải lên xong rồi có thể đóng tab — server lo phần còn lại.' : '🤖 Autopilot: wait for this upload to finish, then you can close the tab.')
                                    : exportPhase === 'pending' || exportPhase === 'processing'
                                        ? (vn ? '🤖 Chế độ tự động: có thể đóng tab. Server sẽ tự upload lên YouTube và xử lý video.' : '🤖 Autopilot: you can close the tab. The server uploads and processes the video.')
                                        : (vn ? '🤖 Chế độ tự động đang bật: xuất xong server tự upload lên YouTube và xử lý.' : '🤖 Autopilot is on: the server uploads the export and processes it.')}
                            </p>
                        )}
                        <div className="flex gap-2">
                            <button onClick={() => setStage('pick')} disabled={exportPhase !== 'idle' && exportPhase !== 'error'}
                                className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors">←</button>
                            <button onClick={() => startExport()} disabled={clips.length === 0 || (exportPhase !== 'idle' && exportPhase !== 'error')}
                                className="flex-[3] py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                                {exportPhase === 'idle' || exportPhase === 'error' ? (
                                    <><Upload size={15} />{vn ? 'Xuất video' : 'Export video'}</>
                                ) : exportPhase === 'uploading' ? (
                                    <><Loader2 size={15} className="animate-spin" />{vn ? `Đang tải lên ${exportProgressPct}%` : `Uploading ${exportProgressPct}%`}</>
                                ) : exportPhase === 'pending' ? (
                                    <><Loader2 size={15} className="animate-spin" />{vn ? 'Đang khởi động...' : 'Starting...'}</>
                                ) : (
                                    <><Loader2 size={15} className="animate-spin" />{processingLabel()}</>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'timeline-review' && (
                    <div className="flex flex-col gap-2 p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        {exportPhase === 'error' && (
                            <p className="text-xs text-red-500 text-center">{exportError}</p>
                        )}
                        {seriesParts.length > 0 && (
                            <p className="text-[11px] text-center text-teal-700 dark:text-teal-300">
                                {vn ? `Video xuất ra sẽ nối sau ${seriesParts.length} video trước: ${seriesParts.map(p => p.title).join(' → ')}` : `The export goes after ${seriesParts.length} earlier video(s): ${seriesParts.map(p => p.title).join(' → ')}`}
                            </p>
                        )}
                        {resumeParts.length > 0 && (
                            <p className="text-[11px] text-center text-indigo-600 dark:text-indigo-300">
                                {vn ? `Phần quay trước (${resumeParts.map(p => formatTime(Math.round(p.keptMs / 1000))).join(' + ')}) sẽ được nối vào đầu video.` : `The earlier recording (${resumeParts.map(p => formatTime(Math.round(p.keptMs / 1000))).join(' + ')}) goes at the start of the video.`}
                            </p>
                        )}
                        {autopilotOn && (
                            <p className="text-[11px] text-center text-emerald-600 dark:text-emerald-400">
                                {exportPhase === 'uploading'
                                    ? (vn ? '🤖 Chế độ tự động: đợi tải lên xong rồi có thể đóng tab — server lo phần còn lại.' : '🤖 Autopilot: wait for this upload to finish, then you can close the tab.')
                                    : exportPhase === 'pending' || exportPhase === 'processing'
                                        ? (vn ? '🤖 Chế độ tự động: có thể đóng tab. Server sẽ tự upload lên YouTube và xử lý video.' : '🤖 Autopilot: you can close the tab. The server uploads and processes the video.')
                                        : (vn ? '🤖 Chế độ tự động đang bật: xuất xong server tự upload lên YouTube và xử lý.' : '🤖 Autopilot is on: the server uploads the export and processes it.')}
                            </p>
                        )}
                        <div className="flex gap-2">
                            <button onClick={() => setStage('pick')} disabled={exportPhase !== 'idle' && exportPhase !== 'error'}
                                className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors">←</button>
                            <button onClick={() => startExport(buildTimelineChunks(timelineSegments))} disabled={clips.length === 0 || (exportPhase !== 'idle' && exportPhase !== 'error')}
                                className="flex-[3] py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                                {exportPhase === 'idle' || exportPhase === 'error' ? (
                                    <><Upload size={15} />{vn ? 'Xuất video' : 'Export video'}</>
                                ) : exportPhase === 'uploading' ? (
                                    <><Loader2 size={15} className="animate-spin" />{vn ? `Đang tải lên ${exportProgressPct}%` : `Uploading ${exportProgressPct}%`}</>
                                ) : exportPhase === 'pending' ? (
                                    <><Loader2 size={15} className="animate-spin" />{vn ? 'Đang khởi động...' : 'Starting...'}</>
                                ) : (
                                    <><Loader2 size={15} className="animate-spin" />{processingLabel()}</>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'review' && (
                    <div className="flex gap-2 p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <button onClick={() => { setFile(null); setStage(clips.length > 1 ? 'clip-review' : (autoPaceEnabled && speedChunks.length > 0 ? 'chunk-review' : (timelineSegments.length > 0 ? 'timeline-review' : 'pick'))); }}
                            className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">←</button>
                        <button onClick={handleUpload} disabled={!ytConnected || !file || madeForKids === null}
                            title={madeForKids === null ? (vn ? 'Hãy khai báo video có dành cho trẻ em không' : 'Declare whether the video is made for kids first') : undefined}
                            className="flex-[3] py-3 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                            <Upload size={15} />
                            {vn ? 'Upload & Sync GitHub' : 'Upload & Sync GitHub'}
                        </button>
                    </div>
                )}

                {(stage === 'done' || stage === 'error') && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <button onClick={requestClose} className="w-full py-3 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 text-sm font-bold transition-colors">
                            {vn ? 'Đóng' : 'Close'}
                        </button>
                    </div>
                )}
            </div>

            {showCloseConfirm && (
                <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="w-[420px] max-w-[92vw] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-red-200 dark:border-red-900/50 px-5 py-4">
                        <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                            {recordState !== 'idle'
                                ? (vn ? 'Đang ghi hình — đóng luôn?' : 'Still recording — close anyway?')
                                : (vn ? 'Video chưa xử lý xong — đóng luôn?' : 'Recording not finished processing — close anyway?')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                            {recordState !== 'idle'
                                ? (vn
                                    ? 'Việc ghi hình sẽ dừng ngay. Bản ghi thô có thể khôi phục lại sau, nhưng bạn sẽ phải làm lại bước xử lý tốc độ và upload.'
                                    : 'Recording will stop immediately. The raw footage can be recovered later, but you\'ll have to redo the speed-processing and upload steps.')
                                : (vn
                                    ? 'Bản ghi vẫn đang ở bước xem lại/xử lý, chưa được upload. Đóng bây giờ sẽ bỏ dở bước xử lý tốc độ (Auto-Pace) — video thô vẫn được lưu tạm và có thể khôi phục, nhưng sẽ không được ghép nhịp.'
                                    : 'This recording is still in review/processing and hasn\'t been uploaded. Closing now skips the Auto-Pace speed-processing step — the raw footage stays recoverable, but won\'t be paced.')}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setShowCloseConfirm(false); onClose(); }}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-colors"
                            >
                                {vn ? 'Đóng, bỏ dở' : 'Close anyway'}
                            </button>
                            <button
                                onClick={() => setShowCloseConfirm(false)}
                                className="flex-1 py-2.5 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 text-xs font-bold transition-colors"
                            >
                                {vn ? 'Ở lại' : 'Stay'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {seriesTrimPart && seriesTrimVideo && (
                <TrimVideoPanel
                    key={seriesTrimPart.key}
                    mode="range"
                    video={seriesTrimVideo}
                    noteTitle={seriesTrimPart.title}
                    vn={vn}
                    language={language}
                    phase="idle"
                    progress={null}
                    error=""
                    initialRange={{ startMs: seriesTrimPart.startMs, endMs: seriesTrimPart.endMs }}
                    onStart={(startMs, endMs) => {
                        setSeriesParts(prev => prev.map(p => p.key === seriesTrimPart.key ? { ...p, startMs, endMs } : p));
                        setSeriesTrimKey(null);
                    }}
                    onClose={() => setSeriesTrimKey(null)}
                />
            )}
        </>
    );
}

/* Lightbox rendered outside modal stacking context */
export function YouTubeThumbnailLightbox({ src, onClose }: { src: string; onClose: () => void }) {
    return (
        <div
            className="fixed inset-0 z-[500] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
            onClick={onClose}
        >
            <img
                src={src}
                className="max-w-full max-h-full rounded-xl shadow-2xl"
                alt="thumbnail preview"
                onClick={e => e.stopPropagation()}
            />
        </div>
    );
}
